/**
 * A function to create a log object for the 'loadingPhaseInInitStart' event (evid: 1)
 *
 * Event description: Module: builder. Flow: a phase in the init has started
 *
 * @param {loadingPhaseInInitStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} subPhaseName - sub phase name
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} loadType - new / existing
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - load time from the beginning of load
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} phaseName - phase name
 * @return {LogObject<loadingPhaseInInitStartParams>} LogObject<loadingPhaseInInitStartParams>
 */
export function loadingPhaseInInitStart(params) {
    return { evid: 1, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introPageView' event (evid: 100)
 *
 * Event description: Module: intro. Flow: intro page view - all pages in the intro.
 *
 * @param {introPageViewParams} params - The parameters for this event
 * @property {number} noOfMatches - for search results page: number of results that were found (overall)
 * @property {string} structureId - structure id that was chosen on the category page
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} contentLanguage - Content Language - which supported on ADI
 * @property {string} industryId - industry id that was chosen on the category page
 * @property {string} screenResolution - screen_resolution
 * @property {string} referral - for business type page: split page / new site in adi site menu/siteless
 * @property {string} origin - the previous page (e.g. 'intro.business-category')
 * @property {string} flow - applies for category search page: main funnel/adi intro
 * @property {string} viewerName - viewer_name
 * @property {GUID} searchGuid - for search results page: every search a new guid is generated by wixwiz server
 * @property {string} tpas - the TPAs which this page is relevant to
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {boolean} isFake - true if sent fake (when intro starts from tpa page)
 * @property {string} view - name of page: business type / services selection / business name / location / search confirm / search results / edit info card
 * @return {LogObject<introPageViewParams>} LogObject<introPageViewParams>
 */
export function introPageView(params) {
    return { evid: 100, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introBusinessTypeWasSelected' event (evid: 101)
 *
 * Event description: Module: intro. Flow: user selected from the drop down.
 *
 * @param {introBusinessTypeWasSelectedParams} params - The parameters for this event
 * @property {string} name - name of the business type
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} text - searched text
 * @property {string} suggestList - from where user selected: example list / no matches list / category list
 * @property {string} screenResolution - screen_resolution
 * @property {string} flow - main funnel/adi intro
 * @property {string} viewerName - viewer_name
 * @property {number} position - position of selected result
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} level - ** Deprecated **
 * @return {LogObject<introBusinessTypeWasSelectedParams>} LogObject<introBusinessTypeWasSelectedParams>
 */
export function introBusinessTypeWasSelected(params) {
    return { evid: 101, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introBusinessTypeTypingClientEvent' event (evid: 102)
 *
 * Event description: Module: intro. Flow: user typed a business type
 *
 * @param {introBusinessTypeTypingClientEventParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {number} returnedResults - how many resuslts the user sees, 0 for zero matches
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} contentLanguage - The content Language used for the intro
 * @property {string} text - searched text
 * @property {string} screenResolution - screen_resolution
 * @property {string} flow - main funnel/adi intro/marketing templates
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<introBusinessTypeTypingClientEventParams>} LogObject<introBusinessTypeTypingClientEventParams>
 */
export function introBusinessTypeTypingClientEvent(params) {
    return { evid: 102, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introSelectBusinessTypePageNavigationClick' event (evid: 103)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button in the select category page (1st page).
 *
 * @param {introSelectBusinessTypePageNavigationClickParams} params - The parameters for this event
 * @property {string} name - name of the business type (in the user's lng)
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} contentLanguage - Content Language - which supported on ADI
 * @property {string} selectedStructureId - structure id
 * @property {string} text - searched text
 * @property {string} suggestList - from where user selected: example list / no matches list / category list
 * @property {string} screenResolution - screen_resolution
 * @property {string} flow - main funnel/adi intro/marketing templates
 * @property {string} viewerName - viewer_name
 * @property {string} selectedIndustryId - industry id
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @property {boolean} isFake - true if sent fake (when intro starts from tpa page)
 * @return {LogObject<introSelectBusinessTypePageNavigationClickParams>} LogObject<introSelectBusinessTypePageNavigationClickParams>
 */
export function introSelectBusinessTypePageNavigationClick(params) {
    return { evid: 103, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introServicesSelected' event (evid: 104)
 *
 * Event description: Module: intro. Flow: user changes a checkbox's status in the services page (for every change).
 *
 * @param {introServicesSelectedParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} bookings - true (default) / true / false . If the user has stores by default, the true (default) value will be reported
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} userSubscription - true (default) / true / false . If the user has stores by default, the true (default) value will be reported
 * @property {string} screenResolution - screen_resolution
 * @property {string} stores - true (default) / true / false . If the user has stores by default, the true (default) value will be reported
 * @property {string} blog - true (default) / true / false . If the user has stores by default, the true (default) value will be reported
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<introServicesSelectedParams>} LogObject<introServicesSelectedParams>
 */
export function introServicesSelected(params) {
    return { evid: 104, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introSelectServicesPageNavigationClick' event (evid: 105)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button in the select services page (2nd page).
 *
 * @param {introSelectServicesPageNavigationClickParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} bookings - true (default) / true / false . If the user has stores by default, the true (default) value will be reported
 * @property {string} bandsInTown - if bands in town is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} singleEvent - if single event is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} music - if music is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} groups - if groups is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} userSubscription - true (default) / true / false . If the user has stores by default, the true (default) value will be reported
 * @property {string} screenResolution - screen_resolution
 * @property {string} chat - if chat is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} multiEvent - if multi events is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} stores - true (default) / true / false . If the user has stores by default, the true (default) value will be reported
 * @property {string} blog - true (default) / true / false . If the user has stores by default, the true (default) value will be reported
 * @property {string} video - if video is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} flow - main funnel when used in the  mani funnel
 * @property {string} forum - if forum is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} viewerName - viewer_name
 * @property {string} paidPlans - if paid plans is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @property {string} restaurantOrders - if restaurant Orders is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} instagram - if instagram is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} restaurantMenus - if restaurant Menus is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} membersArea - if members area is selected: true/false (default/prechecked/recommended/popular)
 * @property {string} restaurantReservations - if restaurant Reservations is selected: true/false (default/prechecked/recommended/popular)
 * @return {LogObject<introSelectServicesPageNavigationClickParams>} LogObject<introSelectServicesPageNavigationClickParams>
 */
export function introSelectServicesPageNavigationClick(params) {
    return { evid: 105, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introTypeBusinessNamePageNavigationClick' event (evid: 106)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button in the name of business page (3rd page).
 *
 * @param {introTypeBusinessNamePageNavigationClickParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} text - input text
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @return {LogObject<introTypeBusinessNamePageNavigationClickParams>} LogObject<introTypeBusinessNamePageNavigationClickParams>
 */
export function introTypeBusinessNamePageNavigationClick(params) {
    return { evid: 106, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introTypeBusinessLocationPageNavigationClick' event (evid: 107)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button in the location page (4th page).
 *
 * @param {introTypeBusinessLocationPageNavigationClickParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} text - input text
 * @property {string} screenResolution - screen_resolution
 * @property {string} lod - level of details of the location (exact / street / neighbourhood / city / state / country/online-event )
 * @property {string} viewerName - viewer_name
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @return {LogObject<introTypeBusinessLocationPageNavigationClickParams>} LogObject<introTypeBusinessLocationPageNavigationClickParams>
 */
export function introTypeBusinessLocationPageNavigationClick(params) {
    return { evid: 107, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introApproveSearchInformationPageNavigationClick' event (evid: 108)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button in the search confirmation page (5th page).
 *
 * @param {introApproveSearchInformationPageNavigationClickParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} text - searched text
 * @property {string} screenResolution - screen_resolution
 * @property {string} lod - level of details of the location (exact / street / neighbourhood / city / state / country )
 * @property {string} viewerName - viewer_name
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @return {LogObject<introApproveSearchInformationPageNavigationClickParams>} LogObject<introApproveSearchInformationPageNavigationClickParams>
 */
export function introApproveSearchInformationPageNavigationClick(params) {
    return { evid: 108, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introWixwizSearchResultsGoToTopOfPageClick' event (evid: 109)
 *
 * Event description: Module: intro. Flow: user clicks on 'go to top' in wixwiz page results.
 *
 * @param {introWixwizSearchResultsGoToTopOfPageClickParams} params - The parameters for this event
 * @property {number} noOfMatches - for search results page: number of results that were found (overall)
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {number} noOfSeenMatches - number of results that user saw (how many results fully displayed on screen)
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} searchGuid - for search results page: every search a new guid is generated by wixwiz server
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<introWixwizSearchResultsGoToTopOfPageClickParams>} LogObject<introWixwizSearchResultsGoToTopOfPageClickParams>
 */
export function introWixwizSearchResultsGoToTopOfPageClick(params) {
    return { evid: 109, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introFullStoryStarted' event (evid: 11)
 *
 * Event description: Module: intro. Flow: intro - full story started recording session.
 *
 * @param {introFullStoryStartedParams} params - The parameters for this event
 * @property {string} structureId - structure id
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} fullstoryurl - link to fullstory recording
 * @property {string} adiBuilderType - adi_builder_type
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<introFullStoryStartedParams>} LogObject<introFullStoryStartedParams>
 */
export function introFullStoryStarted(params) {
    return { evid: 11, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introWixwizSearchResultsPageNavigationClick' event (evid: 110)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button in the search results page (6th page), including the 'it's me' button. Navigation options: next (=it's me), back, skip.
 *
 * @param {introWixwizSearchResultsPageNavigationClickParams} params - The parameters for this event
 * @property {number} noOfMatches - for search results page: number of results that were found (overall)
 * @property {number} locationScore - location score from wixwiz on selected result
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {number} noOfSeenMatches - number of results that user saw (how many results fully displayed on screen)
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} searchGuid - for search results page: every search a new guid is generated by wixwiz server
 * @property {number} position - position of selected result
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @property {number} similarityScore - similarity score from wixwiz on selected result
 * @return {LogObject<introWixwizSearchResultsPageNavigationClickParams>} LogObject<introWixwizSearchResultsPageNavigationClickParams>
 */
export function introWixwizSearchResultsPageNavigationClick(params) {
    return { evid: 110, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introInfoCardAddEditRemoveInfo' event (evid: 111)
 *
 * Event description: Module: intro. Flow: user changes a value in the info card. It can be done in the intro funnel & later from welcome tour / top bar
 *
 * @param {introInfoCardAddEditRemoveInfoParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen_resolution
 * @property {number} fieldLength - what is the length of the content that user entered
 * @property {string} fieldName - which field is updated: business logo / company name / address / email / phone / facebook / twitter / linkedin / pinterest
 * @property {string} origin - where the user edits: info-card / welcomeTour-before-extraction / welcomeTour-after extraction / clipart-settings-panel / header-panel
 * @property {string} viewerName - viewer_name
 * @property {GUID} searchGuid - for search results page: every search a new guid is generated by wixwiz server
 * @property {number} loadTime - loading time from approve to success
 * @property {string} fieldType - text/ social / logo
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} action - add / edit / remove
 * @return {LogObject<introInfoCardAddEditRemoveInfoParams>} LogObject<introInfoCardAddEditRemoveInfoParams>
 */
export function introInfoCardAddEditRemoveInfo(params) {
    return { evid: 111, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introEditInfoCardPageNavigationClick' event (evid: 112)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button in the edit info card page (7th page).
 *
 * @param {introEditInfoCardPageNavigationClickParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @return {LogObject<introEditInfoCardPageNavigationClickParams>} LogObject<introEditInfoCardPageNavigationClickParams>
 */
export function introEditInfoCardPageNavigationClick(params) {
    return { evid: 112, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introBusinessTypeNotSupported' event (evid: 113)
 *
 * Event description: Module: intro. Flow: intro - Select Business type page - not supported message.
 *
 * @param {introBusinessTypeNotSupportedParams} params - The parameters for this event
 * @property {string} name - name of the business type
 * @property {string} mainCategory - the category from the "vertical selector" category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} text - searched text when selecting a business type
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<introBusinessTypeNotSupportedParams>} LogObject<introBusinessTypeNotSupportedParams>
 */
export function introBusinessTypeNotSupported(params) {
    return { evid: 113, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introClickOnSearchButtonInSelectUserPage' event (evid: 115)
 *
 * Event description: Module: intro. Flow: user clicks on a search button in select-user page (Wiz event)
 *
 * @param {introClickOnSearchButtonInSelectUserPageParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} text - searched text
 * @property {string} screenResolution - screen_resolution
 * @property {string} lod - level of details of the location (exact / street / neighbourhood / city / state / country )
 * @property {string} viewerName - viewer_name
 * @property {string} address - address typed by user
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @return {LogObject<introClickOnSearchButtonInSelectUserPageParams>} LogObject<introClickOnSearchButtonInSelectUserPageParams>
 */
export function introClickOnSearchButtonInSelectUserPage(params) {
    return { evid: 115, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'clickOnReplaceLogoIntroWelcomeTourBuilder' event (evid: 117)
 *
 * Event description: Module: tour. Flow: user clicked on replace logo button in the intro / welcomeTour / builder
 *
 * @param {clickOnReplaceLogoIntroWelcomeTourBuilderParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - name of tour: welcome / preview / design-entire website / design-fonts&colors / edit section / add section / add page
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - origin of action: intro / welcomeTour-before-extraction / welcomeTour-after-extraction / header-panel / clipart-settings-panel
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / no-tour
 * @property {GUID} msid - metasite id
 * @return {LogObject<clickOnReplaceLogoIntroWelcomeTourBuilderParams>} LogObject<clickOnReplaceLogoIntroWelcomeTourBuilderParams>
 */
export function clickOnReplaceLogoIntroWelcomeTourBuilder(params) {
    return { evid: 117, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introGenericCategoryDialogIsShown' event (evid: 118)
 *
 * Event description: Module: intro. Flow: user types a 'missing' category and the generic business category dialog is shown
 *
 * @param {introGenericCategoryDialogIsShownParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} text - searched text
 * @property {string} screenResolution - screen_resolution
 * @property {string} flow - main funnel/adi intro
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<introGenericCategoryDialogIsShownParams>} LogObject<introGenericCategoryDialogIsShownParams>
 */
export function introGenericCategoryDialogIsShown(params) {
    return { evid: 118, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introEventsTpaSchedulePageNavigationClick' event (evid: 119)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button on the Event schedule page (relevant for Events TPA categories only).
 *
 * @param {introEventsTpaSchedulePageNavigationClickParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} startValueExists - true if user chose start date / time
 * @property {string} adiBuilderType - mobile / desktop
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {string} endValueExists - true if user chose end date / time
 * @property {string} nextView - name of next page
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @return {LogObject<introEventsTpaSchedulePageNavigationClickParams>} LogObject<introEventsTpaSchedulePageNavigationClickParams>
 */
export function introEventsTpaSchedulePageNavigationClick(params) {
    return { evid: 119, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderCoreLoadingWizardServiceCoreInitializeStart' event (evid: 12)
 *
 * Event description: Module: builder. Flow: phase in loading the core: load layouts, load preview, theme chooser, layout solver
 *
 * @param {builderCoreLoadingWizardServiceCoreInitializeStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} loadType - new site / existing site
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - loading time diff from previous phase
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderCoreLoadingWizardServiceCoreInitializeStartParams>} LogObject<builderCoreLoadingWizardServiceCoreInitializeStartParams>
 */
export function builderCoreLoadingWizardServiceCoreInitializeStart(params) {
    return { evid: 12, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introEventsTpaSchedulePageActionOnPage' event (evid: 120)
 *
 * Event description: Module: intro. Flow: user makes changes on the Events (TPA) schedule page
 *
 * @param {introEventsTpaSchedulePageActionOnPageParams} params - The parameters for this event
 * @property {string} name - field/button name: start date, start time, end date, end time,time zone, add end time...
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - mobile / desktop
 * @property {string} screenResolution - screen resolution
 * @property {string} actionName - click, choose, remove
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<introEventsTpaSchedulePageActionOnPageParams>} LogObject<introEventsTpaSchedulePageActionOnPageParams>
 */
export function introEventsTpaSchedulePageActionOnPage(params) {
    return { evid: 120, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introSiteConverterPagesNavigationClick' event (evid: 121)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button on the pages of Site Converter
 *
 * @param {introSiteConverterPagesNavigationClickParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - NA
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @property {string} view - page name
 * @return {LogObject<introSiteConverterPagesNavigationClickParams>} LogObject<introSiteConverterPagesNavigationClickParams>
 */
export function introSiteConverterPagesNavigationClick(params) {
    return { evid: 121, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introSiteConverterErrorIsShown' event (evid: 122)
 *
 * Event description: Module: intro. Flow: error is shown to user during Site Converter process
 *
 * @param {introSiteConverterErrorIsShownParams} params - The parameters for this event
 * @property {string} name - error name
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - NA
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} view - page name
 * @return {LogObject<introSiteConverterErrorIsShownParams>} LogObject<introSiteConverterErrorIsShownParams>
 */
export function introSiteConverterErrorIsShown(params) {
    return { evid: 122, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introSiteConverterPhaseInImportStart' event (evid: 123)
 *
 * Event description: Module: builder. Flow: a phase in Site Converter process - start
 *
 * @param {introSiteConverterPhaseInImportStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {number} totalPages - total number of pages to import
 * @property {string} structureId - structure id
 * @property {string} url - site url for conversion
 * @property {string} adiBuilderType - NA
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {string} details - e.g. num_of_images_to_import
 * @property {number} loadTime - load time from the beginning of the import
 * @property {boolean} isHomepage - true if importing the homepage
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} phaseName - phase name
 * @return {LogObject<introSiteConverterPhaseInImportStartParams>} LogObject<introSiteConverterPhaseInImportStartParams>
 */
export function introSiteConverterPhaseInImportStart(params) {
    return { evid: 123, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introStieConverterPhaseInImportComplete' event (evid: 124)
 *
 * Event description: Module: builder. Flow: a phase in the init has been completed
 *
 * @param {introStieConverterPhaseInImportCompleteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {number} totalPages - total number of pages to import
 * @property {string} structureId - structure id
 * @property {string} url - site url for conversion
 * @property {string} adiBuilderType - NA
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {string} details - wix site, non-wix site, content-imported, content-not-imported
 * @property {number} intervalLoadTime - load time of this phase in ms
 * @property {number} loadTime - total load time till this phase in ms
 * @property {boolean} isHomepage - true if importing the homepage
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} phaseName - phase name
 * @return {LogObject<introStieConverterPhaseInImportCompleteParams>} LogObject<introStieConverterPhaseInImportCompleteParams>
 */
export function introStieConverterPhaseInImportComplete(params) {
    return { evid: 124, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'loadingFirstTimeStart' event (evid: 125)
 *
 * Event description: Module: builder. Flow: builder is starting to load for the first time (after the intro). Prepare: MediaFrame warmp, callbacks
 *
 * @param {loadingFirstTimeStartParams} params - The parameters for this event
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - from where the builder is opened: intro / home
 * @property {string} init_origin - initiator of the init
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - loading time diff from previous phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<loadingFirstTimeStartParams>} LogObject<loadingFirstTimeStartParams>
 */
export function loadingFirstTimeStart(params) {
    return { evid: 125, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'loadingFirstTimeComplete' event (evid: 134)
 *
 * Event description: Module: builder. Flow: builder is loaded for the first time (after the intro).
 *
 * @param {loadingFirstTimeCompleteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {number} timeInPage - time that user spent in ADI tab (in ms)
 * @property {string} structureId - structure id
 * @property {number} focusOnTab - 1 - if the tab was focused during the loading time, 0 if switched tabs during loading
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} validStory - true / false
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - loading time diff from previous phase
 * @property {number} loadTime - loading time till this phase
 * @property {string} rtlEnabled - true/false
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} loadingDetails - different loading details
 * @return {LogObject<loadingFirstTimeCompleteParams>} LogObject<loadingFirstTimeCompleteParams>
 */
export function loadingFirstTimeComplete(params) {
    return { evid: 134, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderLoadingExistingSiteStart' event (evid: 135)
 *
 * Event description: Module: builder. Flow: builder is starting to load an existing site (NOT from the intro).
 *
 * @param {builderLoadingExistingSiteStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {GUID} originalEsi - ESI generated by the server or passed via query param
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} loadType - existing site / template
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - from where the builder is opened: intro / home
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editing session, generated on client
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderLoadingExistingSiteStartParams>} LogObject<builderLoadingExistingSiteStartParams>
 */
export function builderLoadingExistingSiteStart(params) {
    return { evid: 135, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'loadingExistingSiteLoadStoryGetMetadata' event (evid: 136)
 *
 * Event description: Module: builder. Flow: phase in opening the builder (existing site) started.
 *
 * @param {loadingExistingSiteLoadStoryGetMetadataParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - loading time diff from previous phase
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<loadingExistingSiteLoadStoryGetMetadataParams>} LogObject<loadingExistingSiteLoadStoryGetMetadataParams>
 */
export function loadingExistingSiteLoadStoryGetMetadata(params) {
    return { evid: 136, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'loadingExistingSiteComplete' event (evid: 139)
 *
 * Event description: Module: builder. Flow: builder is loaded with existing site (NOT from the intro).
 *
 * @param {loadingExistingSiteCompleteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {number} timeInPage - time that user spent in ADI tab (in ms)
 * @property {number} storyGzipSize - story blob Gzip size (represents bytes), not arriving for all users
 * @property {string} structureId - structure id
 * @property {GUID} originalEsi - ESI generated by the server or passed via query param
 * @property {number} focusOnTab - 1 - if the tab was focused during the loading time, 0 if switched tabs during loading
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} loadType - existing site / template
 * @property {string} validStory - true / false
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} storySize - story blob size (represents bytes), not arriving for all users
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - loading time diff from previous phase
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} loadingDetails - loading details json (# pages, # tpa pages, etc)
 * @return {LogObject<loadingExistingSiteCompleteParams>} LogObject<loadingExistingSiteCompleteParams>
 */
export function loadingExistingSiteComplete(params) {
    return { evid: 139, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderUndoRedoLastChange' event (evid: 140)
 *
 * Event description: Module: builder. Flow: user clicks the undo / redo.
 *
 * @param {builderUndoRedoLastChangeParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - action type: undo/redo
 * @return {LogObject<builderUndoRedoLastChangeParams>} LogObject<builderUndoRedoLastChangeParams>
 */
export function builderUndoRedoLastChange(params) {
    return { evid: 140, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderUndoRedoSuccess' event (evid: 141)
 *
 * Event description: Module: builder. Flow: undo / redo was updated successfully on stage.
 *
 * @param {builderUndoRedoSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - action type: undo/redo
 * @return {LogObject<builderUndoRedoSuccessParams>} LogObject<builderUndoRedoSuccessParams>
 */
export function builderUndoRedoSuccess(params) {
    return { evid: 141, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarHelpClick' event (evid: 142)
 *
 * Event description: Module: builder. Flow: user clicks on help.
 *
 * @param {builderTopBarHelpClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarHelpClickParams>} LogObject<builderTopBarHelpClickParams>
 */
export function builderTopBarHelpClick(params) {
    return { evid: 142, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarUpgradeOpenOnHover' event (evid: 143)
 *
 * Event description: Module: builder. Flow: user hovers on Upgrade in top bar for 2 seconds
 *
 * @param {builderTopBarUpgradeOpenOnHoverParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarUpgradeOpenOnHoverParams>} LogObject<builderTopBarUpgradeOpenOnHoverParams>
 */
export function builderTopBarUpgradeOpenOnHover(params) {
    return { evid: 143, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderBusinessInfoEditBusinessInfoPageView' event (evid: 144)
 *
 * Event description: Module: builder. Flow: business info page is shown.
 *
 * @param {builderBusinessInfoEditBusinessInfoPageViewParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderBusinessInfoEditBusinessInfoPageViewParams>} LogObject<builderBusinessInfoEditBusinessInfoPageViewParams>
 */
export function builderBusinessInfoEditBusinessInfoPageView(params) {
    return { evid: 144, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderBusinessInfoUpdateChanges' event (evid: 145)
 *
 * Event description: Module: builder. Flow: user approves changes in the business info page.
 *
 * @param {builderBusinessInfoUpdateChangesParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} fieldsAdded - list of fields that were added divided by commas: name,logo,email,address,phone,fax,social-facebook,social-twitter,social-linkedin ..
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} fieldsUpdated - list of fields that were updated divided by commas: name,logo,email,address,phone,fax,social-facebook,social-twitter,social-linkedin ..
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} fieldsRemoved - list of fields that were removed divided by commas: name,logo,email,address,phone,fax,social-facebook,social-twitter,social-linkedin ..
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderBusinessInfoUpdateChangesParams>} LogObject<builderBusinessInfoUpdateChangesParams>
 */
export function builderBusinessInfoUpdateChanges(params) {
    return { evid: 145, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderBusinessInfoUpdateChangesSuccess' event (evid: 146)
 *
 * Event description: Module: builder. Flow: business info changes were updated successfully on stage.
 *
 * @param {builderBusinessInfoUpdateChangesSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time it took in ms
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderBusinessInfoUpdateChangesSuccessParams>} LogObject<builderBusinessInfoUpdateChangesSuccessParams>
 */
export function builderBusinessInfoUpdateChangesSuccess(params) {
    return { evid: 146, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderBusinessInfoClosePage' event (evid: 147)
 *
 * Event description: Module: builder. Flow: user closes the business info page (x/cancel).
 *
 * @param {builderBusinessInfoClosePageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderBusinessInfoClosePageParams>} LogObject<builderBusinessInfoClosePageParams>
 */
export function builderBusinessInfoClosePage(params) {
    return { evid: 147, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarGoToMyDashboard' event (evid: 148)
 *
 * Event description: Module: builder. Flow: user clicks on site > my dashboard.
 *
 * @param {builderTopBarGoToMyDashboardParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarGoToMyDashboardParams>} LogObject<builderTopBarGoToMyDashboardParams>
 */
export function builderTopBarGoToMyDashboard(params) {
    return { evid: 148, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarGoToSiteSettings' event (evid: 149)
 *
 * Event description: Module: builder. Flow: user clicks on site > site settings.
 *
 * @param {builderTopBarGoToSiteSettingsParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarGoToSiteSettingsParams>} LogObject<builderTopBarGoToSiteSettingsParams>
 */
export function builderTopBarGoToSiteSettings(params) {
    return { evid: 149, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarConnectDomainClick' event (evid: 150)
 *
 * Event description: Module: builder. Flow: user clicks on site > connect domain.
 *
 * @param {builderTopBarConnectDomainClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarConnectDomainClickParams>} LogObject<builderTopBarConnectDomainClickParams>
 */
export function builderTopBarConnectDomainClick(params) {
    return { evid: 150, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderConnectDomainPageView' event (evid: 151)
 *
 * Event description: Module: builder. Flow: connect domain page view (x/ maybe later).
 *
 * @param {builderConnectDomainPageViewParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} referral - origin of page view: 1st publish / top bar
 * @property {string} viewerName - viewer_name
 * @property {string} referralAdditionalInfo - --
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderConnectDomainPageViewParams>} LogObject<builderConnectDomainPageViewParams>
 */
export function builderConnectDomainPageView(params) {
    return { evid: 151, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderConnectDomainNavigateToDomainSPages' event (evid: 152)
 *
 * Event description: Module: builder. Flow: user navigates from connect domain.
 *
 * @param {builderConnectDomainNavigateToDomainSPagesParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - action type: undo/redo
 * @return {LogObject<builderConnectDomainNavigateToDomainSPagesParams>} LogObject<builderConnectDomainNavigateToDomainSPagesParams>
 */
export function builderConnectDomainNavigateToDomainSPages(params) {
    return { evid: 152, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderConnectDomainClosePage' event (evid: 153)
 *
 * Event description: Module: builder. Flow: user closes the connect domain page.
 *
 * @param {builderConnectDomainClosePageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - {X,maybe later}
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderConnectDomainClosePageParams>} LogObject<builderConnectDomainClosePageParams>
 */
export function builderConnectDomainClosePage(params) {
    return { evid: 153, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarViewSiteClick' event (evid: 154)
 *
 * Event description: Module: builder. Flow: user clicks on site > view published site.
 *
 * @param {builderTopBarViewSiteClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarViewSiteClickParams>} LogObject<builderTopBarViewSiteClickParams>
 */
export function builderTopBarViewSiteClick(params) {
    return { evid: 154, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderGoToEditorClick' event (evid: 155)
 *
 * Event description: Module: builder. Flow: user clicks on go to editor from topbar or on floating button.
 *
 * @param {builderGoToEditorClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - topbar, floating button
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderGoToEditorClickParams>} LogObject<builderGoToEditorClickParams>
 */
export function builderGoToEditorClick(params) {
    return { evid: 155, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderGoToEditorNotificationPageView' event (evid: 156)
 *
 * Event description: Module: builder. Flow: go to editor notification page view.
 *
 * @param {builderGoToEditorNotificationPageViewParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderGoToEditorNotificationPageViewParams>} LogObject<builderGoToEditorNotificationPageViewParams>
 */
export function builderGoToEditorNotificationPageView(params) {
    return { evid: 156, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderGoToEditorContinueToProEditorClick' event (evid: 157)
 *
 * Event description: Module: builder. Flow: user chooses to leave ADI and go to Pro Editor.
 *
 * @param {builderGoToEditorContinueToProEditorClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - migrate old blog homepage/migrate old blog design
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderGoToEditorContinueToProEditorClickParams>} LogObject<builderGoToEditorContinueToProEditorClickParams>
 */
export function builderGoToEditorContinueToProEditorClick(params) {
    return { evid: 157, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderGoToEditorClosePage' event (evid: 158)
 *
 * Event description: Module: builder. Flow: user closes the go to editor dialog
 *
 * @param {builderGoToEditorClosePageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderGoToEditorClosePageParams>} LogObject<builderGoToEditorClosePageParams>
 */
export function builderGoToEditorClosePage(params) {
    return { evid: 158, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarPreviewSiteClick' event (evid: 159)
 *
 * Event description: Module: builder. Flow: user clicks on preview site.
 *
 * @param {builderTopBarPreviewSiteClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - -
 * @property {string} origin - origin of preview: top bar , custom site design
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarPreviewSiteClickParams>} LogObject<builderTopBarPreviewSiteClickParams>
 */
export function builderTopBarPreviewSiteClick(params) {
    return { evid: 159, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPreviewSitePageView' event (evid: 160)
 *
 * Event description: Module: builder. Flow: preview site page view.
 *
 * @param {builderPreviewSitePageViewParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPreviewSitePageViewParams>} LogObject<builderPreviewSitePageViewParams>
 */
export function builderPreviewSitePageView(params) {
    return { evid: 160, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPreviewSiteSwitchToMobileDesktopView' event (evid: 161)
 *
 * Event description: Module: builder. Flow: user switches between desktop and mobile view.
 *
 * @param {builderPreviewSiteSwitchToMobileDesktopViewParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} viewMode - mobile / desktop
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {string} mode - preview in welcome tour / preview in builder
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPreviewSiteSwitchToMobileDesktopViewParams>} LogObject<builderPreviewSiteSwitchToMobileDesktopViewParams>
 */
export function builderPreviewSiteSwitchToMobileDesktopView(params) {
    return { evid: 161, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPreviewSiteClosePage' event (evid: 162)
 *
 * Event description: Module: builder. Flow: user closes the preview site mode.
 *
 * @param {builderPreviewSiteClosePageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} navigationClick - back / edit. relevant for preview in welcome tour
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {string} mode - preview in welcome tour / preview in builder
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPreviewSiteClosePageParams>} LogObject<builderPreviewSiteClosePageParams>
 */
export function builderPreviewSiteClosePage(params) {
    return { evid: 162, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarPublishSiteClick' event (evid: 163)
 *
 * Event description: Module: builder. Flow: user clicks on publish site.
 *
 * @param {builderTopBarPublishSiteClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {string} mode - builder / preview in welcome tour / preview in builder
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarPublishSiteClickParams>} LogObject<builderTopBarPublishSiteClickParams>
 */
export function builderTopBarPublishSiteClick(params) {
    return { evid: 163, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPublishSiteCongratsPageView' event (evid: 164)
 *
 * Event description: Module: builder. Flow: post publish congrats page view.
 *
 * @param {builderPublishSiteCongratsPageViewParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageView - --
 * @property {string} viewerName - viewer_name
 * @property {string} referralAdditionalInfo - --
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPublishSiteCongratsPageViewParams>} LogObject<builderPublishSiteCongratsPageViewParams>
 */
export function builderPublishSiteCongratsPageView(params) {
    return { evid: 164, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPublishSiteClosePage' event (evid: 165)
 *
 * Event description: Module: builder. Flow: user closes the congrats page (x or continue editting).
 *
 * @param {builderPublishSiteClosePageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {string} referralAdditionalInfo - --
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPublishSiteClosePageParams>} LogObject<builderPublishSiteClosePageParams>
 */
export function builderPublishSiteClosePage(params) {
    return { evid: 165, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderZoomInOut' event (evid: 166)
 *
 * Event description: Module: builder. Flow: user zooms in / out the site.
 *
 * @param {builderZoomInOutParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {number} scale - what is the scale after the click: 1,2,3,4,5
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} zoomType - zoom in / zoom out
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderZoomInOutParams>} LogObject<builderZoomInOutParams>
 */
export function builderZoomInOut(params) {
    return { evid: 166, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderLoadingError' event (evid: 167)
 *
 * Event description: Module: builder. Flow: error during first / existing loading .
 *
 * @param {builderLoadingErrorParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} loadType - "new" for initial load / "existing" for exisitng load
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {string} err - error message
 * @property {GUID} esi - session id
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderLoadingErrorParams>} LogObject<builderLoadingErrorParams>
 */
export function builderLoadingError(params) {
    return { evid: 167, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderAutosaveTryTo' event (evid: 168)
 *
 * Event description: Module: builder. Flow: try to autosave.
 *
 * @param {builderAutosaveTryToParams} params - The parameters for this event
 * @property {string} instanceId - instance_id, represents the story/msid
 * @property {boolean} saveToSanta - param=1, when trying to save to santa as well as to consumer
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - structure id
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - meat site id
 * @return {LogObject<builderAutosaveTryToParams>} LogObject<builderAutosaveTryToParams>
 */
export function builderAutosaveTryTo(params) {
    return { evid: 168, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'clientAutosaveSuccess' event (evid: 174)
 *
 * Event description: Module: server. Flow: site is saved automatically every fixed time (if the user has done some changes).
 *
 * @param {clientAutosaveSuccessParams} params - The parameters for this event
 * @property {GUID} instanceId - instance_id, represents the story/msid
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision number of editor
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed (in ms) from start
 * @property {GUID} esi - editor session id
 * @property {GUID} msid - msid
 * @return {LogObject<clientAutosaveSuccessParams>} LogObject<clientAutosaveSuccessParams>
 */
export function clientAutosaveSuccess(params) {
    return { evid: 174, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'stillLoadingSite' event (evid: 175)
 *
 * Event description: Module: builder. Flow: Sent after every 5 seconds from start loading till site is loaded successfully
 *
 * @param {stillLoadingSiteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} interval - 5 is the default, as this event is sent every 5 seconds
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - cumulative loading time since start event:  evid 125 for new site, evid 135 for existing site
 * @property {GUID} esi - editor session id, for every site editing session
 * @property {GUID} msid - metasite id
 * @property {string} siteMode - existing/new.
 * @return {LogObject<stillLoadingSiteParams>} LogObject<stillLoadingSiteParams>
 */
export function stillLoadingSite(params) {
    return { evid: 175, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarAddDropdownListOpenOnHover' event (evid: 177)
 *
 * Event description: Module: builder. Flow: user hovers (for 2 seconds) on the Add  button and the dropdown list is open
 *
 * @param {builderTopBarAddDropdownListOpenOnHoverParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarAddDropdownListOpenOnHoverParams>} LogObject<builderTopBarAddDropdownListOpenOnHoverParams>
 */
export function builderTopBarAddDropdownListOpenOnHover(params) {
    return { evid: 177, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarGiveFeedbackClick' event (evid: 178)
 *
 * Event description: Module: builder. Flow: user clicks on Give Feedback
 *
 * @param {builderTopBarGiveFeedbackClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarGiveFeedbackClickParams>} LogObject<builderTopBarGiveFeedbackClickParams>
 */
export function builderTopBarGiveFeedbackClick(params) {
    return { evid: 178, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarPreviewSiteSuccess' event (evid: 179)
 *
 * Event description: Module: builder. Flow: preview site success
 *
 * @param {builderTopBarPreviewSiteSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {string} mode - preview in welcome tour / preview in builder
 * @property {number} loadTime - time in ms elapsed since start loading the preview
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarPreviewSiteSuccessParams>} LogObject<builderTopBarPreviewSiteSuccessParams>
 */
export function builderTopBarPreviewSiteSuccess(params) {
    return { evid: 179, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderCoreLoadingLoadThemeChooserComplete' event (evid: 18)
 *
 * Event description: Module: builder. Flow: phase in loading the core
 *
 * @param {builderCoreLoadingLoadThemeChooserCompleteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - loading time diff from previous phase
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderCoreLoadingLoadThemeChooserCompleteParams>} LogObject<builderCoreLoadingLoadThemeChooserCompleteParams>
 */
export function builderCoreLoadingLoadThemeChooserComplete(params) {
    return { evid: 18, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarCreateNewSite' event (evid: 180)
 *
 * Event description: Module: builder. Flow: user clicks on create new site from sites or account menu
 *
 * @param {builderTopBarCreateNewSiteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarCreateNewSiteParams>} LogObject<builderTopBarCreateNewSiteParams>
 */
export function builderTopBarCreateNewSite(params) {
    return { evid: 180, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medGatewayAllocationRequestTry' event (evid: 1800)
 *
 * Event description: Module: MED. Flow: Gateway allocation request sent
 *
 * @param {medGatewayAllocationRequestTryParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editing session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medGatewayAllocationRequestTryParams>} LogObject<medGatewayAllocationRequestTryParams>
 */
export function medGatewayAllocationRequestTry(params) {
    return { evid: 1800, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medGatewayAllocationRequestSuccess' event (evid: 1801)
 *
 * Event description: Module: MED. Flow: Gateway allocation request returns successfully (allocation occurred)
 *
 * @param {medGatewayAllocationRequestSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - mapped already / allocated new / allocate after deallocation
 * @property {string} headlessName - headless_server_name
 * @property {string} adiBuilderType - MED
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from trial
 * @property {GUID} esi - editor session id, for every site editing session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medGatewayAllocationRequestSuccessParams>} LogObject<medGatewayAllocationRequestSuccessParams>
 */
export function medGatewayAllocationRequestSuccess(params) {
    return { evid: 1801, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medGatewayAllocationRequestFailed' event (evid: 1802)
 *
 * Event description: Module: MED. Flow: Gateway allocation request failed (allocation didn't succeed)
 *
 * @param {medGatewayAllocationRequestFailedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - uuid
 * @property {string} reason - reason for failure
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from trial
 * @property {GUID} esi - editor session id, for every site editing session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medGatewayAllocationRequestFailedParams>} LogObject<medGatewayAllocationRequestFailedParams>
 */
export function medGatewayAllocationRequestFailed(params) {
    return { evid: 1802, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarSeoPanelGetFoundOnGoogleClick' event (evid: 181)
 *
 * Event description: Module: builder. Flow: user clicks on site > get found on google
 *
 * @param {builderTopBarSeoPanelGetFoundOnGoogleClickParams} params - The parameters for this event
 * @property {string} instanceId - instance_id
 * @property {string} structureId - structure_id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - uuid
 * @property {string} industryId - industry_id
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - topbar / seo panel
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<builderTopBarSeoPanelGetFoundOnGoogleClickParams>} LogObject<builderTopBarSeoPanelGetFoundOnGoogleClickParams>
 */
export function builderTopBarSeoPanelGetFoundOnGoogleClick(params) {
    return { evid: 181, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarSiteSettingsMenuDialogOpen' event (evid: 182)
 *
 * Event description: Module: builder. Flow: user hovers on the site/settings  menu at the top bar (and the menu opens)
 *
 * @param {builderTopBarSiteSettingsMenuDialogOpenParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - type of builder editor: mobile / desktop
 * @property {GUID} uuid - uuid
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editing session id
 * @property {GUID} msid - msid
 * @return {LogObject<builderTopBarSiteSettingsMenuDialogOpenParams>} LogObject<builderTopBarSiteSettingsMenuDialogOpenParams>
 */
export function builderTopBarSiteSettingsMenuDialogOpen(params) {
    return { evid: 182, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPublishContinueClickFirstPublishFlow' event (evid: 183)
 *
 * Event description: Module: builder. Flow: user clicks on publish & Continue in first publish.
 *
 * @param {builderPublishContinueClickFirstPublishFlowParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} domainOption - {Wix domain, Customize domain }
 * @property {string} adiBuilderType - --
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - --
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPublishContinueClickFirstPublishFlowParams>} LogObject<builderPublishContinueClickFirstPublishFlowParams>
 */
export function builderPublishContinueClickFirstPublishFlow(params) {
    return { evid: 183, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarAccountMenuDialogOpen' event (evid: 184)
 *
 * Event description: Module: builder. Flow: user hovers on the account menu at the top bar (and the menu opens)
 *
 * @param {builderTopBarAccountMenuDialogOpenParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - type of builder editor: mobile / desktop
 * @property {GUID} uuid - uuid
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editing session id
 * @property {GUID} msid - msid
 * @return {LogObject<builderTopBarAccountMenuDialogOpenParams>} LogObject<builderTopBarAccountMenuDialogOpenParams>
 */
export function builderTopBarAccountMenuDialogOpen(params) {
    return { evid: 184, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderGoToEditorContinueToProEditorSuccess' event (evid: 185)
 *
 * Event description: Module: builder. Flow: user chooses to leave ADI and go to Pro Editor - success event (the editor started loading)
 *
 * @param {builderGoToEditorContinueToProEditorSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time from click
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderGoToEditorContinueToProEditorSuccessParams>} LogObject<builderGoToEditorContinueToProEditorSuccessParams>
 */
export function builderGoToEditorContinueToProEditorSuccess(params) {
    return { evid: 185, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPublishSiteCongratsPageClickOnDoneButton' event (evid: 186)
 *
 * Event description: Module: builder. Flow: post publish congrats page view - click on done
 *
 * @param {builderPublishSiteCongratsPageClickOnDoneButtonParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - --
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {boolean} showNextTime - {True if marked, else false)
 * @property {string} screenResolution - --
 * @property {string} pageView - all options {onboarding-1publish-congrats,onboarding-2publish-congrats,premium}
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPublishSiteCongratsPageClickOnDoneButtonParams>} LogObject<builderPublishSiteCongratsPageClickOnDoneButtonParams>
 */
export function builderPublishSiteCongratsPageClickOnDoneButton(params) {
    return { evid: 186, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPublishSiteCongratsPageClickOnViewSite' event (evid: 187)
 *
 * Event description: Module: builder. Flow: post publish congrats page view - click on view site
 *
 * @param {builderPublishSiteCongratsPageClickOnViewSiteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - --
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - --
 * @property {string} pageView - all options {onboarding-1publish-congrats,onboarding-2publish-congrats,premium}
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPublishSiteCongratsPageClickOnViewSiteParams>} LogObject<builderPublishSiteCongratsPageClickOnViewSiteParams>
 */
export function builderPublishSiteCongratsPageClickOnViewSite(params) {
    return { evid: 187, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPublishSiteCongratsPageClickOnSocial' event (evid: 188)
 *
 * Event description: Module: builder. Flow: post publish congrats page view - click on social button
 *
 * @param {builderPublishSiteCongratsPageClickOnSocialParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - --
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - --
 * @property {string} socialButton - {FB,Tweeter}
 * @property {string} pageView - all options {onboarding-1publish-congrats,onboarding-2publish-congrats,premium}
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPublishSiteCongratsPageClickOnSocialParams>} LogObject<builderPublishSiteCongratsPageClickOnSocialParams>
 */
export function builderPublishSiteCongratsPageClickOnSocial(params) {
    return { evid: 188, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarClickOnLinkToOneOfTheAccountPages' event (evid: 189)
 *
 * Event description: Module: builder. Flow: user clicks in account menu on one of the links to an account page (my sites, account settings, billing)
 *
 * @param {builderTopBarClickOnLinkToOneOfTheAccountPagesParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - page name (my sites, account settings, billing)
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - type of builder editor: mobile / desktop
 * @property {GUID} uuid - uuid
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editing session id
 * @property {GUID} msid - msid
 * @return {LogObject<builderTopBarClickOnLinkToOneOfTheAccountPagesParams>} LogObject<builderTopBarClickOnLinkToOneOfTheAccountPagesParams>
 */
export function builderTopBarClickOnLinkToOneOfTheAccountPages(params) {
    return { evid: 189, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderCoreLoadingLoadLayoutSolverComplete' event (evid: 19)
 *
 * Event description: Module: builder. Flow: phase in loading the core
 *
 * @param {builderCoreLoadingLoadLayoutSolverCompleteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - loading time diff from previous phase
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderCoreLoadingLoadLayoutSolverCompleteParams>} LogObject<builderCoreLoadingLoadLayoutSolverCompleteParams>
 */
export function builderCoreLoadingLoadLayoutSolverComplete(params) {
    return { evid: 19, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarWixLogoClick' event (evid: 190)
 *
 * Event description: Module: builder. Flow: user clicks the wix logo in the builder
 *
 * @param {builderTopBarWixLogoClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - type of builder editor: mobile / desktop
 * @property {GUID} uuid - uuid
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editing session id
 * @property {GUID} msid - msid
 * @return {LogObject<builderTopBarWixLogoClickParams>} LogObject<builderTopBarWixLogoClickParams>
 */
export function builderTopBarWixLogoClick(params) {
    return { evid: 190, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarHireAWebProfessional' event (evid: 191)
 *
 * Event description: Module: builder. Flow: user clicks on Hire a web professional from sites menu or smart assist panel
 *
 * @param {builderTopBarHireAWebProfessionalParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - type of ADI builder: desktop/MED/headless
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution of user device
 * @property {string} origin - top bar/smart assist
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarHireAWebProfessionalParams>} LogObject<builderTopBarHireAWebProfessionalParams>
 */
export function builderTopBarHireAWebProfessional(params) {
    return { evid: 191, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'migrateOldBlogLearnMoreClick' event (evid: 192)
 *
 * Event description: Module: Migrate old Blog. Flow: user clicks 'Learn more' in a pop up window
 *
 * @param {migrateOldBlogLearnMoreClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - resolution on user device screen
 * @property {string} origin - homepage / design
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<migrateOldBlogLearnMoreClickParams>} LogObject<migrateOldBlogLearnMoreClickParams>
 */
export function migrateOldBlogLearnMoreClick(params) {
    return { evid: 192, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderLightboxModeOpenClose' event (evid: 193)
 *
 * Event description: Module: builder. Flow: Lightbox mode opened/closed
 *
 * @param {builderLightboxModeOpenCloseParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {number} delay - chosen delay (for close action)
 * @property {string} pagesShown - list of pages the lightbox is selected to show on (for close action)
 * @property {string} adiBuilderType - desktop/MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - resolution on user device screen
 * @property {boolean} isActive - is Lightbox activated (for close action)
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - open/close
 * @return {LogObject<builderLightboxModeOpenCloseParams>} LogObject<builderLightboxModeOpenCloseParams>
 */
export function builderLightboxModeOpenClose(params) {
    return { evid: 193, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorChangeComponentFontSize' event (evid: 198)
 *
 * Event description: Module: contentEditor. Flow: user changes font size of a single component
 *
 * @param {contentEditorChangeComponentFontSizeParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile / MED...
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} direction - up / down
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} fieldName - which field is updated: title,subtitle,body,button
 * @property {string} origin - OSI / right click
 * @property {string} viewerName - viewer_name
 * @property {string} fontSize - font size
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorChangeComponentFontSizeParams>} LogObject<contentEditorChangeComponentFontSizeParams>
 */
export function contentEditorChangeComponentFontSize(params) {
    return { evid: 198, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorChangeLogoSize' event (evid: 199)
 *
 * Event description: Module: contentEditor. Flow: user changes the size of the logo in the header
 *
 * @param {contentEditorChangeLogoSizeParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile / MED / headless
 * @property {number} sizeValue - logo numeric size
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - deprecated
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorChangeLogoSizeParams>} LogObject<contentEditorChangeLogoSizeParams>
 */
export function contentEditorChangeLogoSize(params) {
    return { evid: 199, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'loadingPhaseInInitComplete' event (evid: 2)
 *
 * Event description: Module: builder. Flow: a phase in the init has been completed
 *
 * @param {loadingPhaseInInitCompleteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} subPhaseName - sub phase name
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} loadType - new / existing
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - load time of this phase in ms
 * @property {number} loadTime - total load time till this phase in ms
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} phaseName - phase name
 * @return {LogObject<loadingPhaseInInitCompleteParams>} LogObject<loadingPhaseInInitCompleteParams>
 */
export function loadingPhaseInInitComplete(params) {
    return { evid: 2, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderCoreLoadingLoadIframeStart' event (evid: 20)
 *
 * Event description: Module: builder. Flow: phase in loading the core
 *
 * @param {builderCoreLoadingLoadIframeStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - loading time diff from previous phase
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderCoreLoadingLoadIframeStartParams>} LogObject<builderCoreLoadingLoadIframeStartParams>
 */
export function builderCoreLoadingLoadIframeStart(params) {
    return { evid: 20, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorPanelIsLoaded' event (evid: 200)
 *
 * Event description: Module: contentEditor. Flow: content editor panel is loaded. E.g., edit, list item and media settings panels. It doesn't include popup dialogs..
 *
 * @param {contentEditorPanelIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - how the panel was opened - button/switch section/ double click / click on specific item / menu edit
 * @property {string} panelType - main / list item / social panel / media settings
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list / field
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorPanelIsLoadedParams>} LogObject<contentEditorPanelIsLoadedParams>
 */
export function contentEditorPanelIsLoaded(params) {
    return { evid: 200, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorPanelClosed' event (evid: 201)
 *
 * Event description: Module: contentEditor. Flow: user clicks on x / cancel to close a window (for every panel in the content editor).
 *
 * @param {contentEditorPanelClosedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} panelType - main / list item / social panel / media settings
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list / field
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorPanelClosedParams>} LogObject<contentEditorPanelClosedParams>
 */
export function contentEditorPanelClosed(params) {
    return { evid: 201, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorUpdateField' event (evid: 202)
 *
 * Event description: Module: contentEditor. Flow: user edits a field inside a section or in a sub panel (for example in the media settings panel). It doesn't include fields inside popup dialogs..
 *
 * @param {contentEditorUpdateFieldParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} fieldLength - length of text in the field
 * @property {string} fieldValue - value selected - only for dropdown fields / empty when user clears field
 * @property {string} fieldName - which field is updated: title,subtitle,body,image,button,link, list description
 * @property {string} origin - stage / panel
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} fieldType - text/ dropdown/ link / long text
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorUpdateFieldParams>} LogObject<contentEditorUpdateFieldParams>
 */
export function contentEditorUpdateField(params) {
    return { evid: 202, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorValueWasDeleted' event (evid: 203)
 *
 * Event description: Module: contentEditor. Flow: user deletes a value (not clears a cell, but clicks on the bin).
 *
 * @param {contentEditorValueWasDeletedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} fieldName - which field is updated: title,subtitle,body,image,button,link, list description
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} fieldType - text/ dropdown/ link / long text
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorValueWasDeletedParams>} LogObject<contentEditorValueWasDeletedParams>
 */
export function contentEditorValueWasDeleted(params) {
    return { evid: 203, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorClickOnHideUnhide' event (evid: 204)
 *
 * Event description: Module: contentEditor. Flow: user clicks on 'hide/unhide from section'. When user clears the value from a cell it also hides it.  Also, when hiding the top menu.
 *
 * @param {contentEditorClickOnHideUnhideParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} fieldName - which field is updated: title,subtitle,body,image,button,link, list description
 * @property {string} origin - where the action was made from (edit panel,OSI dropdown)
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} fieldType - text/ dropdown/ link / long text
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} action - hide/unhide
 * @return {LogObject<contentEditorClickOnHideUnhideParams>} LogObject<contentEditorClickOnHideUnhideParams>
 */
export function contentEditorClickOnHideUnhide(params) {
    return { evid: 204, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorComponentWasHiddenSuccess' event (evid: 205)
 *
 * Event description: Module: contentEditor. Flow: component was hidden successfully and change is seen on stahe.
 *
 * @param {contentEditorComponentWasHiddenSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} fieldName - which field is updated: title,subtitle,body,image,button,link, list description
 * @property {string} origin - where the action was made from (edit panel,OSI dropdown)
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {string} fieldType - text/ dropdown/ link / long text
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} action - hide/unhide
 * @return {LogObject<contentEditorComponentWasHiddenSuccessParams>} LogObject<contentEditorComponentWasHiddenSuccessParams>
 */
export function contentEditorComponentWasHiddenSuccess(params) {
    return { evid: 205, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorSwitchMediaTypeClick' event (evid: 206)
 *
 * Event description: Module: contentEditor. Flow: changing image/gallery/video.
 *
 * @param {contentEditorSwitchMediaTypeClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - which field is clicked: image/gallery/video/shape/clipart
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorSwitchMediaTypeClickParams>} LogObject<contentEditorSwitchMediaTypeClickParams>
 */
export function contentEditorSwitchMediaTypeClick(params) {
    return { evid: 206, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorClickOnReplaceOrganizeMedia' event (evid: 207)
 *
 * Event description: Module: contentEditor. Flow: user clicks on replace media when in image/clipart/shape/video mode or on organize media when in gallery mode.
 *
 * @param {contentEditorClickOnReplaceOrganizeMediaParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - which field is clicked: image/gallery/video/shape/clipart
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorClickOnReplaceOrganizeMediaParams>} LogObject<contentEditorClickOnReplaceOrganizeMediaParams>
 */
export function contentEditorClickOnReplaceOrganizeMedia(params) {
    return { evid: 207, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorCropImageDialogIsShown' event (evid: 208)
 *
 * Event description: Module: contentEditor. Flow: media - user clicks on the crop button and the crop dialog is loaded.
 *
 * @param {contentEditorCropImageDialogIsShownParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - media_settings_panel,osi
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list / field
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorCropImageDialogIsShownParams>} LogObject<contentEditorCropImageDialogIsShownParams>
 */
export function contentEditorCropImageDialogIsShown(params) {
    return { evid: 208, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageCropZoomInZoomOut' event (evid: 209)
 *
 * Event description: Module: contentEditor. Flow: user changes the zoom-in/out in the crop tool.
 *
 * @param {contentEditorImageCropZoomInZoomOutParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} zoomType - zoom in / zoom out
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list / field
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorImageCropZoomInZoomOutParams>} LogObject<contentEditorImageCropZoomInZoomOutParams>
 */
export function contentEditorImageCropZoomInZoomOut(params) {
    return { evid: 209, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPagePresetPreviewStartsLoading' event (evid: 21)
 *
 * Event description: Module: builder. Flow: page preset preview starts loading after silent builder
 *
 * @param {builderPagePresetPreviewStartsLoadingParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - fake browser / top bar
 * @property {string} viewerName - viewer_name
 * @property {number} pageNumber - page number of fpd layouts
 * @property {number} intervalLoadTime - **deprecated**
 * @property {string} mode - builder/welcome tour
 * @property {number} loadTime - **deprecated**
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} view - Select a Layout / Choose Your Colors
 * @return {LogObject<builderPagePresetPreviewStartsLoadingParams>} LogObject<builderPagePresetPreviewStartsLoadingParams>
 */
export function builderPagePresetPreviewStartsLoading(params) {
    return { evid: 21, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageCropSuccess' event (evid: 210)
 *
 * Event description: Module: contentEditor. Flow: image cropped after the user selected the area and clicked done, the change is updated also on the stage.
 *
 * @param {contentEditorImageCropSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list / field
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - deprecated
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorImageCropSuccessParams>} LogObject<contentEditorImageCropSuccessParams>
 */
export function contentEditorImageCropSuccess(params) {
    return { evid: 210, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageCropCancel' event (evid: 211)
 *
 * Event description: Module: contentEditor. Flow: user didn't use the crop function (clicked on done without doing a change or clicked on cancel).
 *
 * @param {contentEditorImageCropCancelParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list / field
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorImageCropCancelParams>} LogObject<contentEditorImageCropCancelParams>
 */
export function contentEditorImageCropCancel(params) {
    return { evid: 211, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderLinkDialogIsLoaded' event (evid: 212)
 *
 * Event description: Module: builder. Flow: user clicks on the link property in the section or in the media settings and the dialog is shown.
 *
 * @param {builderLinkDialogIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - from where the user opened the link dialog {button/richText/menu item}
 * @property {string} panelType - main / list item / social panel / media settings
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} fieldLength - length of text in the field
 * @property {string} fieldName - which field is updated: title,subtitle,body,image,button,link, list description
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} fieldType - text/ dropdown/ link / long text
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderLinkDialogIsLoadedParams>} LogObject<builderLinkDialogIsLoadedParams>
 */
export function builderLinkDialogIsLoaded(params) {
    return { evid: 212, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderLinkDialogDoneClicked' event (evid: 213)
 *
 * Event description: Module: builder. Flow: user clicks on 'done' in the link dialog.
 *
 * @param {builderLinkDialogDoneClickedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - from where the user edit the link {button/richText/menu item}
 * @property {string} panelType - main / list item / social panel / media settings
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} isHidden - true/false is the linked page hidden (for lightbox link)
 * @property {string} industryId - industry id
 * @property {string} linkTo - none / page / web /  document / email / phone / lightbox
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} fieldLength - length of text in the field
 * @property {string} fieldName - which field is updated: title,subtitle,body,image,button,link, list description
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} place - section name
 * @property {string} fieldType - text/ dropdown/ link / long text
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} targetPage - if web: the url, if page: the page name
 * @return {LogObject<builderLinkDialogDoneClickedParams>} LogObject<builderLinkDialogDoneClickedParams>
 */
export function builderLinkDialogDoneClicked(params) {
    return { evid: 213, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderLinkDialogClosed' event (evid: 214)
 *
 * Event description: Module: builder. Flow: user clicks on the x or the cancel buttons in the 'link to' window.
 *
 * @param {builderLinkDialogClosedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - from where the user edit the link {button/richText / menu item}
 * @property {string} panelType - main / list item / social panel / media settings
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderLinkDialogClosedParams>} LogObject<builderLinkDialogClosedParams>
 */
export function builderLinkDialogClosed(params) {
    return { evid: 214, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorClickOnAddDuplicateItemInAList' event (evid: 215)
 *
 * Event description: Module: contentEditor. Flow: user adds item to list.
 *
 * @param {contentEditorClickOnAddDuplicateItemInAListParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} addType - new/ duplicate
 * @return {LogObject<contentEditorClickOnAddDuplicateItemInAListParams>} LogObject<contentEditorClickOnAddDuplicateItemInAListParams>
 */
export function contentEditorClickOnAddDuplicateItemInAList(params) {
    return { evid: 215, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorAddDuplicateItemSuccess' event (evid: 216)
 *
 * Event description: Module: contentEditor. Flow: item was added successfully to stage.
 *
 * @param {contentEditorAddDuplicateItemSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} addType - new/ duplicate
 * @return {LogObject<contentEditorAddDuplicateItemSuccessParams>} LogObject<contentEditorAddDuplicateItemSuccessParams>
 */
export function contentEditorAddDuplicateItemSuccess(params) {
    return { evid: 216, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorReorderItemInAList' event (evid: 217)
 *
 * Event description: Module: contentEditor. Flow: user moves an item in list and changes its order.
 *
 * @param {contentEditorReorderItemInAListParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorReorderItemInAListParams>} LogObject<contentEditorReorderItemInAListParams>
 */
export function contentEditorReorderItemInAList(params) {
    return { evid: 217, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorItemReorderSuccess' event (evid: 218)
 *
 * Event description: Module: contentEditor. Flow: items order changed successfully on stage.
 *
 * @param {contentEditorItemReorderSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorItemReorderSuccessParams>} LogObject<contentEditorItemReorderSuccessParams>
 */
export function contentEditorItemReorderSuccess(params) {
    return { evid: 218, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorSwitchListItemToPage' event (evid: 219)
 *
 * Event description: Module: contentEditor. Flow: user moves an item to a page.
 *
 * @param {contentEditorSwitchListItemToPageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} originPageId - originPageId and pageId will be ewual in this phase since the target is not known
 * @property {string} pageId - number of ORIGIN page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorSwitchListItemToPageParams>} LogObject<contentEditorSwitchListItemToPageParams>
 */
export function contentEditorSwitchListItemToPage(params) {
    return { evid: 219, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPagePresetPreviewLoaded' event (evid: 22)
 *
 * Event description: Module: builder. Flow: page preset preview loaded after silent builder
 *
 * @param {builderPagePresetPreviewLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {boolean} isFirstPage - deprecated
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - fake browser / top bar
 * @property {string} viewerName - viewer_name
 * @property {number} pageNumber - page number of fpd layouts
 * @property {number} intervalLoadTime - time (in ms) elapsed since loading started
 * @property {string} mode - builder/welcome tour
 * @property {number} loadTime - time (in ms) elapsed since loading started
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} view - select a layout / choose your colors / ....
 * @return {LogObject<builderPagePresetPreviewLoadedParams>} LogObject<builderPagePresetPreviewLoadedParams>
 */
export function builderPagePresetPreviewLoaded(params) {
    return { evid: 22, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorItemMovedToAnotherPageSuccess' event (evid: 220)
 *
 * Event description: Module: contentEditor. Flow: item moved to a page successfully.
 *
 * @param {contentEditorItemMovedToAnotherPageSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} originPageId - number of origin page
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorItemMovedToAnotherPageSuccessParams>} LogObject<contentEditorItemMovedToAnotherPageSuccessParams>
 */
export function contentEditorItemMovedToAnotherPageSuccess(params) {
    return { evid: 220, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorGalleryLayoutWindowOpened' event (evid: 221)
 *
 * Event description: Module: contentEditor. Flow: user clicks on Change Gallery Type and galleries layout window is opened.
 *
 * @param {contentEditorGalleryLayoutWindowOpenedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} layoutType - image / full width / shape
 * @property {number} noOfSuggestions - int number that represents the number of suggested layouts
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorGalleryLayoutWindowOpenedParams>} LogObject<contentEditorGalleryLayoutWindowOpenedParams>
 */
export function contentEditorGalleryLayoutWindowOpened(params) {
    return { evid: 221, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorGalleryLayoutChanged' event (evid: 222)
 *
 * Event description: Module: contentEditor. Flow: user selected a layout from the list & the galleries window is closed (no need to report the close event also).
 *
 * @param {contentEditorGalleryLayoutChangedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} layoutType - image / full width / shape
 * @property {number} noOfSuggestions - int number that represents the number of suggested layouts
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} placeOfSelected - int number that represents the place of the selected layout (1 for the first,…)
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} galleryType - which gallery layout selected: stripSlidshow/fullwidthCollage/…
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorGalleryLayoutChangedParams>} LogObject<contentEditorGalleryLayoutChangedParams>
 */
export function contentEditorGalleryLayoutChanged(params) {
    return { evid: 222, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorGalleryLayoutChangedSuccessEventIsCancelled' event (evid: 223)
 *
 * Event description: Module: contentEditor. Flow: text manager is loaded.
 *
 * @param {contentEditorGalleryLayoutChangedSuccessEventIsCancelledParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} layoutType - **deprecated**
 * @property {number} noOfSuggestions - number of texts in the panel
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {number} noOfUserSuggestions - number of texts originates in user's site in the panel
 * @property {string} textType - what triggered the text manager: description / title / ....
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} placeOfSelected - **deprecated**
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} galleryType - **deprecated**
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorGalleryLayoutChangedSuccessEventIsCancelledParams>} LogObject<contentEditorGalleryLayoutChangedSuccessEventIsCancelledParams>
 */
export function contentEditorGalleryLayoutChangedSuccessEventIsCancelled(params) {
    return { evid: 223, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorGalleryLayoutWindowClosed' event (evid: 224)
 *
 * Event description: Module: contentEditor. Flow: user closed the layout panel without changing the layout.
 *
 * @param {contentEditorGalleryLayoutWindowClosedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} layoutType - image / full width / shape
 * @property {number} noOfSuggestions - int number that represents the number of suggested layouts
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} galleryType - which gallery layout selected: stripSlidshow/fullwidthCollage/…
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorGalleryLayoutWindowClosedParams>} LogObject<contentEditorGalleryLayoutWindowClosedParams>
 */
export function contentEditorGalleryLayoutWindowClosed(params) {
    return { evid: 224, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorFailoverReArrangeSolution' event (evid: 225)
 *
 * Event description: Module: contentEditor. Flow: user added more content in list item (e.g. long title), the layout is the same but the solution has changed.
 *
 * @param {contentEditorFailoverReArrangeSolutionParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorFailoverReArrangeSolutionParams>} LogObject<contentEditorFailoverReArrangeSolutionParams>
 */
export function contentEditorFailoverReArrangeSolution(params) {
    return { evid: 225, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorFailoverReArrangeLayout' event (evid: 226)
 *
 * Event description: Module: contentEditor. Flow: user added more content, the solution cannot be generated and a new layout is shown.
 *
 * @param {contentEditorFailoverReArrangeLayoutParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorFailoverReArrangeLayoutParams>} LogObject<contentEditorFailoverReArrangeLayoutParams>
 */
export function contentEditorFailoverReArrangeLayout(params) {
    return { evid: 226, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorFailToFitLayoutCanTFindAProperLayout' event (evid: 227)
 *
 * Event description: Module: contentEditor. Flow: CRM has failed to fit a layout.
 *
 * @param {contentEditorFailToFitLayoutCanTFindAProperLayoutParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} reason - failure reason
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorFailToFitLayoutCanTFindAProperLayoutParams>} LogObject<contentEditorFailToFitLayoutCanTFindAProperLayoutParams>
 */
export function contentEditorFailToFitLayoutCanTFindAProperLayout(params) {
    return { evid: 227, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorFieldWasUpdatedSuccess' event (evid: 228)
 *
 * Event description: Module: contentEditor. Flow: field was updated successfully.
 *
 * @param {contentEditorFieldWasUpdatedSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {boolean} bestMatchExists - text manager:  true if best match exists in the suggestions
 * @property {string} fieldPrevValue - text value before update
 * @property {string} adiBuilderType - adi_builder_type
 * @property {boolean} isBestMatch - text manager:  true if best match
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} textOrigin - cms, user import (imported from facebook page / site/...)
 * @property {string} screenResolution - screen_resolution
 * @property {number} noOfFaces - for images only - no of recognized faces
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} fieldLength - length of text in the field
 * @property {string} fieldValue - value selected - only for dropdown fields / empty when user clears field (starting x/12/20 also includes data for text field
 * @property {string} fieldName - which field is updated: title,subtitle,body,image,button,link, list description
 * @property {string} origin - panel / stage
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} position - text manager: index of the text
 * @property {number} loadTime - time it took in ms
 * @property {string} fieldType - text/ dropdown/ link / long text
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} editType - text manager: append / replace
 * @return {LogObject<contentEditorFieldWasUpdatedSuccessParams>} LogObject<contentEditorFieldWasUpdatedSuccessParams>
 */
export function contentEditorFieldWasUpdatedSuccess(params) {
    return { evid: 228, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorDataInsertedToTpaAutomatically' event (evid: 229)
 *
 * Event description: Module: contentEditor. Flow: data from CMS is inserted to the TPA automatically.
 *
 * @param {contentEditorDataInsertedToTpaAutomaticallyParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} extraInfo - additional info for TPAs,e.g for Bookings private/group
 * @property {string} appId - the TPA id
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorDataInsertedToTpaAutomaticallyParams>} LogObject<contentEditorDataInsertedToTpaAutomaticallyParams>
 */
export function contentEditorDataInsertedToTpaAutomatically(params) {
    return { evid: 229, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderCoreLoadingLoadIframeComplete' event (evid: 23)
 *
 * Event description: Module: builder. Flow: when the user chooses one of the options or clicks skip
 *
 * @param {builderCoreLoadingLoadIframeCompleteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} colorationLevel - plain, tinted, colorful,...
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} navigationClick - edit site / skip / preview
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - **deprecated**
 * @property {string} mode - builder/welcome tour
 * @property {number} position - position of the chosen option
 * @property {string} presetList - list of all presets in selected design
 * @property {number} loadTime - **deprecated**
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} colorationMode - light, dark, mix
 * @property {GUID} msid - metasite id
 * @property {string} view - presets preview /  preview
 * @return {LogObject<builderCoreLoadingLoadIframeCompleteParams>} LogObject<builderCoreLoadingLoadIframeCompleteParams>
 */
export function builderCoreLoadingLoadIframeComplete(params) {
    return { evid: 23, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorClickOnHideShowOnItemInListItems' event (evid: 230)
 *
 * Event description: Module: contentEditor. Flow: user clicks on 'hide/show on an item in list.
 *
 * @param {contentEditorClickOnHideShowOnItemInListItemsParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} action - hide/show
 * @return {LogObject<contentEditorClickOnHideShowOnItemInListItemsParams>} LogObject<contentEditorClickOnHideShowOnItemInListItemsParams>
 */
export function contentEditorClickOnHideShowOnItemInListItems(params) {
    return { evid: 230, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorClickOnHideShowOnItemInListItemsSuccess' event (evid: 231)
 *
 * Event description: Module: contentEditor. Flow: user clicks on 'hide/show on an item in list, and action has successed.
 *
 * @param {contentEditorClickOnHideShowOnItemInListItemsSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} action - hide/show
 * @return {LogObject<contentEditorClickOnHideShowOnItemInListItemsSuccessParams>} LogObject<contentEditorClickOnHideShowOnItemInListItemsSuccessParams>
 */
export function contentEditorClickOnHideShowOnItemInListItemsSuccess(params) {
    return { evid: 231, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorRichTextClick' event (evid: 232)
 *
 * Event description: Module: contentEditor. Flow: user clicks on rich text
 *
 * @param {contentEditorRichTextClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} richTextMode - on/off
 * @property {string} industryId - industry id
 * @property {string} richTextType - {Bold,Italic,Underline,Numbers,Bullets,Link,ClearFormating}
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} fieldLength - length of text in the field
 * @property {string} fieldValue - value selected - only for dropdown fields / empty when user clears field
 * @property {string} fieldName - which field is updated: title,subtitle,body,image,button,link, list description
 * @property {string} origin - stage / panel
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} fieldType - text/ dropdown/ link / long text
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorRichTextClickParams>} LogObject<contentEditorRichTextClickParams>
 */
export function contentEditorRichTextClick(params) {
    return { evid: 232, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageCropSave' event (evid: 233)
 *
 * Event description: Module: contentEditor. Flow: user saves the crop changes
 *
 * @param {contentEditorImageCropSaveParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list / field
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorImageCropSaveParams>} LogObject<contentEditorImageCropSaveParams>
 */
export function contentEditorImageCropSave(params) {
    return { evid: 233, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageCropCropClick' event (evid: 234)
 *
 * Event description: Module: contentEditor. Flow: user clicked on one of the Media Size options in media settings panel
 *
 * @param {contentEditorImageCropCropClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - media size: crop, background, show entire image, box....
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list / field
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorImageCropCropClickParams>} LogObject<contentEditorImageCropCropClickParams>
 */
export function contentEditorImageCropCropClick(params) {
    return { evid: 234, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorOpenQuickActionDropdown' event (evid: 235)
 *
 * Event description: Module: contentEditor. Flow: user opens the quick actions dropdown from OSI/right click
 *
 * @param {contentEditorOpenQuickActionDropdownParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} fieldName - which field is selected: title,subtitle,body,image,button,logo, list item etc.
 * @property {string} origin - OSI / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorOpenQuickActionDropdownParams>} LogObject<contentEditorOpenQuickActionDropdownParams>
 */
export function contentEditorOpenQuickActionDropdown(params) {
    return { evid: 235, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorQuickActionClick' event (evid: 236)
 *
 * Event description: Module: contentEditor. Flow: user performs a quick action from the OSI/right click dropdown or double clicking on a component
 *
 * @param {contentEditorQuickActionClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - the name of action clicked (hide/edit/settings etc.)
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} fieldName - which field is selected: title,subtitle,body,image,button,logo, list item etc.
 * @property {string} origin - where the action was made from: double click/OSI dropdown
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorQuickActionClickParams>} LogObject<contentEditorQuickActionClickParams>
 */
export function contentEditorQuickActionClick(params) {
    return { evid: 236, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorChangeContentColorClick' event (evid: 237)
 *
 * Event description: Module: contentEditor. Flow: user chooses a color for a specific content in a section
 *
 * @param {contentEditorChangeContentColorClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} colorType - readable/ unreadable
 * @property {string} viewerName - viewer_name
 * @property {string} contentName - which content is updated: title,subtitle,body,image,button,link, list description
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} colorLocation - D1, D2, D3, L1, L2, L3
 * @return {LogObject<contentEditorChangeContentColorClickParams>} LogObject<contentEditorChangeContentColorClickParams>
 */
export function contentEditorChangeContentColorClick(params) {
    return { evid: 237, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorChangeContentColorSuccess' event (evid: 238)
 *
 * Event description: Module: contentEditor. Flow: user chooses a color on the OSI tooltip shown for a specific content in the section
 *
 * @param {contentEditorChangeContentColorSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} colorType - readable/ unreadable
 * @property {string} viewerName - viewer_name
 * @property {string} contentName - content updated: title,subtitle,body,image,button,link, list description,...
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} colorLocation - D1, D2, D3, L1, L2, L3
 * @return {LogObject<contentEditorChangeContentColorSuccessParams>} LogObject<contentEditorChangeContentColorSuccessParams>
 */
export function contentEditorChangeContentColorSuccess(params) {
    return { evid: 238, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorResetContentColorClick' event (evid: 239)
 *
 * Event description: Module: contentEditor. Flow: user clicks on "fix color" on the OSI tooltip shown for a content in section
 *
 * @param {contentEditorResetContentColorClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} contentName - content updated: title,subtitle,body,image,button,link, list description,...
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorResetContentColorClickParams>} LogObject<contentEditorResetContentColorClickParams>
 */
export function contentEditorResetContentColorClick(params) {
    return { evid: 239, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPublishSuccess' event (evid: 24)
 *
 * Event description: Module: builder. Flow: publish succeeded (including publish time)
 *
 * @param {builderPublishSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {boolean} isFirst - is first publish
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time (in ms) elapsed since the final click on publish
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPublishSuccessParams>} LogObject<builderPublishSuccessParams>
 */
export function builderPublishSuccess(params) {
    return { evid: 24, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorResetContentColorSuccess' event (evid: 240)
 *
 * Event description: Module: contentEditor. Flow: color is reset for a specific content in section
 *
 * @param {contentEditorResetContentColorSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} contentName - content type reset, in list items it is the content the OSI was clicked from
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorResetContentColorSuccessParams>} LogObject<contentEditorResetContentColorSuccessParams>
 */
export function contentEditorResetContentColorSuccess(params) {
    return { evid: 240, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorHideComponentFromSite' event (evid: 241)
 *
 * Event description: Module: contentEditor. Flow: user clicks on 'hide Icons' in smart assist card tour
 *
 * @param {contentEditorHideComponentFromSiteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} fieldName - which field is updated: title,subtitle,body,image,button,link, list description
 * @property {string} origin - smart assist
 * @property {string} viewerName - viewer_name
 * @property {string} fieldType - text/ dropdown/ link / long text
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual / smart assist
 * @property {GUID} msid - metasite id
 * @property {string} action - hide/unhide
 * @return {LogObject<contentEditorHideComponentFromSiteParams>} LogObject<contentEditorHideComponentFromSiteParams>
 */
export function contentEditorHideComponentFromSite(params) {
    return { evid: 241, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorHideComponentFromSiteSuccess' event (evid: 242)
 *
 * Event description: Module: contentEditor. Flow: user clicks on 'hide Icons' in smart assist card tour
 *
 * @param {contentEditorHideComponentFromSiteSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} fieldName - which field is updated: title,subtitle,body,image,button,link, list description
 * @property {string} origin - smart assist
 * @property {string} viewerName - viewer_name
 * @property {string} fieldType - text/ dropdown/ link / long text
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual / smart assist
 * @property {GUID} msid - metasite id
 * @property {string} action - hide/unhide
 * @return {LogObject<contentEditorHideComponentFromSiteSuccessParams>} LogObject<contentEditorHideComponentFromSiteSuccessParams>
 */
export function contentEditorHideComponentFromSiteSuccess(params) {
    return { evid: 242, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorChangeButtonStyleClick' event (evid: 243)
 *
 * Event description: Module: contentEditor. Flow: user chooses another style for a button
 *
 * @param {contentEditorChangeButtonStyleClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} styleIndex - position on panel
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list / ..
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorChangeButtonStyleClickParams>} LogObject<contentEditorChangeButtonStyleClickParams>
 */
export function contentEditorChangeButtonStyleClick(params) {
    return { evid: 243, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorChangeButtonStyleSuccess' event (evid: 244)
 *
 * Event description: Module: contentEditor. Flow: user chooses another style for a button
 *
 * @param {contentEditorChangeButtonStyleSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} styleIndex - position on panel
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list...
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorChangeButtonStyleSuccessParams>} LogObject<contentEditorChangeButtonStyleSuccessParams>
 */
export function contentEditorChangeButtonStyleSuccess(params) {
    return { evid: 244, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorPopupDialogIsLoaded' event (evid: 245)
 *
 * Event description: Module: contentEditor. Flow: content editor popup dialog is loaded. E.g., button style dialog.
 *
 * @param {contentEditorPopupDialogIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - dialog name
 * @property {string} structureId - structure id
 * @property {string} source - how the panel was opened - button/button in list item
 * @property {string} adiBuilderType - desktop/mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorPopupDialogIsLoadedParams>} LogObject<contentEditorPopupDialogIsLoadedParams>
 */
export function contentEditorPopupDialogIsLoaded(params) {
    return { evid: 245, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorPopupDialogIsClosed' event (evid: 246)
 *
 * Event description: Module: contentEditor. Flow: content editor popup dialog is loaded. E.g., button style dialog.
 *
 * @param {contentEditorPopupDialogIsClosedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - dialog name
 * @property {string} structureId - structure id
 * @property {string} source - how the panel was opened - button/
 * @property {string} adiBuilderType - desktop/mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} objectType - single / list
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorPopupDialogIsClosedParams>} LogObject<contentEditorPopupDialogIsClosedParams>
 */
export function contentEditorPopupDialogIsClosed(params) {
    return { evid: 246, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorChangeImageOpacity' event (evid: 247)
 *
 * Event description: Module: contentEditor. Flow: user changes image opacity in the media settings panel
 *
 * @param {contentEditorChangeImageOpacityParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile / MED / headless
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI /right click / panel
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {boolean} isReset - true if opacity was reset
 * @property {number} opacity - image opacity
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorChangeImageOpacityParams>} LogObject<contentEditorChangeImageOpacityParams>
 */
export function contentEditorChangeImageOpacity(params) {
    return { evid: 247, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorChangeColorBehindImage' event (evid: 248)
 *
 * Event description: Module: contentEditor. Flow: user changes the color behind image in the media settings panel
 *
 * @param {contentEditorChangeColorBehindImageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile / MED / headless
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} colorLocation - index of color position
 * @return {LogObject<contentEditorChangeColorBehindImageParams>} LogObject<contentEditorChangeColorBehindImageParams>
 */
export function contentEditorChangeColorBehindImage(params) {
    return { evid: 248, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorTextManagerSearchResultsPresented' event (evid: 249)
 *
 * Event description: Module: contentEditor. Flow: user search for a text and get search results (including empty results page)
 *
 * @param {contentEditorTextManagerSearchResultsPresentedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} textType - title / description / ...
 * @property {number} bestMatchResults - # of best match texts
 * @property {string} adiBuilderType - desktop / mobile / ...
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} results - # of results
 * @property {string} objectType - single / list / field
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorTextManagerSearchResultsPresentedParams>} LogObject<contentEditorTextManagerSearchResultsPresentedParams>
 */
export function contentEditorTextManagerSearchResultsPresented(params) {
    return { evid: 249, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introCreateSiteOnMedClick' event (evid: 25)
 *
 * Event description: Module: intro. Flow: click 'Start' on 'Let's get you a website' page (last page of MED intro)
 *
 * @param {introCreateSiteOnMedClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - --not relevant--
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<introCreateSiteOnMedClickParams>} LogObject<introCreateSiteOnMedClickParams>
 */
export function introCreateSiteOnMedClick(params) {
    return { evid: 25, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementReplaceSectionDesignPanelIsLoaded' event (evid: 250)
 *
 * Event description: Module: sectionManagement. Flow: user opens replace section design panel
 *
 * @param {sectionManagementReplaceSectionDesignPanelIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - where was the panel opened from {section/content - bg hidden}
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementReplaceSectionDesignPanelIsLoadedParams>} LogObject<sectionManagementReplaceSectionDesignPanelIsLoadedParams>
 */
export function sectionManagementReplaceSectionDesignPanelIsLoaded(params) {
    return { evid: 250, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementChangeASectionLayout' event (evid: 251)
 *
 * Event description: Module: sectionManagement. Flow: user switches section's layout .
 *
 * @param {sectionManagementChangeASectionLayoutParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {boolean} is_columns - flag for columns in selected layout
 * @property {number} noOfRecommendedViewed - number of recommended layouts viewed by the user (by scrolling down the list)
 * @property {number} noOfSuggestions - int number that represents the number of suggested layouts that the user saw
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - where was the panel opened from {section/content - bg hidden}
 * @property {number} noOfSuggestionsViewed - **deprecated**
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} placeOfSelected - int number that represents the place of the selected layout (1 for the first,…)
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} selectedLayout - layout id
 * @property {string} place - recommended / others
 * @property {string} previousLayout - the previous layout id (before the change)
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementChangeASectionLayoutParams>} LogObject<sectionManagementChangeASectionLayoutParams>
 */
export function sectionManagementChangeASectionLayout(params) {
    return { evid: 251, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementChangeSectionLayoutSuccess' event (evid: 252)
 *
 * Event description: Module: sectionManagement. Flow: selected layout changed and updated on the stage.
 *
 * @param {sectionManagementChangeSectionLayoutSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {boolean} is_columns - flag for columns in selected layout
 * @property {number} noOfRecommendedViewed - number of recommended layouts viewed by the user (by scrolling down the list)
 * @property {number} noOfSuggestions - int number that represents the number of suggested layouts that the user saw
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - where was the panel opened from {section/content - bg hidden}
 * @property {number} noOfSuggestionsViewed - **deprecated**
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} placeOfSelected - int number that represents the place of the selected layout (1 for the first,…)
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} selectedLayout - layout id
 * @property {number} loadTime - time in ms
 * @property {string} place - recommended / others
 * @property {string} previousLayout - the previous layout id (before the change)
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementChangeSectionLayoutSuccessParams>} LogObject<sectionManagementChangeSectionLayoutSuccessParams>
 */
export function sectionManagementChangeSectionLayoutSuccess(params) {
    return { evid: 252, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementAddSectionPanelIsLoaded' event (evid: 253)
 *
 * Event description: Module: sectionManagement. Flow: user clicks on add section and the panel is loaded.
 *
 * @param {sectionManagementAddSectionPanelIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - top bar / menu link panel
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementAddSectionPanelIsLoadedParams>} LogObject<sectionManagementAddSectionPanelIsLoadedParams>
 */
export function sectionManagementAddSectionPanelIsLoaded(params) {
    return { evid: 253, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementSwitchSectionTypeInTheAddSectionPanel' event (evid: 254)
 *
 * Event description: Module: sectionManagement. Flow: user switches between section types.
 *
 * @param {sectionManagementSwitchSectionTypeInTheAddSectionPanelParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} place - where the group is placed: recommended / others / expanded list
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementSwitchSectionTypeInTheAddSectionPanelParams>} LogObject<sectionManagementSwitchSectionTypeInTheAddSectionPanelParams>
 */
export function sectionManagementSwitchSectionTypeInTheAddSectionPanel(params) {
    return { evid: 254, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementAddSectionToPage' event (evid: 255)
 *
 * Event description: Module: sectionManagement. Flow: user chooses a layout of the new section.
 * In case of dragging the section, this event will be reported only after user positioned the section in the desired place.
 *
 * @param {sectionManagementAddSectionToPageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {boolean} is_columns - flag for columns in selected layout
 * @property {number} noOfSuggestions - int number that represents the number of suggested layouts that the user saw
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {boolean} isLinked - flag for linked sections
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} placeOfSelected - int number that represents the place of the selected layout (1 for the first,…)
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - from where the section was added: sections panel / apps panel etc
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} position - position of section in page
 * @property {string} selectedLayout - layout id
 * @property {string} place - where the group is placed: recommended / others / expanded list
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} addType - click / drag / tap (on MED) / none (from app panel)
 * @return {LogObject<sectionManagementAddSectionToPageParams>} LogObject<sectionManagementAddSectionToPageParams>
 */
export function sectionManagementAddSectionToPage(params) {
    return { evid: 255, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementAddSectionToPageSuccess' event (evid: 256)
 *
 * Event description: Module: sectionManagement. Flow: section was added to stage successfully.
 *
 * @param {sectionManagementAddSectionToPageSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {boolean} is_columns - flag for columns in selected layout
 * @property {number} noOfSuggestions - int number that represents the number of suggested layouts that the user saw
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {boolean} isLinked - flag for linked sections
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} placeOfSelected - int number that represents the place of the selected layout (1 for the first,…)
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} position - position of section in page
 * @property {string} selectedLayout - layout id
 * @property {number} loadTime - time in ms
 * @property {string} place - where the group is placed: recommended / others / expanded list
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @property {string} addType - click / drag / tap (on MED) / none (from app panel)
 * @return {LogObject<sectionManagementAddSectionToPageSuccessParams>} LogObject<sectionManagementAddSectionToPageSuccessParams>
 */
export function sectionManagementAddSectionToPageSuccess(params) {
    return { evid: 256, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementSectionMenuDeleteSectionClick' event (evid: 257)
 *
 * Event description: Module: sectionManagement. Flow: user clicks on delete section from the section menu.
 *
 * @param {sectionManagementSectionMenuDeleteSectionClickParams} params - The parameters for this event
 * @property {string} instanceId - instance_id
 * @property {string} sectionType - section_type
 * @property {string} structureId - structure_id
 * @property {boolean} isLinked - flag for linked sections
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - uuid
 * @property {string} industryId - industry_id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - page_id
 * @property {number} revision - revision
 * @property {string} origin - how delete section dialog was shown: hide-all-elements / more-actions
 * @property {string} sectionAlias - section_alias
 * @property {string} viewerName - viewer_name
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} editMode - edit_mode
 * @property {string} nodeId - node_id
 * @property {GUID} msid - msid
 * @return {LogObject<sectionManagementSectionMenuDeleteSectionClickParams>} LogObject<sectionManagementSectionMenuDeleteSectionClickParams>
 */
export function sectionManagementSectionMenuDeleteSectionClick(params) {
    return { evid: 257, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementDeleteSectionSuccess' event (evid: 258)
 *
 * Event description: Module: sectionManagement. Flow: section was deleted successfully.
 *
 * @param {sectionManagementDeleteSectionSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {boolean} isLinked - flag for linked sections
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - origin
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementDeleteSectionSuccessParams>} LogObject<sectionManagementDeleteSectionSuccessParams>
 */
export function sectionManagementDeleteSectionSuccess(params) {
    return { evid: 258, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementMoveSectionWithinThePageStartDragging' event (evid: 259)
 *
 * Event description: Module: sectionManagement. Flow: user chooses a section to move within the page and start to drag it. Evid 260 will represent drag-complete.
 *
 * @param {sectionManagementMoveSectionWithinThePageStartDraggingParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} originPosition - new position of section in page
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementMoveSectionWithinThePageStartDraggingParams>} LogObject<sectionManagementMoveSectionWithinThePageStartDraggingParams>
 */
export function sectionManagementMoveSectionWithinThePageStartDragging(params) {
    return { evid: 259, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSignUpLoginPopUpShown' event (evid: 26)
 *
 * Event description: Module: builder. Flow: in anon flow, when the sign up login window is shown
 *
 * @param {builderSignUpLoginPopUpShownParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - which click has triggered the pop up
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - **deprecated**
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderSignUpLoginPopUpShownParams>} LogObject<builderSignUpLoginPopUpShownParams>
 */
export function builderSignUpLoginPopUpShown(params) {
    return { evid: 26, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementMoveSectionWithinThePageClick' event (evid: 260)
 *
 * Event description: Module: sectionManagement. Flow: user clicks on move section up/down/scroll within the page.
 *
 * @param {sectionManagementMoveSectionWithinThePageClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} direction - up / down / scroll
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} originPosition - new position of section in page
 * @property {string} origin - OSI / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} position - position of section in page
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementMoveSectionWithinThePageClickParams>} LogObject<sectionManagementMoveSectionWithinThePageClickParams>
 */
export function sectionManagementMoveSectionWithinThePageClick(params) {
    return { evid: 260, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementSectionMovedWithinThePageSuccess' event (evid: 261)
 *
 * Event description: Module: sectionManagement. Flow: user chooses the target page of the transfered section.
 *
 * @param {sectionManagementSectionMovedWithinThePageSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} direction - up / down / scroll
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} originPosition - new position of section in page
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} position - position of section in page
 * @property {number} loadTime - time in ms
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementSectionMovedWithinThePageSuccessParams>} LogObject<sectionManagementSectionMovedWithinThePageSuccessParams>
 */
export function sectionManagementSectionMovedWithinThePageSuccess(params) {
    return { evid: 261, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementSectionMenuMoveSectionToAnotherPageClick' event (evid: 262)
 *
 * Event description: Module: sectionManagement. Flow: user clicks on 'move section to another page' from the section cog.
 *
 * @param {sectionManagementSectionMenuMoveSectionToAnotherPageClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - stage / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementSectionMenuMoveSectionToAnotherPageClickParams>} LogObject<sectionManagementSectionMenuMoveSectionToAnotherPageClickParams>
 */
export function sectionManagementSectionMenuMoveSectionToAnotherPageClick(params) {
    return { evid: 262, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementSectionMovedToANewPageSuccess' event (evid: 263)
 *
 * Event description: Module: sectionManagement. Flow: user chooses the target page of the transfered section.
 *
 * @param {sectionManagementSectionMovedToANewPageSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} originPageId - number of origin page
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} originPosition - new position of section in page
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} position - position of section in page
 * @property {number} loadTime - time in ms
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementSectionMovedToANewPageSuccessParams>} LogObject<sectionManagementSectionMovedToANewPageSuccessParams>
 */
export function sectionManagementSectionMovedToANewPageSuccess(params) {
    return { evid: 263, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementSectionMenuDuplicateSectionClick' event (evid: 264)
 *
 * Event description: Module: sectionManagement. Flow: user chooses to duplicate an existing section.
 *
 * @param {sectionManagementSectionMenuDuplicateSectionClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click / stage
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementSectionMenuDuplicateSectionClickParams>} LogObject<sectionManagementSectionMenuDuplicateSectionClickParams>
 */
export function sectionManagementSectionMenuDuplicateSectionClick(params) {
    return { evid: 264, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementSectionDuplicateSuccess' event (evid: 265)
 *
 * Event description: Module: sectionManagement. Flow: section was duplicated successfully.
 *
 * @param {sectionManagementSectionDuplicateSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementSectionDuplicateSuccessParams>} LogObject<sectionManagementSectionDuplicateSuccessParams>
 */
export function sectionManagementSectionDuplicateSuccess(params) {
    return { evid: 265, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementShowMoreSectionPageLayoutsOnScroll' event (evid: 266)
 *
 * Event description: Module: sectionManagement. Flow: user scrolls down and see more layout/pages designs (add / replace).
 *
 * @param {sectionManagementShowMoreSectionPageLayoutsOnScrollParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {string} context - context of loading more layouts: add section / replace section / add page
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementShowMoreSectionPageLayoutsOnScrollParams>} LogObject<sectionManagementShowMoreSectionPageLayoutsOnScrollParams>
 */
export function sectionManagementShowMoreSectionPageLayoutsOnScroll(params) {
    return { evid: 266, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementSectionPanelClosed' event (evid: 267)
 *
 * Event description: Module: sectionManagement. Flow: user closed the add replace section panel.
 *
 * @param {sectionManagementSectionPanelClosedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementSectionPanelClosedParams>} LogObject<sectionManagementSectionPanelClosedParams>
 */
export function sectionManagementSectionPanelClosed(params) {
    return { evid: 267, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementAddSectionClick' event (evid: 268)
 *
 * Event description: Module: sectionManagement. Flow: user clicks on add section (from top bar / plus / when adding blank page ). .
 *
 * @param {sectionManagementAddSectionClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - how the add section was opened: plus button / top bar / blank page
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementAddSectionClickParams>} LogObject<sectionManagementAddSectionClickParams>
 */
export function sectionManagementAddSectionClick(params) {
    return { evid: 268, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementAddSectionToPageStartDragging' event (evid: 269)
 *
 * Event description: Module: sectionManagement. Flow: user chooses a layout of the new section and start to drag it to the stage. Evid 255 will represent drag-complete.
 *
 * @param {sectionManagementAddSectionToPageStartDraggingParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {number} noOfSuggestions - int number that represents the number of suggested layouts that the user saw
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} placeOfSelected - int number that represents the place of the selected layout (1 for the first,…)
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} selectedLayout - layout id
 * @property {string} place - where the group is placed: recommended / others / expanded list
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} addType - click / drag
 * @return {LogObject<sectionManagementAddSectionToPageStartDraggingParams>} LogObject<sectionManagementAddSectionToPageStartDraggingParams>
 */
export function sectionManagementAddSectionToPageStartDragging(params) {
    return { evid: 269, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSwitchViewFromAnonToUserSuccess' event (evid: 27)
 *
 * Event description: Module: builder. Flow: after user logged in or signed up successfully
 *
 * @param {builderSwitchViewFromAnonToUserSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} userStatus - new / existing
 * @property {string} origin - what triggered the login / sign up
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - **deprecated**
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderSwitchViewFromAnonToUserSuccessParams>} LogObject<builderSwitchViewFromAnonToUserSuccessParams>
 */
export function builderSwitchViewFromAnonToUserSuccess(params) {
    return { evid: 27, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementClickOnMoreActionsMenu' event (evid: 270)
 *
 * Event description: Module: sectionManagement. Flow: User clicks on the "more actions"  small menu on the right of a section
 *
 * @param {sectionManagementClickOnMoreActionsMenuParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} placeOfSelected - not relevant
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} selectedLayout - not relevant
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementClickOnMoreActionsMenuParams>} LogObject<sectionManagementClickOnMoreActionsMenuParams>
 */
export function sectionManagementClickOnMoreActionsMenu(params) {
    return { evid: 270, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementPlanToAddTpaInWelcomeTour' event (evid: 271)
 *
 * Event description: Module: sectionManagement. Flow: sent for each tpa that is about to be added in the welcome tour, based on user selection and CMS
 *
 * @param {sectionManagementPlanToAddTpaInWelcomeTourParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} source - recommended , intro
 * @property {string} appName - the app name
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {string} appId - in case of tpa: app_id
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementPlanToAddTpaInWelcomeTourParams>} LogObject<sectionManagementPlanToAddTpaInWelcomeTourParams>
 */
export function sectionManagementPlanToAddTpaInWelcomeTour(params) {
    return { evid: 271, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementFetchImagesInAddSectionPanel' event (evid: 272)
 *
 * Event description: Module: sectionManagement. Flow: in add section imported images are fetched and inserted into draft sections
 *
 * @param {sectionManagementFetchImagesInAddSectionPanelParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} numOfImages - number of available images
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} place - where the group is placed: recommended / others / expanded list
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionManagementFetchImagesInAddSectionPanelParams>} LogObject<sectionManagementFetchImagesInAddSectionPanelParams>
 */
export function sectionManagementFetchImagesInAddSectionPanel(params) {
    return { evid: 272, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionManagementSeeAllLessSectionsClick' event (evid: 273)
 *
 * Event description: Module: sectionManagement. Flow: when user clicks "See all" or "See less" on Add Section panel
 *
 * @param {sectionManagementSeeAllLessSectionsClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - mobile / desktop
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - see all / see less
 * @return {LogObject<sectionManagementSeeAllLessSectionsClickParams>} LogObject<sectionManagementSeeAllLessSectionsClickParams>
 */
export function sectionManagementSeeAllLessSectionsClick(params) {
    return { evid: 273, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSwitchViewFromAnonToUserSuccessSrc66Evid28' event (evid: 28)
 *
 * Event description: Module: builder. Flow: after user logged in or signed up successfully
 *
 * @param {builderSwitchViewFromAnonToUserSuccessSrc66Evid28Params} params - The parameters for this event
 * @property {string} instanceId - instance_id, represents the story/msid
 * @property {boolean} saveToSanta - **deprecated**
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - **deprecated**
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} msid - metasite id
 * @return {LogObject<builderSwitchViewFromAnonToUserSuccessSrc66Evid28Params>} LogObject<builderSwitchViewFromAnonToUserSuccessSrc66Evid28Params>
 */
export function builderSwitchViewFromAnonToUserSuccessSrc66Evid28(params) {
    return { evid: 28, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarHoverOnUpgrade' event (evid: 283)
 *
 * Event description: Module: builder. Flow: user hovers on Upgrade in Top Bar
 *
 * @param {builderTopBarHoverOnUpgradeParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/MED/headless
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - user device screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarHoverOnUpgradeParams>} LogObject<builderTopBarHoverOnUpgradeParams>
 */
export function builderTopBarHoverOnUpgrade(params) {
    return { evid: 283, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderTopBarBusinessPhoneNumberClick' event (evid: 284)
 *
 * Event description: Module: builder. Flow: user clicks on Business Phone Number
 *
 * @param {builderTopBarBusinessPhoneNumberClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/MED/headless
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - user device screen resolution
 * @property {string} origin - top bar/header/footer
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderTopBarBusinessPhoneNumberClickParams>} LogObject<builderTopBarBusinessPhoneNumberClickParams>
 */
export function builderTopBarBusinessPhoneNumberClick(params) {
    return { evid: 284, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPagePresetPreviewCarouselClick' event (evid: 29)
 *
 * Event description: Module: builder. Flow: user clicks on left / right (arrows) on the fpd page
 *
 * @param {builderPagePresetPreviewCarouselClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} direction - left / right
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {string} mode - builder/welcome
 * @property {number} loadTime - **deprecated**
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPagePresetPreviewCarouselClickParams>} LogObject<builderPagePresetPreviewCarouselClickParams>
 */
export function builderPagePresetPreviewCarouselClick(params) {
    return { evid: 29, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSiteConverterSectionPresetPickerStart' event (evid: 3)
 *
 * Event description: Module: builder. Flow: calculation of presets fit for section has started
 *
 * @param {builderSiteConverterSectionPresetPickerStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {number} index - section index in original json
 * @return {LogObject<builderSiteConverterSectionPresetPickerStartParams>} LogObject<builderSiteConverterSectionPresetPickerStartParams>
 */
export function builderSiteConverterSectionPresetPickerStart(params) {
    return { evid: 3, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'loadingAutomationTestCompleted' event (evid: 30)
 *
 * Event description: Module: builder. Flow: performance test completed.
 *
 * @param {loadingAutomationTestCompletedParams} params - The parameters for this event
 * @property {string} testName - test's name
 * @property {string} adiBuilderType - adi_builder_type
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} msid - metasite id
 * @return {LogObject<loadingAutomationTestCompletedParams>} LogObject<loadingAutomationTestCompletedParams>
 */
export function loadingAutomationTestCompleted(params) {
    return { evid: 30, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignDesignPanelIsLoaded' event (evid: 300)
 *
 * Event description: Module: siteDesign. Flow: user clicks on 'design' and the panel is loaded. also when the user clicks on 'back' from the customize design panel.
 *
 * @param {siteDesignDesignPanelIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - how the panel is loaded: header / back from customize
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignDesignPanelIsLoadedParams>} LogObject<siteDesignDesignPanelIsLoadedParams>
 */
export function siteDesignDesignPanelIsLoaded(params) {
    return { evid: 300, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignClickOnDesiredDesign' event (evid: 301)
 *
 * Event description: Module: siteDesign. Flow: user switches design.
 *
 * @param {siteDesignClickOnDesiredDesignParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} prevDesignId - id of previous design
 * @property {string} structureId - structure id
 * @property {string} targetDesignId - target design id, replacing the 'designId' param in this event
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {number} designNumber - what's the number of the selected design in the list: 1,2,…,
 * @property {string} screenResolution - screen_resolution
 * @property {number} noOfDesigns - how many designs there are
 * @property {number} revision - builder version
 * @property {string} origin - how the panel is loaded: header / back from customize
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignClickOnDesiredDesignParams>} LogObject<siteDesignClickOnDesiredDesignParams>
 */
export function siteDesignClickOnDesiredDesign(params) {
    return { evid: 301, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignDesignChangeSuccess' event (evid: 302)
 *
 * Event description: Module: siteDesign. Flow: design was changed successfully.
 *
 * @param {siteDesignDesignChangeSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} prevDesignId - id of previous design
 * @property {string} structureId - structure id
 * @property {string} targetDesignId - target design id, replacing the 'designId' param in this event
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {number} designNumber - what's the number of the selected design in the list: 1,2,…,
 * @property {string} screenResolution - screen_resolution
 * @property {number} noOfDesigns - how many designs there are
 * @property {number} revision - builder version
 * @property {string} origin - how the panel is loaded: header / back from customize
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignDesignChangeSuccessParams>} LogObject<siteDesignDesignChangeSuccessParams>
 */
export function siteDesignDesignChangeSuccess(params) {
    return { evid: 302, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignPanelIsLoaded' event (evid: 303)
 *
 * Event description: Module: siteDesign. Flow: user clicks on 'customize' in a specific design.
 *
 * @param {siteDesignCustomizeDesignPanelIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignPanelIsLoadedParams>} LogObject<siteDesignCustomizeDesignPanelIsLoadedParams>
 */
export function siteDesignCustomizeDesignPanelIsLoaded(params) {
    return { evid: 303, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignSubPanelIsLoaded' event (evid: 304)
 *
 * Event description: Module: siteDesign. Flow: user opens the animation / font panel.
 *
 * @param {siteDesignCustomizeDesignSubPanelIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance_id
 * @property {string} name - name
 * @property {string} structureId - structure_id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - uuid
 * @property {string} designId - design_id
 * @property {string} industryId - industry_id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} origin - how panel was opened {topbar, design panel}
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - esi
 * @property {string} editMode - edit_mode
 * @property {GUID} msid - msid
 * @return {LogObject<siteDesignCustomizeDesignSubPanelIsLoadedParams>} LogObject<siteDesignCustomizeDesignSubPanelIsLoadedParams>
 */
export function siteDesignCustomizeDesignSubPanelIsLoaded(params) {
    return { evid: 304, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignClickOnDesiredFont' event (evid: 305)
 *
 * Event description: Module: siteDesign. Flow: user chooses a color/font set.
 *
 * @param {siteDesignClickOnDesiredFontParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - sub panel name:  font set
 * @property {string} structureId - structure id
 * @property {number} noOfSets - how many color/font sets there are
 * @property {number} setNumber - what's the number of the selected color/font set in the list: 1,2,…,35
 * @property {string} setId - unique identifier of the color/font set
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignClickOnDesiredFontParams>} LogObject<siteDesignClickOnDesiredFontParams>
 */
export function siteDesignClickOnDesiredFont(params) {
    return { evid: 305, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignFontSetChangeSuccess' event (evid: 306)
 *
 * Event description: Module: siteDesign. Flow: color/font set was updated successfully.
 *
 * @param {siteDesignFontSetChangeSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - sub panel name: font set
 * @property {string} structureId - structure id
 * @property {number} noOfSets - how many color/font sets there are
 * @property {number} setNumber - what's the number of the selected font set in the list: 1,2,…,35
 * @property {string} setId - unique identifier of the font set
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignFontSetChangeSuccessParams>} LogObject<siteDesignFontSetChangeSuccessParams>
 */
export function siteDesignFontSetChangeSuccess(params) {
    return { evid: 306, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignClickOnResetDesign' event (evid: 307)
 *
 * Event description: Module: siteDesign. Flow: user clicks on reset.
 *
 * @param {siteDesignClickOnResetDesignParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {boolean} isAdvanced - Is reset clicked from advanced editing mode or from main panel{True if clicked from advanced}
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignClickOnResetDesignParams>} LogObject<siteDesignClickOnResetDesignParams>
 */
export function siteDesignClickOnResetDesign(params) {
    return { evid: 307, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignResetDesignSuccess' event (evid: 308)
 *
 * Event description: Module: siteDesign. Flow: design was reset successfully.
 *
 * @param {siteDesignResetDesignSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {boolean} isAdvanced - Is reset clicked from advanced editing mode or from main panel{True if clicked from advanced}
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - loading time till this phase
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignResetDesignSuccessParams>} LogObject<siteDesignResetDesignSuccessParams>
 */
export function siteDesignResetDesignSuccess(params) {
    return { evid: 308, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignDesignWindowWasClosed' event (evid: 309)
 *
 * Event description: Module: siteDesign. Flow: user closes the design panels.
 *
 * @param {siteDesignDesignWindowWasClosedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - sub panel name: color set / font set
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignDesignWindowWasClosedParams>} LogObject<siteDesignDesignWindowWasClosedParams>
 */
export function siteDesignDesignWindowWasClosed(params) {
    return { evid: 309, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignOpenColorSubPanel' event (evid: 310)
 *
 * Event description: Module: siteDesign. Flow: user opens the color palette settings
 *
 * @param {siteDesignCustomizeDesignOpenColorSubPanelParams} params - The parameters for this event
 * @property {string} instanceId - instance_id
 * @property {string} structureId - structure_id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - uuid
 * @property {string} designId - design_id
 * @property {string} industryId - industry_id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} origin - how panel was opened {topbar, design panel}
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - esi
 * @property {string} editMode - edit_mode
 * @property {GUID} msid - msid
 * @return {LogObject<siteDesignCustomizeDesignOpenColorSubPanelParams>} LogObject<siteDesignCustomizeDesignOpenColorSubPanelParams>
 */
export function siteDesignCustomizeDesignOpenColorSubPanel(params) {
    return { evid: 310, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignColorPaletteModeChangeColorMainClick' event (evid: 311)
 *
 * Event description: Module: siteDesign. Flow: user chooses to change color
 *
 * @param {siteDesignCustomizeDesignColorPaletteModeChangeColorMainClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / no-tour
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignColorPaletteModeChangeColorMainClickParams>} LogObject<siteDesignCustomizeDesignColorPaletteModeChangeColorMainClickParams>
 */
export function siteDesignCustomizeDesignColorPaletteModeChangeColorMainClick(params) {
    return { evid: 311, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignColorPaletteModeColorSelect' event (evid: 312)
 *
 * Event description: Module: siteDesign. Flow: user chooses to change color
 *
 * @param {siteDesignCustomizeDesignColorPaletteModeColorSelectParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - which panel the color from: regular,color picker, hex
 * @property {string} structureId - structure id
 * @property {boolean} advancedEdit - did the pallete open from advanced settings mode or not
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / no-tour
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignColorPaletteModeColorSelectParams>} LogObject<siteDesignCustomizeDesignColorPaletteModeColorSelectParams>
 */
export function siteDesignCustomizeDesignColorPaletteModeColorSelect(params) {
    return { evid: 312, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignColorPaletteModeAdvancedEditingColorClick' event (evid: 313)
 *
 * Event description: Module: siteDesign. Flow: user chooses the advanced editing button
 *
 * @param {siteDesignCustomizeDesignColorPaletteModeAdvancedEditingColorClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / no-tour
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignColorPaletteModeAdvancedEditingColorClickParams>} LogObject<siteDesignCustomizeDesignColorPaletteModeAdvancedEditingColorClickParams>
 */
export function siteDesignCustomizeDesignColorPaletteModeAdvancedEditingColorClick(params) {
    return { evid: 313, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignClickOnDesiredPaletteFromCustomizePallete' event (evid: 314)
 *
 * Event description: Module: siteDesign. Flow: user chooses a palette in customize pallette panel
 *
 * @param {siteDesignClickOnDesiredPaletteFromCustomizePalleteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} colorationLevel - {plain, tinted,  colorful, vibrant, none}
 * @property {string} structureId - structure id
 * @property {number} noOfSets - The number of palettes to choose from, including Logo, Custom and Recommended Palettes
 * @property {number} setNumber - The index of the chosen palette, where -1 means logo palette, and 0 or above means index in list of custom (if exists) or calculated palettes
 * @property {string} adiBuilderType - adi_builder_type
 * @property {number} num_of_colors - number of colors in the palette
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - welcomeTour / header-panel / clipart-settings-panel / customize-palette-panel
 * @property {string} viewerName - viewer_name
 * @property {string} palleteCreator - server(magic), default,custom,calculated, logo
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignClickOnDesiredPaletteFromCustomizePalleteParams>} LogObject<siteDesignClickOnDesiredPaletteFromCustomizePalleteParams>
 */
export function siteDesignClickOnDesiredPaletteFromCustomizePallete(params) {
    return { evid: 314, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignOpenAnimationListPanel' event (evid: 315)
 *
 * Event description: Module: siteDesign. Flow: user clicks on animations dropdown or opens the animation panel
 *
 * @param {siteDesignCustomizeDesignOpenAnimationListPanelParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignOpenAnimationListPanelParams>} LogObject<siteDesignCustomizeDesignOpenAnimationListPanelParams>
 */
export function siteDesignCustomizeDesignOpenAnimationListPanel(params) {
    return { evid: 315, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignChangeElementAnimationClick' event (evid: 316)
 *
 * Event description: Module: siteDesign. Flow: user clicks on a desired element animation
 *
 * @param {siteDesignCustomizeDesignChangeElementAnimationClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - name of animation: none, fade, float, ...
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignChangeElementAnimationClickParams>} LogObject<siteDesignCustomizeDesignChangeElementAnimationClickParams>
 */
export function siteDesignCustomizeDesignChangeElementAnimationClick(params) {
    return { evid: 316, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignChangeElementAnimationSuccess' event (evid: 317)
 *
 * Event description: Module: siteDesign. Flow: a desired element animation was updated successfully
 *
 * @param {siteDesignCustomizeDesignChangeElementAnimationSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - name of animation: none, fade, float, ...
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms for changing animation
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignChangeElementAnimationSuccessParams>} LogObject<siteDesignCustomizeDesignChangeElementAnimationSuccessParams>
 */
export function siteDesignCustomizeDesignChangeElementAnimationSuccess(params) {
    return { evid: 317, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignChangeDarkLightMode' event (evid: 318)
 *
 * Event description: Module: siteDesign. Flow: user clicks onchange dark/light mode
 *
 * @param {siteDesignCustomizeDesignChangeDarkLightModeParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - dark/light/...
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / no-tour
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignChangeDarkLightModeParams>} LogObject<siteDesignCustomizeDesignChangeDarkLightModeParams>
 */
export function siteDesignCustomizeDesignChangeDarkLightMode(params) {
    return { evid: 318, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignChangeDarkLightModeSuccess' event (evid: 319)
 *
 * Event description: Module: siteDesign. Flow: user clicks onchange dark/light mode
 *
 * @param {siteDesignCustomizeDesignChangeDarkLightModeSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - dark/light/...
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - loading time between click to success
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / no-tour
 * @property {GUID} msid - metasite id
 * @property {number} loading_time - loading time between click to Success
 * @return {LogObject<siteDesignCustomizeDesignChangeDarkLightModeSuccessParams>} LogObject<siteDesignCustomizeDesignChangeDarkLightModeSuccessParams>
 */
export function siteDesignCustomizeDesignChangeDarkLightModeSuccess(params) {
    return { evid: 319, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignColorPaletteModeColorSelectSuccess' event (evid: 320)
 *
 * Event description: Module: siteDesign. Flow: user chooses to change color - Success
 *
 * @param {siteDesignCustomizeDesignColorPaletteModeColorSelectSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - which panel the color from: regular,color picker, hex
 * @property {string} structureId - structure id
 * @property {boolean} advancedEdit - did the pallete open from advanced settings mode or not
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - loading time between click to success
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / no-tour
 * @property {GUID} msid - metasite id
 * @property {number} loading_time - loading time between click to Success
 * @return {LogObject<siteDesignCustomizeDesignColorPaletteModeColorSelectSuccessParams>} LogObject<siteDesignCustomizeDesignColorPaletteModeColorSelectSuccessParams>
 */
export function siteDesignCustomizeDesignColorPaletteModeColorSelectSuccess(params) {
    return { evid: 320, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignColorPaletteModeAdvancedEditingColorClickSuccess' event (evid: 321)
 *
 * Event description: Module: siteDesign. Flow: user chooses the advanced editing button success
 *
 * @param {siteDesignCustomizeDesignColorPaletteModeAdvancedEditingColorClickSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - loading time between click to success
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / no-tour
 * @property {GUID} msid - metasite id
 * @property {number} loading_time - loading time between click to Success
 * @return {LogObject<siteDesignCustomizeDesignColorPaletteModeAdvancedEditingColorClickSuccessParams>} LogObject<siteDesignCustomizeDesignColorPaletteModeAdvancedEditingColorClickSuccessParams>
 */
export function siteDesignCustomizeDesignColorPaletteModeAdvancedEditingColorClickSuccess(params) {
    return { evid: 321, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignClickOnDesiredPaletteFromCustomizePalleteSuccess' event (evid: 322)
 *
 * Event description: Module: siteDesign. Flow: user chooses a palette in customize pallette panel - Success
 *
 * @param {siteDesignClickOnDesiredPaletteFromCustomizePalleteSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} colorationLevel - {plain, tinted,  colorful, vibrant, none}
 * @property {string} structureId - structure id
 * @property {number} noOfSets - The number of palettes to choose from, including Logo, Custom and Recommended Palettes
 * @property {number} setNumber - The index of the chosen palette, where -1 means logo palette, and 0 or above means index in list of custom (if exists) or calculated palettes
 * @property {string} adiBuilderType - adi_builder_type
 * @property {number} num_of_colors - number of colors in the palette
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - welcomeTour  / header-panel / clipart-settings-panel / customize-palette-panel
 * @property {string} viewerName - viewer_name
 * @property {string} palleteCreator - server(magic), default,custom,calculated
 * @property {number} loadTime - loading time between click to success
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {number} loading_time - loading time between click to Success
 * @return {LogObject<siteDesignClickOnDesiredPaletteFromCustomizePalleteSuccessParams>} LogObject<siteDesignClickOnDesiredPaletteFromCustomizePalleteSuccessParams>
 */
export function siteDesignClickOnDesiredPaletteFromCustomizePalleteSuccess(params) {
    return { evid: 322, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignChangeScrollAnimationClick' event (evid: 323)
 *
 * Event description: Module: siteDesign. Flow: user clicks on a desired  scroll animation
 *
 * @param {siteDesignCustomizeDesignChangeScrollAnimationClickParams} params - The parameters for this event
 * @property {string} instanceId - instance_id
 * @property {string} name - name
 * @property {string} structureId - structure_id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - uuid
 * @property {string} designId - design_id
 * @property {string} industryId - industry_id
 * @property {string} screenResolution - screen_resolution
 * @property {string} alias - label (name the user sees) of animation:  none, parallax, reveal ...
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - esi
 * @property {string} editMode - edit_mode
 * @property {GUID} msid - msid
 * @return {LogObject<siteDesignCustomizeDesignChangeScrollAnimationClickParams>} LogObject<siteDesignCustomizeDesignChangeScrollAnimationClickParams>
 */
export function siteDesignCustomizeDesignChangeScrollAnimationClick(params) {
    return { evid: 323, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignChangeScrollAnimationSuccess' event (evid: 324)
 *
 * Event description: Module: siteDesign. Flow: a desired scroll animation was updated successfully
 *
 * @param {siteDesignCustomizeDesignChangeScrollAnimationSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance_id
 * @property {string} name - name
 * @property {string} structureId - structure_id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - uuid
 * @property {string} designId - design_id
 * @property {string} industryId - industry_id
 * @property {string} screenResolution - screen_resolution
 * @property {string} alias - label (name seen by user) of animation:  none, parallax, reveal ...
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - load_time
 * @property {GUID} esi - esi
 * @property {string} editMode - edit_mode
 * @property {GUID} msid - msid
 * @return {LogObject<siteDesignCustomizeDesignChangeScrollAnimationSuccessParams>} LogObject<siteDesignCustomizeDesignChangeScrollAnimationSuccessParams>
 */
export function siteDesignCustomizeDesignChangeScrollAnimationSuccess(params) {
    return { evid: 324, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignAdvancedColorsAreHardToReadDisplayed' event (evid: 325)
 *
 * Event description: Module: siteDesign. Flow: in advanced editing, custom colors that the user chose are hard to read, warning message is displayed
 *
 * @param {siteDesignCustomizeDesignAdvancedColorsAreHardToReadDisplayedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignAdvancedColorsAreHardToReadDisplayedParams>} LogObject<siteDesignCustomizeDesignAdvancedColorsAreHardToReadDisplayedParams>
 */
export function siteDesignCustomizeDesignAdvancedColorsAreHardToReadDisplayed(params) {
    return { evid: 325, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignAdvancedUserClicksFixColors' event (evid: 326)
 *
 * Event description: Module: siteDesign. Flow: in advanced editing, custom colors that the user chose are hard to read, warning message is displayed and user clicks on fix colors
 *
 * @param {siteDesignCustomizeDesignAdvancedUserClicksFixColorsParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignAdvancedUserClicksFixColorsParams>} LogObject<siteDesignCustomizeDesignAdvancedUserClicksFixColorsParams>
 */
export function siteDesignCustomizeDesignAdvancedUserClicksFixColors(params) {
    return { evid: 326, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignAdvancedFixColorsSuccess' event (evid: 327)
 *
 * Event description: Module: siteDesign. Flow: in advanced editing, user clicked on fix colors and changes were made succeessfully
 *
 * @param {siteDesignCustomizeDesignAdvancedFixColorsSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms for fixing colors
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignAdvancedFixColorsSuccessParams>} LogObject<siteDesignCustomizeDesignAdvancedFixColorsSuccessParams>
 */
export function siteDesignCustomizeDesignAdvancedFixColorsSuccess(params) {
    return { evid: 327, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignChangeFontSizeClick' event (evid: 328)
 *
 * Event description: Module: siteDesign. Flow: user changes font size
 *
 * @param {siteDesignChangeFontSizeClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} setId - unique identifier of the font set
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {string} fontSize - font size
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignChangeFontSizeClickParams>} LogObject<siteDesignChangeFontSizeClickParams>
 */
export function siteDesignChangeFontSizeClick(params) {
    return { evid: 328, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignChangeFontSizeSuccess' event (evid: 329)
 *
 * Event description: Module: siteDesign. Flow: user changes font size - success
 *
 * @param {siteDesignChangeFontSizeSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} setId - unique identifier of the font set
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {string} fontSize - font size
 * @property {number} loadTime - time to change size
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignChangeFontSizeSuccessParams>} LogObject<siteDesignChangeFontSizeSuccessParams>
 */
export function siteDesignChangeFontSizeSuccess(params) {
    return { evid: 329, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderFullPageDesignBubbleShow' event (evid: 33)
 *
 * Event description: Module: builder. Flow: when bubble appears in the builder
 *
 * @param {builderFullPageDesignBubbleShowParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - bubble text description
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - -
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} triggerName - fpd - second load / fpd - ugliness image on image
 * @return {LogObject<builderFullPageDesignBubbleShowParams>} LogObject<builderFullPageDesignBubbleShowParams>
 */
export function builderFullPageDesignBubbleShow(params) {
    return { evid: 33, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignOverrideSectionDesignDisplayed' event (evid: 330)
 *
 * Event description: Module: siteDesign. Flow: user changes site design and has already set a section with a different animation/font. message is displayed to apply the new animation/font to all sections
 *
 * @param {siteDesignCustomizeDesignOverrideSectionDesignDisplayedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - font / element animation / scroll animation
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignOverrideSectionDesignDisplayedParams>} LogObject<siteDesignCustomizeDesignOverrideSectionDesignDisplayedParams>
 */
export function siteDesignCustomizeDesignOverrideSectionDesignDisplayed(params) {
    return { evid: 330, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignOverrideSectionDesignClickApply' event (evid: 331)
 *
 * Event description: Module: siteDesign. Flow: user changes site design and has already set a section with a different animation/font. user clicks on apply the new animation/font to all sections
 *
 * @param {siteDesignCustomizeDesignOverrideSectionDesignClickApplyParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - font / element animation / scroll animation
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignOverrideSectionDesignClickApplyParams>} LogObject<siteDesignCustomizeDesignOverrideSectionDesignClickApplyParams>
 */
export function siteDesignCustomizeDesignOverrideSectionDesignClickApply(params) {
    return { evid: 331, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignOverrideSectionDesignApplySuccess' event (evid: 332)
 *
 * Event description: Module: siteDesign. Flow: user changes site design and has already set a section with a different animation/font. user clicks on apply the new animation/font to all sections - success
 *
 * @param {siteDesignCustomizeDesignOverrideSectionDesignApplySuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - font / element animation / scroll animation
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms to apply change
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignOverrideSectionDesignApplySuccessParams>} LogObject<siteDesignCustomizeDesignOverrideSectionDesignApplySuccessParams>
 */
export function siteDesignCustomizeDesignOverrideSectionDesignApplySuccess(params) {
    return { evid: 332, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCreatePaletteFromLogoAlgoStart' event (evid: 333)
 *
 * Event description: Module: siteDesign. Flow: a color palette is created from the logo uploaded by the user. algo runs in background.
 *
 * @param {siteDesignCreatePaletteFromLogoAlgoStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - phase name: load-image / color-extract
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - info-card / welcomeTour / clipart-settings-panel / header-panel
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCreatePaletteFromLogoAlgoStartParams>} LogObject<siteDesignCreatePaletteFromLogoAlgoStartParams>
 */
export function siteDesignCreatePaletteFromLogoAlgoStart(params) {
    return { evid: 333, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCreatePaletteFromLogoAlgoSuccess' event (evid: 334)
 *
 * Event description: Module: siteDesign. Flow: a color palette is created from the logo uploaded by the user. algo runs in background and ends successfully
 *
 * @param {siteDesignCreatePaletteFromLogoAlgoSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - phase name: load-image / color-extract
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {number} num_of_colors - number of colors in the palette
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - info-card / welcomeTour / clipart-settings-panel / header-panel
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - time elapsed from last phase
 * @property {number} loadTime - time elapsed from algo start
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCreatePaletteFromLogoAlgoSuccessParams>} LogObject<siteDesignCreatePaletteFromLogoAlgoSuccessParams>
 */
export function siteDesignCreatePaletteFromLogoAlgoSuccess(params) {
    return { evid: 334, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignChangeColorationLevelClick' event (evid: 335)
 *
 * Event description: Module: siteDesign. Flow: user clicks chooses coloration level in site color panel
 *
 * @param {siteDesignCustomizeDesignChangeColorationLevelClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - plain,colorful,...
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - mobile, desktop
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / no-tour
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignChangeColorationLevelClickParams>} LogObject<siteDesignCustomizeDesignChangeColorationLevelClickParams>
 */
export function siteDesignCustomizeDesignChangeColorationLevelClick(params) {
    return { evid: 335, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignCustomizeDesignChangeColorationLevelSuccess' event (evid: 336)
 *
 * Event description: Module: siteDesign. Flow: user clicks chooses coloration level in site color panel
 *
 * @param {siteDesignCustomizeDesignChangeColorationLevelSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - plain,colorful,...
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - mobile, desktop
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - loading time between click to success
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / no-tour
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignCustomizeDesignChangeColorationLevelSuccessParams>} LogObject<siteDesignCustomizeDesignChangeColorationLevelSuccessParams>
 */
export function siteDesignCustomizeDesignChangeColorationLevelSuccess(params) {
    return { evid: 336, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'siteDesignDesignDropdownListOpenOnHover' event (evid: 337)
 *
 * Event description: Module: siteDesign. Flow: user hovers (for 2 seconds) on the design/site design  button and the dropdown list is open
 *
 * @param {siteDesignDesignDropdownListOpenOnHoverParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<siteDesignDesignDropdownListOpenOnHoverParams>} LogObject<siteDesignDesignDropdownListOpenOnHoverParams>
 */
export function siteDesignDesignDropdownListOpenOnHover(params) {
    return { evid: 337, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderFullPageDesignScroll' event (evid: 34)
 *
 * Event description: Module: builder. Flow: when the user scrolls in full page design
 *
 * @param {builderFullPageDesignScrollParams} params - The parameters for this event
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - -
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {string} viewerName - viewer_name
 * @property {string} mode - builder/welcome
 * @property {number} position - position of the chosen option
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderFullPageDesignScrollParams>} LogObject<builderFullPageDesignScrollParams>
 */
export function builderFullPageDesignScroll(params) {
    return { evid: 34, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'itemsManagerAddItemClick' event (evid: 349)
 *
 * Event description: Module: itemsManager. Flow: user clicks on the add page/link button in the menu / pages panel
 *
 * @param {itemsManagerAddItemClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - how the pages section was opened: top bar / pages panel
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} item - page / menu
 * @return {LogObject<itemsManagerAddItemClickParams>} LogObject<itemsManagerAddItemClickParams>
 */
export function itemsManagerAddItemClick(params) {
    return { evid: 349, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSwitchBuilderTypeMobileDesktopClick' event (evid: 35)
 *
 * Event description: Module: builder. Flow: user switches between mobile and desktop editors click
 *
 * @param {builderSwitchBuilderTypeMobileDesktopClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - where was switched from (topbar, pages panel)
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {string} selectedBuilder - mobile/desktop
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderSwitchBuilderTypeMobileDesktopClickParams>} LogObject<builderSwitchBuilderTypeMobileDesktopClickParams>
 */
export function builderSwitchBuilderTypeMobileDesktopClick(params) {
    return { evid: 35, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'itemManagerPagesMenuPanelIsLoaded' event (evid: 350)
 *
 * Event description: Module: itemManager. Flow: user clicks on the pages/menu button/manage pages and the pages panel is loaded.
 *
 * @param {itemManagerPagesMenuPanelIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - origin of opening the pages panel: top bar / second topbar/ header_menu / site pages / manage pages /footer panel
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} item - menu / pages
 * @return {LogObject<itemManagerPagesMenuPanelIsLoadedParams>} LogObject<itemManagerPagesMenuPanelIsLoadedParams>
 */
export function itemManagerPagesMenuPanelIsLoaded(params) {
    return { evid: 350, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerAddPagePanelIsLoaded' event (evid: 351)
 *
 * Event description: Module: pagesManager. Flow: user clicks on add page button and add page panel is loaded.
 *
 * @param {pagesManagerAddPagePanelIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - top bar / menu link panel
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerAddPagePanelIsLoadedParams>} LogObject<pagesManagerAddPagePanelIsLoadedParams>
 */
export function pagesManagerAddPagePanelIsLoaded(params) {
    return { evid: 351, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerSwitchPageTypeInTheAddPagePanel' event (evid: 352)
 *
 * Event description: Module: pagesManager. Flow: user switches between page types.
 *
 * @param {pagesManagerSwitchPageTypeInTheAddPagePanelParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} place - where the group is placed: recommended / others / expanded list
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerSwitchPageTypeInTheAddPagePanelParams>} LogObject<pagesManagerSwitchPageTypeInTheAddPagePanelParams>
 */
export function pagesManagerSwitchPageTypeInTheAddPagePanel(params) {
    return { evid: 352, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerClickOnDesiredNewPage' event (evid: 353)
 *
 * Event description: Module: pagesManager. Flow: user chooses a layout of the new page.
 *
 * @param {pagesManagerClickOnDesiredNewPageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {boolean} is_columns - flag for columns in selected layout
 * @property {number} noOfSuggestions - int number that represents the number of suggested layouts that the user saw
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} placeOfSelected - int number that represents the place of the selected layout (1 for the first,…)
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - pages panel / link dialog
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} selectedLayout - layout id
 * @property {string} place - where the group is placed: recommended / others / expanded list
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerClickOnDesiredNewPageParams>} LogObject<pagesManagerClickOnDesiredNewPageParams>
 */
export function pagesManagerClickOnDesiredNewPage(params) {
    return { evid: 353, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerAddNewPageSuccess' event (evid: 354)
 *
 * Event description: Module: pagesManager. Flow: user chooses a layout of the new page.
 *
 * @param {pagesManagerAddNewPageSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {boolean} is_columns - flag for columns in selected layout
 * @property {number} noOfSuggestions - int number that represents the number of suggested layouts that the user saw
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} appInstanceId - unique identifier of app (store, bookings, etc.) instance in site
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {number} placeOfSelected - int number that represents the place of the selected layout (1 for the first,…)
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - pages panel / link dialog
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} selectedLayout - layout id
 * @property {number} loadTime - time in ms
 * @property {string} place - where the group is placed: recommended / others / expanded list
 * @property {string} appPageType - some app pages are added automaically by the app: by user / by app
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerAddNewPageSuccessParams>} LogObject<pagesManagerAddNewPageSuccessParams>
 */
export function pagesManagerAddNewPageSuccess(params) {
    return { evid: 354, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerDuplicatePageSuccess' event (evid: 355)
 *
 * Event description: Module: pagesManager. Flow: page duplicated successfully
 *
 * @param {pagesManagerDuplicatePageSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} originPageId - number of origin page
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerDuplicatePageSuccessParams>} LogObject<pagesManagerDuplicatePageSuccessParams>
 */
export function pagesManagerDuplicatePageSuccess(params) {
    return { evid: 355, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'itemsManagerHideShowItem' event (evid: 356)
 *
 * Event description: Module: itemsManager. Flow: user clicks on hide / show  item in menu/pages panel.
 *
 * @param {itemsManagerHideShowItemParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} itemType - page / section / document / external link etc.
 * @property {string} viewerName - viewer_name
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} action - hide/show
 * @property {string} item - menu / pages
 * @return {LogObject<itemsManagerHideShowItemParams>} LogObject<itemsManagerHideShowItemParams>
 */
export function itemsManagerHideShowItem(params) {
    return { evid: 356, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'itemsManagerRenameItem' event (evid: 357)
 *
 * Event description: Module: ItemsManager. Flow: user clicks on rename item in menu/pages panel.
 *
 * @param {itemsManagerRenameItemParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} itemType - page / section / document / external link etc.
 * @property {string} viewerName - viewer_name
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} item - pages / menu
 * @return {LogObject<itemsManagerRenameItemParams>} LogObject<itemsManagerRenameItemParams>
 */
export function itemsManagerRenameItem(params) {
    return { evid: 357, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'itemsManagerSetItemAsSubItemMainItem' event (evid: 358)
 *
 * Event description: Module: itemsManager. Flow: user change an item to be a subitem / main item.
 *
 * @param {itemsManagerSetItemAsSubItemMainItemParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} direction - subpage to main page / main page to subpage
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} itemType - page / section / document / external link etc.
 * @property {string} viewerName - viewer_name
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} item - pages / menu
 * @return {LogObject<itemsManagerSetItemAsSubItemMainItemParams>} LogObject<itemsManagerSetItemAsSubItemMainItemParams>
 */
export function itemsManagerSetItemAsSubItemMainItem(params) {
    return { evid: 358, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerClickOnDeletePage' event (evid: 359)
 *
 * Event description: Module: pagesManager. Flow: user clicks on delete page.
 *
 * @param {pagesManagerClickOnDeletePageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerClickOnDeletePageParams>} LogObject<pagesManagerClickOnDeletePageParams>
 */
export function pagesManagerClickOnDeletePage(params) {
    return { evid: 359, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSwitchBuilderTypeMobileDesktopSuccess' event (evid: 36)
 *
 * Event description: Module: builder. Flow: user switches between mobile and desktop editors success
 *
 * @param {builderSwitchBuilderTypeMobileDesktopSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - where was switched from (topbar, pages panel)
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time it took to load
 * @property {string} selectedBuilder - mobile/desktop
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderSwitchBuilderTypeMobileDesktopSuccessParams>} LogObject<builderSwitchBuilderTypeMobileDesktopSuccessParams>
 */
export function builderSwitchBuilderTypeMobileDesktopSuccess(params) {
    return { evid: 36, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerDeletePageSuccess' event (evid: 360)
 *
 * Event description: Module: pagesManager. Flow: user clicks on delete and the page is deleted.
 *
 * @param {pagesManagerDeletePageSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerDeletePageSuccessParams>} LogObject<pagesManagerDeletePageSuccessParams>
 */
export function pagesManagerDeletePageSuccess(params) {
    return { evid: 360, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'itemManagerClickOnSetPageAsHomepage' event (evid: 361)
 *
 * Event description: Module: ItemManager. Flow: user clicks on 'set as homepage'.
 *
 * @param {itemManagerClickOnSetPageAsHomepageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - pages panel / menu panel
 * @property {string} itemType - page / section / document / external link etc.
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} item - pages / menu
 * @return {LogObject<itemManagerClickOnSetPageAsHomepageParams>} LogObject<itemManagerClickOnSetPageAsHomepageParams>
 */
export function itemManagerClickOnSetPageAsHomepage(params) {
    return { evid: 361, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerSeoPanelIsLoaded' event (evid: 362)
 *
 * Event description: Module: pagesManager. Flow: user clicks on 'page seo' and the seo panel is loaded.
 *
 * @param {pagesManagerSeoPanelIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} viewerName - viewer_name
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerSeoPanelIsLoadedParams>} LogObject<pagesManagerSeoPanelIsLoadedParams>
 */
export function pagesManagerSeoPanelIsLoaded(params) {
    return { evid: 362, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerSeoPanelFieldWasUpdated' event (evid: 363)
 *
 * Event description: Module: pagesManager. Flow: field was updated in the seo page.
 *
 * @param {pagesManagerSeoPanelFieldWasUpdatedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} fieldLength - length of text in the field (when relevant)
 * @property {string} fieldValue - value selected - only for show in seach results - true / false
 * @property {string} fieldName - which field is updated: title,description,keywords,show in page results, page url
 * @property {string} viewerName - viewer_name
 * @property {string} fieldType - text/ boolea button / link
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerSeoPanelFieldWasUpdatedParams>} LogObject<pagesManagerSeoPanelFieldWasUpdatedParams>
 */
export function pagesManagerSeoPanelFieldWasUpdated(params) {
    return { evid: 363, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerTransitionBetweenPagesUpdated' event (evid: 364)
 *
 * Event description: Module: pagesManager. Flow: user changes the transition between pages.
 *
 * @param {pagesManagerTransitionBetweenPagesUpdatedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} transitionType - none / horizontal / vertical / cross fade / out-in
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} prevTransitionType - previous transition type: none / horizontal / vertical / cross fade / out-in
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerTransitionBetweenPagesUpdatedParams>} LogObject<pagesManagerTransitionBetweenPagesUpdatedParams>
 */
export function pagesManagerTransitionBetweenPagesUpdated(params) {
    return { evid: 364, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'itemsManagerReorderItem' event (evid: 365)
 *
 * Event description: Module: itemsManager. Flow: user changes a Item's order in menu / pages
 *
 * @param {itemsManagerReorderItemParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} itemType - page / section / document / external link etc.
 * @property {string} viewerName - viewer_name
 * @property {number} position - new position
 * @property {number} prevPosition - previous position
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} item - pages / menu
 * @return {LogObject<itemsManagerReorderItemParams>} LogObject<itemsManagerReorderItemParams>
 */
export function itemsManagerReorderItem(params) {
    return { evid: 365, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'itemManagerPagesMenuPanelWasClosed' event (evid: 366)
 *
 * Event description: Module: ItemManager. Flow: user closes the pages/menu panel.
 *
 * @param {itemManagerPagesMenuPanelWasClosedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - name of closed window - add page / seo / pages panel
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} item - menu / pages
 * @return {LogObject<itemManagerPagesMenuPanelWasClosedParams>} LogObject<itemManagerPagesMenuPanelWasClosedParams>
 */
export function itemManagerPagesMenuPanelWasClosed(params) {
    return { evid: 366, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerDuplicatePageClick' event (evid: 367)
 *
 * Event description: Module: pagesManager. Flow: user clicks on duplicate page - try to
 *
 * @param {pagesManagerDuplicatePageClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} originPageId - number of origin page
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerDuplicatePageClickParams>} LogObject<pagesManagerDuplicatePageClickParams>
 */
export function pagesManagerDuplicatePageClick(params) {
    return { evid: 367, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerClickOnPage' event (evid: 368)
 *
 * Event description: Module: pagesManager. Flow: user switches between pages in pages panel
 *
 * @param {pagesManagerClickOnPageParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - second topbar / pages panel
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {string} pageName - page name
 * @property {GUID} appId - app_id
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerClickOnPageParams>} LogObject<pagesManagerClickOnPageParams>
 */
export function pagesManagerClickOnPage(params) {
    return { evid: 368, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerClickOnSettings' event (evid: 369)
 *
 * Event description: Module: pagesManager. Flow: user clicks on settings button of page, in pages manager
 *
 * @param {pagesManagerClickOnSettingsParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {string} pageName - page name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerClickOnSettingsParams>} LogObject<pagesManagerClickOnSettingsParams>
 */
export function pagesManagerClickOnSettings(params) {
    return { evid: 369, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSuggestedDomainIsDisplayed' event (evid: 37)
 *
 * Event description: Module: builder. Flow: a suggested domain is displayed in fakebrowser
 *
 * @param {builderSuggestedDomainIsDisplayedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderSuggestedDomainIsDisplayedParams>} LogObject<builderSuggestedDomainIsDisplayedParams>
 */
export function builderSuggestedDomainIsDisplayed(params) {
    return { evid: 37, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerGoToPageSuccess' event (evid: 370)
 *
 * Event description: Module: pagesManager. Flow: user chooses a page to navigate to
 *
 * @param {pagesManagerGoToPageSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - second topbar/ pages panel
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {string} selectedLayout - layout id
 * @property {number} loadTime - time in ms
 * @property {string} pageName - page name
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerGoToPageSuccessParams>} LogObject<pagesManagerGoToPageSuccessParams>
 */
export function pagesManagerGoToPageSuccess(params) {
    return { evid: 370, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerAddTpaPackageSuccess' event (evid: 371)
 *
 * Event description: Module: pagesManager. Flow: all TPA pages were created.
 *
 * @param {pagesManagerAddTpaPackageSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {boolean} is_columns - flag for columns in selected layout
 * @property {string} structureId - structure id
 * @property {string} appInstanceId - unique guid of app instance on site
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - apps panel / section panel /biz manager
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {number} numOfPages - number of additional TPA pages added
 * @property {string} appPageType - **deprecated**
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerAddTpaPackageSuccessParams>} LogObject<pagesManagerAddTpaPackageSuccessParams>
 */
export function pagesManagerAddTpaPackageSuccess(params) {
    return { evid: 371, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerPagesDropdownListOpenOnHover' event (evid: 372)
 *
 * Event description: Module: pagesManager. Flow: user hovers on the pages button and the pages dropdown list is open
 *
 * @param {pagesManagerPagesDropdownListOpenOnHoverParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerPagesDropdownListOpenOnHoverParams>} LogObject<pagesManagerPagesDropdownListOpenOnHoverParams>
 */
export function pagesManagerPagesDropdownListOpenOnHover(params) {
    return { evid: 372, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerRemoveTpaPackageSuccess' event (evid: 373)
 *
 * Event description: Module: apps. Flow: all TPA pages/sections were deleted.
 *
 * @param {pagesManagerRemoveTpaPackageSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {boolean} is_columns - flag for columns in selected layout
 * @property {string} structureId - structure id
 * @property {GUID} appInstanceId - app instance id
 * @property {string} adiBuilderType - desktop / mobile / MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms
 * @property {number} numOfPages - number of additional TPA pages removed
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerRemoveTpaPackageSuccessParams>} LogObject<pagesManagerRemoveTpaPackageSuccessParams>
 */
export function pagesManagerRemoveTpaPackageSuccess(params) {
    return { evid: 373, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerPagePermissionsPanelIsLoaded' event (evid: 376)
 *
 * Event description: Module: pagesManager. Flow: user loaded the Page permissions panel
 *
 * @param {pagesManagerPagePermissionsPanelIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} viewerName - viewer_name
 * @property {string} pageType - TPA / General / Members / Members area - private / Members area -  public
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerPagePermissionsPanelIsLoadedParams>} LogObject<pagesManagerPagePermissionsPanelIsLoadedParams>
 */
export function pagesManagerPagePermissionsPanelIsLoaded(params) {
    return { evid: 376, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'pagesManagerPagePermissionsPanelFieldWasUpdated' event (evid: 377)
 *
 * Event description: Module: pagesManager. Flow: field was updated in the Page permissions  panel
 *
 * @param {pagesManagerPagePermissionsPanelFieldWasUpdatedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED / desktop
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} fieldLength - length of text in the field (when relevant)
 * @property {string} fieldValue - value selected - only for show in seach results - true / false
 * @property {string} fieldName - which field is updated: title,description,keywords,show in page results, page url
 * @property {string} viewerName - viewer_name
 * @property {string} pageType - pageType = {TPA / General / Members / Members area - private / Members area -  public}
 * @property {string} fieldType - text/ boolea button / link
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<pagesManagerPagePermissionsPanelFieldWasUpdatedParams>} LogObject<pagesManagerPagePermissionsPanelFieldWasUpdatedParams>
 */
export function pagesManagerPagePermissionsPanelFieldWasUpdated(params) {
    return { evid: 377, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSuggestedDomainIsDismissed' event (evid: 38)
 *
 * Event description: Module: builder. Flow: a suggested domain is dismissed from fakebrowser
 *
 * @param {builderSuggestedDomainIsDismissedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderSuggestedDomainIsDismissedParams>} LogObject<builderSuggestedDomainIsDismissedParams>
 */
export function builderSuggestedDomainIsDismissed(params) {
    return { evid: 38, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSuggestedDomainApiRequest' event (evid: 39)
 *
 * Event description: Module: builder. Flow: suggest domain API is called
 *
 * @param {builderSuggestedDomainApiRequestParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderSuggestedDomainApiRequestParams>} LogObject<builderSuggestedDomainApiRequestParams>
 */
export function builderSuggestedDomainApiRequest(params) {
    return { evid: 39, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementPhotoStudioWindowIsLoaded' event (evid: 398)
 *
 * Event description: Module: mediaManagement. Flow: photo studio is loaded
 *
 * @param {mediaManagementPhotoStudioWindowIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - -
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / media settings
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementPhotoStudioWindowIsLoadedParams>} LogObject<mediaManagementPhotoStudioWindowIsLoadedParams>
 */
export function mediaManagementPhotoStudioWindowIsLoaded(params) {
    return { evid: 398, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementClosePhotoStudioWindow' event (evid: 399)
 *
 * Event description: Module: mediaManagement. Flow: save/close photo studio window
 *
 * @param {mediaManagementClosePhotoStudioWindowParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - -
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} action - save/cancel
 * @return {LogObject<mediaManagementClosePhotoStudioWindowParams>} LogObject<mediaManagementClosePhotoStudioWindowParams>
 */
export function mediaManagementClosePhotoStudioWindow(params) {
    return { evid: 399, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderBrowserWindowFocus' event (evid: 3997)
 *
 * Event description: Sent: When the user moves to another tab ("suspend") When the user comes back the tab of the ADI ("activate") - only for adi2editor users during preloader (only desktop builder)
 *
 * @param {builderBrowserWindowFocusParams} params - The parameters for this event
 * @property {string} structureId - site structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - site industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} actionName - suspend, activate
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderBrowserWindowFocusParams>} LogObject<builderBrowserWindowFocusParams>
 */
export function builderBrowserWindowFocus(params) {
    return { evid: 3997, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderBrowserTabClose' event (evid: 3998)
 *
 * Event description: Sent when a user closes a tab with open ADI builder
 *
 * @param {builderBrowserTabCloseParams} params - The parameters for this event
 * @property {string} structureId - site structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - site industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderBrowserTabCloseParams>} LogObject<builderBrowserTabCloseParams>
 */
export function builderBrowserTabClose(params) {
    return { evid: 3998, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'goToEditorContinueToProEditorStartForAdi2EditorTest' event (evid: 3999)
 *
 * Event description: Module: builder. Flow: ADI start to transfer a site to Editor (Adi to Editor test)
 *
 * @param {goToEditorContinueToProEditorStartForAdi2EditorTestParams} params - The parameters for this event
 * @property {string} structureId - site structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - site industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - migration flow ondemand or onload
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @return {LogObject<goToEditorContinueToProEditorStartForAdi2EditorTestParams>} LogObject<goToEditorContinueToProEditorStartForAdi2EditorTestParams>
 */
export function goToEditorContinueToProEditorStartForAdi2EditorTest(params) {
    return { evid: 3999, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSiteConverterSectionPresetPickerSuccess' event (evid: 4)
 *
 * Event description: Module: builder. Flow: calculation of presets fit for section has completed successfully
 *
 * @param {builderSiteConverterSectionPresetPickerSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} presetsCalculated - number of presets calculated for this section
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from calculation start
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {number} index - section index in original json
 * @return {LogObject<builderSiteConverterSectionPresetPickerSuccessParams>} LogObject<builderSiteConverterSectionPresetPickerSuccessParams>
 */
export function builderSiteConverterSectionPresetPickerSuccess(params) {
    return { evid: 4, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSuggestedDomainApiResponse' event (evid: 40)
 *
 * Event description: Module: builder. Flow: suggest domain API returned an answer
 *
 * @param {builderSuggestedDomainApiResponseParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} domains - array of returned domains
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderSuggestedDomainApiResponseParams>} LogObject<builderSuggestedDomainApiResponseParams>
 */
export function builderSuggestedDomainApiResponse(params) {
    return { evid: 40, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementReplaceMediaWindowIsLoaded' event (evid: 400)
 *
 * Event description: Module: mediaManagement. Flow: media organizer / media gallery is loaded, for all types of media windows.
 *
 * @param {mediaManagementReplaceMediaWindowIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - single image/image gallery/clipart/shape/bgVideo/extVideo
 * @property {string} structureId - structure id
 * @property {number} numTotal - in case of gallery: number of items in gallery
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - from where the media window was opened: edit panel /gallery organizer
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} galleryType - single item/external video/ gallery organizer
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementReplaceMediaWindowIsLoadedParams>} LogObject<mediaManagementReplaceMediaWindowIsLoadedParams>
 */
export function mediaManagementReplaceMediaWindowIsLoaded(params) {
    return { evid: 400, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'migrationToEditor2NotificationClick' event (evid: 4000)
 *
 * Event description: Migration to editor2 notification - click
 *
 * @param {migrationToEditor2NotificationClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - .
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - .
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - get_the_new_editor / learn_more
 * @return {LogObject<migrationToEditor2NotificationClickParams>} LogObject<migrationToEditor2NotificationClickParams>
 */
export function migrationToEditor2NotificationClick(params) {
    return { evid: 4000, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'migrationToEditor2SiteIsNotEligibleForMigration' event (evid: 4001)
 *
 * Event description: Migration to editor2 notification - site is not eligible for migration
 *
 * @param {migrationToEditor2SiteIsNotEligibleForMigrationParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - .
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} reason - why site is not eligible
 * @property {string} screenResolution - .
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<migrationToEditor2SiteIsNotEligibleForMigrationParams>} LogObject<migrationToEditor2SiteIsNotEligibleForMigrationParams>
 */
export function migrationToEditor2SiteIsNotEligibleForMigration(params) {
    return { evid: 4001, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementCloseMediaWindow' event (evid: 401)
 *
 * Event description: Module: mediaManagement. Flow: user clicks on cancel or X in media organizer, only for the media organizer.
 *
 * @param {mediaManagementCloseMediaWindowParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - single image/image gallery/clipart/shape/bgVideo/extVideo
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - from where the media window was opened: edit panel /gallery organizer
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} galleryType - single item/external video/ gallery organizer
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementCloseMediaWindowParams>} LogObject<mediaManagementCloseMediaWindowParams>
 */
export function mediaManagementCloseMediaWindow(params) {
    return { evid: 401, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementApproveChangesInTheMediaWindow' event (evid: 402)
 *
 * Event description: Module: mediaManagement. Flow: user clicks on apply and approves changes in the media panel (media gallery or media organizer).
 *
 * @param {mediaManagementApproveChangesInTheMediaWindowParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - single image/image gallery/clipart/shape/bgVideo/extVideo
 * @property {string} structureId - structure id
 * @property {number} numTotal - in case of gallery: number of items in gallery
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - from where the media window was opened: edit panel /gallery organizer
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} galleryType - single item/external video/ gallery organizer
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementApproveChangesInTheMediaWindowParams>} LogObject<mediaManagementApproveChangesInTheMediaWindowParams>
 */
export function mediaManagementApproveChangesInTheMediaWindow(params) {
    return { evid: 402, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementGalleryOrganizerClickAddMoreMedia' event (evid: 403)
 *
 * Event description: Module: mediaManagement. Flow: user clicks on add more images in the gallery organizer.
 *
 * @param {mediaManagementGalleryOrganizerClickAddMoreMediaParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementGalleryOrganizerClickAddMoreMediaParams>} LogObject<mediaManagementGalleryOrganizerClickAddMoreMediaParams>
 */
export function mediaManagementGalleryOrganizerClickAddMoreMedia(params) {
    return { evid: 403, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementGalleryOrganizerMediaAddedToOrganizer' event (evid: 404)
 *
 * Event description: Module: mediaManagement. Flow: user returns from media gallery to gallery organizer.
 *
 * @param {mediaManagementGalleryOrganizerMediaAddedToOrganizerParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {number} numTotal - in case of gallery: number of items in gallery
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} numAdded - number of added images
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementGalleryOrganizerMediaAddedToOrganizerParams>} LogObject<mediaManagementGalleryOrganizerMediaAddedToOrganizerParams>
 */
export function mediaManagementGalleryOrganizerMediaAddedToOrganizer(params) {
    return { evid: 404, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementGalleryOrganizerDeleteMedia' event (evid: 405)
 *
 * Event description: Module: mediaManagement. Flow: user deletes images from gallery organizer.
 *
 * @param {mediaManagementGalleryOrganizerDeleteMediaParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {number} numTotal - in case of gallery: number of items in gallery
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementGalleryOrganizerDeleteMediaParams>} LogObject<mediaManagementGalleryOrganizerDeleteMediaParams>
 */
export function mediaManagementGalleryOrganizerDeleteMedia(params) {
    return { evid: 405, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementExternalVideoSearchVideoClick' event (evid: 406)
 *
 * Event description: Module: mediaManagement. Flow: user clicks on the search button in the extVideo gallery.
 *
 * @param {mediaManagementExternalVideoSearchVideoClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - video source: vimeo / youtube
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} text - search term
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementExternalVideoSearchVideoClickParams>} LogObject<mediaManagementExternalVideoSearchVideoClickParams>
 */
export function mediaManagementExternalVideoSearchVideoClick(params) {
    return { evid: 406, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementExternalVideoSearchCompleted' event (evid: 407)
 *
 * Event description: Module: mediaManagement. Flow: user see the returned results.
 *
 * @param {mediaManagementExternalVideoSearchCompletedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - video source: vimeo / youtube
 * @property {number} returnedResults - how many resuslts the user sees, 0 for zero matches
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} text - search term
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementExternalVideoSearchCompletedParams>} LogObject<mediaManagementExternalVideoSearchCompletedParams>
 */
export function mediaManagementExternalVideoSearchCompleted(params) {
    return { evid: 407, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementExternalVideoSwitchSearchSource' event (evid: 408)
 *
 * Event description: Module: mediaManagement. Flow: user switches between vimeo and youtube sources.
 *
 * @param {mediaManagementExternalVideoSwitchSearchSourceParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} source - video source: vimeo / youtube
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementExternalVideoSwitchSearchSourceParams>} LogObject<mediaManagementExternalVideoSwitchSearchSourceParams>
 */
export function mediaManagementExternalVideoSwitchSearchSource(params) {
    return { evid: 408, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementPreviewMediaClick' event (evid: 409)
 *
 * Event description: Module: mediaManagement. Flow: user clicks on the magnifying glass to enlarge an image in the gallery organizer, or clicks on the play button in the external video gallery.
 *
 * @param {mediaManagementPreviewMediaClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - single image/image gallery/clipart/shape/bgVideo/extVideo
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} galleryType - single item/external video/ gallery organizer
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementPreviewMediaClickParams>} LogObject<mediaManagementPreviewMediaClickParams>
 */
export function mediaManagementPreviewMediaClick(params) {
    return { evid: 409, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderMobileMenuBubbleShow' event (evid: 41)
 *
 * Event description: Module: builder. Flow: when user make changes in mobile header and the 'show menu' toggle is off
 *
 * @param {builderMobileMenuBubbleShowParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - -
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderMobileMenuBubbleShowParams>} LogObject<builderMobileMenuBubbleShowParams>
 */
export function builderMobileMenuBubbleShow(params) {
    return { evid: 41, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementPreviewMediaLoaded' event (evid: 410)
 *
 * Event description: Module: mediaManagement. Flow: media is successfully shown (extVideo/ gallery image).
 *
 * @param {mediaManagementPreviewMediaLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - single image/image gallery/clipart/shape/bgVideo/extVideo
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} galleryType - single item/external video/ gallery organizer
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementPreviewMediaLoadedParams>} LogObject<mediaManagementPreviewMediaLoadedParams>
 */
export function mediaManagementPreviewMediaLoaded(params) {
    return { evid: 410, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementMediaSettingsShow' event (evid: 411)
 *
 * Event description: Module: mediaManagement. Flow: user clicks on media settings and settings panel is loaded. Available for clipart, shape, single image, extVideo.
 *
 * @param {mediaManagementMediaSettingsShowParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - single image/image gallery/clipart/shape/bgVideo/extVideo
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} galleryType - single item/external video/ gallery organizer
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementMediaSettingsShowParams>} LogObject<mediaManagementMediaSettingsShowParams>
 */
export function mediaManagementMediaSettingsShow(params) {
    return { evid: 411, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementMediaSettingsFieldWasUpdated' event (evid: 412)
 *
 * Event description: Module: mediaManagement. Flow: user update a field in the media settings (clipart, shape, single image, extVideo or image gallery).
 *
 * @param {mediaManagementMediaSettingsFieldWasUpdatedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - single image/image gallery/clipart/shape/bgVideo/extVideo
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} fieldLength - length of text in the field
 * @property {string} fieldValue - value selected - only for dropdown & toggle fields
 * @property {string} fieldName - which field is updated: media preview,title,description,link,video url, auto play,loop play, video controls
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} fieldType - text/ dropdown/ link / long text / toggle
 * @property {string} galleryType - single item/external video/ gallery organizer
 * @property {GUID} esi - editor session id
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementMediaSettingsFieldWasUpdatedParams>} LogObject<mediaManagementMediaSettingsFieldWasUpdatedParams>
 */
export function mediaManagementMediaSettingsFieldWasUpdated(params) {
    return { evid: 412, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'mediaManagementGalleryOrganizerCloseAddMoreMediaWindow' event (evid: 414)
 *
 * Event description: Module: mediaManagement. Flow: user clicks on cancel or X in the media gallery (aka add more media window)
 *
 * @param {mediaManagementGalleryOrganizerCloseAddMoreMediaWindowParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id
 * @property {string} editMode - manual-tour / auto-tour / no-tour
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<mediaManagementGalleryOrganizerCloseAddMoreMediaWindowParams>} LogObject<mediaManagementGalleryOrganizerCloseAddMoreMediaWindowParams>
 */
export function mediaManagementGalleryOrganizerCloseAddMoreMediaWindow(params) {
    return { evid: 414, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageResolutionShowEnhancementMessage' event (evid: 419)
 *
 * Event description: Module: contentEditor. Flow: Enhancement message is shown in media panel
 *
 * @param {contentEditorImageResolutionShowEnhancementMessageParams} params - The parameters for this event
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {number} upscaleRatioHeight - upscale_ratio_height, values are percentage
 * @property {number} upscaleRatio - ratio between orig size and required size values are percentage
 * @property {string} adiBuilderType - desktop / MED / ...
 * @property {string} industryId - industry id
 * @property {string} originalImageUrl - original image url
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {number} upscaleRatioWidth - upscale_ratio_weight values are percentage
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} action - super res potential: trigger / display
 * @return {LogObject<contentEditorImageResolutionShowEnhancementMessageParams>} LogObject<contentEditorImageResolutionShowEnhancementMessageParams>
 */
export function contentEditorImageResolutionShowEnhancementMessage(params) {
    return { evid: 419, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderMobileMenuShowMenuToggleClick' event (evid: 42)
 *
 * Event description: Module: builder. Flow: when user clicks on 'show menu' toggle
 *
 * @param {builderMobileMenuShowMenuToggleClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / MED / ...
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} actionType - manual / auto
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} action - on / off
 * @return {LogObject<builderMobileMenuShowMenuToggleClickParams>} LogObject<builderMobileMenuShowMenuToggleClickParams>
 */
export function builderMobileMenuShowMenuToggleClick(params) {
    return { evid: 42, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageResolutionImproveDialogIsLoaded' event (evid: 420)
 *
 * Event description: Module: contentEditor. Flow: user opens the Image Resolution Improve dialog when the image resolution is low
 *
 * @param {contentEditorImageResolutionImproveDialogIsLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / MED / ...
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} originalImageUrl - original image url
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorImageResolutionImproveDialogIsLoadedParams>} LogObject<contentEditorImageResolutionImproveDialogIsLoadedParams>
 */
export function contentEditorImageResolutionImproveDialogIsLoaded(params) {
    return { evid: 420, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageResolutionImproveDialogApprove' event (evid: 421)
 *
 * Event description: Module: contentEditor. Flow: user closes the Image Resolution Improve dialog and approves changes
 *
 * @param {contentEditorImageResolutionImproveDialogApproveParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} originalImageSize - w X h
 * @property {string} adiBuilderType - desktop / MED / ...
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} originalImageUrl - url of the image uploaded by the user
 * @property {string} improvedImageUrl - url of the improved image
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} placeholderSize - w X h
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} action - on / off
 * @return {LogObject<contentEditorImageResolutionImproveDialogApproveParams>} LogObject<contentEditorImageResolutionImproveDialogApproveParams>
 */
export function contentEditorImageResolutionImproveDialogApprove(params) {
    return { evid: 421, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageResolutionImproveDialogCancel' event (evid: 422)
 *
 * Event description: Module: contentEditor. Flow: user closes the Image Resolution Improve dialog and does not approve
 *
 * @param {contentEditorImageResolutionImproveDialogCancelParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / MED / ...
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorImageResolutionImproveDialogCancelParams>} LogObject<contentEditorImageResolutionImproveDialogCancelParams>
 */
export function contentEditorImageResolutionImproveDialogCancel(params) {
    return { evid: 422, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageResolutionImproveToggleClick' event (evid: 423)
 *
 * Event description: Module: contentEditor. Flow: user clicks the toggle that improves / reset the image quality
 *
 * @param {contentEditorImageResolutionImproveToggleClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} originalImageSize - w X h
 * @property {string} adiBuilderType - desktop / MED / ...
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} originalImageUrl - url of the image uploaded by the user
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} placeholderSize - w X h
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} action - on / off
 * @return {LogObject<contentEditorImageResolutionImproveToggleClickParams>} LogObject<contentEditorImageResolutionImproveToggleClickParams>
 */
export function contentEditorImageResolutionImproveToggleClick(params) {
    return { evid: 423, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorImageResolutionImproveToggleSuccess' event (evid: 424)
 *
 * Event description: Module: contentEditor. Flow: user clicks the toggle that improves / reset the image quality and then the image changes
 *
 * @param {contentEditorImageResolutionImproveToggleSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} originalImageSize - w X h
 * @property {string} adiBuilderType - desktop / MED / ...
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} originalImageUrl - url of the image uploaded by the user
 * @property {string} improvedImageUrl - url of the improved image
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from start
 * @property {string} placeholderSize - w X h
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @property {string} action - on / off
 * @return {LogObject<contentEditorImageResolutionImproveToggleSuccessParams>} LogObject<contentEditorImageResolutionImproveToggleSuccessParams>
 */
export function contentEditorImageResolutionImproveToggleSuccess(params) {
    return { evid: 424, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorAddSecondaryButton' event (evid: 425)
 *
 * Event description: Module: contentEditor. Flow: user clicks on "Add Secondary Button"
 *
 * @param {contentEditorAddSecondaryButtonParams} params - The parameters for this event
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / MED / ...
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorAddSecondaryButtonParams>} LogObject<contentEditorAddSecondaryButtonParams>
 */
export function contentEditorAddSecondaryButton(params) {
    return { evid: 425, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorDeleteSecondaryButton' event (evid: 426)
 *
 * Event description: Module: contentEditor. Flow: user on delete a secondary button
 *
 * @param {contentEditorDeleteSecondaryButtonParams} params - The parameters for this event
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / MED / ...
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorDeleteSecondaryButtonParams>} LogObject<contentEditorDeleteSecondaryButtonParams>
 */
export function contentEditorDeleteSecondaryButton(params) {
    return { evid: 426, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorUpdateEmbedSettings' event (evid: 427)
 *
 * Event description: Module: contentEditor. Flow: user edits a field inside a embed settings panel
 *
 * @param {contentEditorUpdateEmbedSettingsParams} params - The parameters for this event
 * @property {string} sectionType - embed
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED/ desktop
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - -
 * @property {number} revision - builder version
 * @property {number} fieldLength - length of text in the field
 * @property {string} fieldValue - value selected
 * @property {string} fieldName - type / url / frame height /  frame width /
 * @property {string} origin - panel
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorUpdateEmbedSettingsParams>} LogObject<contentEditorUpdateEmbedSettingsParams>
 */
export function contentEditorUpdateEmbedSettings(params) {
    return { evid: 427, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'contentEditorDataValidationMessageDisplayed' event (evid: 429)
 *
 * Event description: Module: contentEditor. Flow: validation message is shown
 *
 * @param {contentEditorDataValidationMessageDisplayedParams} params - The parameters for this event
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / MED / ...
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} fieldName - field name for whom the messages relevant
 * @property {string} origin - from which screen the validation sent
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} message - messages description
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<contentEditorDataValidationMessageDisplayedParams>} LogObject<contentEditorDataValidationMessageDisplayedParams>
 */
export function contentEditorDataValidationMessageDisplayed(params) {
    return { evid: 429, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introPlatformsScreenInteractive' event (evid: 43)
 *
 * Event description: Module: intro. Flow: user clicked / typed anything in platform screen intro page
 *
 * @param {introPlatformsScreenInteractiveParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - desktop
 * @property {GUID} uuid - user id
 * @property {string} platformName - facebook, youtube, website , google places
 * @property {string} userSubscription - deprecated
 * @property {string} screenResolution - -
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} action - type / click
 * @return {LogObject<introPlatformsScreenInteractiveParams>} LogObject<introPlatformsScreenInteractiveParams>
 */
export function introPlatformsScreenInteractive(params) {
    return { evid: 43, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'appsClickOnManageApp' event (evid: 450)
 *
 * Event description: Module: apps. Flow: user clicks on manage app button, which opens the app backoffice.
 *
 * @param {appsClickOnManageAppParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - section / top-bar...
 * @property {string} appInstanceId - unique identifier of app (store, bookings, etc.) instance in site
 * @property {string} appName - blog, Store, Booking
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} appId - app_id, like: 61F33D50-3002-4882-AE86-D319C1A249AB = blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<appsClickOnManageAppParams>} LogObject<appsClickOnManageAppParams>
 */
export function appsClickOnManageApp(params) {
    return { evid: 450, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderUndoRedoLastChangeFetchFromServer' event (evid: 451)
 *
 * Event description: Module: builder. Flow: user clicks the undo / redo many times and we have to go to server to retrieve the relevant revision.
 * Can't be fired without event 140 before.
 *
 * @param {builderUndoRedoLastChangeFetchFromServerParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - action type: undo/redo
 * @return {LogObject<builderUndoRedoLastChangeFetchFromServerParams>} LogObject<builderUndoRedoLastChangeFetchFromServerParams>
 */
export function builderUndoRedoLastChangeFetchFromServer(params) {
    return { evid: 451, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderUndoRedoSuccessRetrieveFromServer' event (evid: 452)
 *
 * Event description: Module: builder. Flow: undo / redo after many times retrieve revision from the server, this event fired when retrieval has succeeded.
 *
 * @param {builderUndoRedoSuccessRetrieveFromServerParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - action type: undo/redo
 * @return {LogObject<builderUndoRedoSuccessRetrieveFromServerParams>} LogObject<builderUndoRedoSuccessRetrieveFromServerParams>
 */
export function builderUndoRedoSuccessRetrieveFromServer(params) {
    return { evid: 452, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'appsClickOnEditAppTpaSettings' event (evid: 453)
 *
 * Event description: Module: apps. Flow: user clicks on edit app button, which opens the TPA settings.
 *
 * @param {appsClickOnEditAppTpaSettingsParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - section / top-bar...
 * @property {string} appInstanceId - unique identifier of app (store, bookings, etc.) instance in site
 * @property {string} appName - blog, Store, Booking
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - panel / header
 * @property {string} viewerName - viewer_name
 * @property {GUID} appId - app_id, like: 61F33D50-3002-4882-AE86-D319C1A249AB = blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<appsClickOnEditAppTpaSettingsParams>} LogObject<appsClickOnEditAppTpaSettingsParams>
 */
export function appsClickOnEditAppTpaSettings(params) {
    return { evid: 453, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'appsRemoveTpaStart' event (evid: 454)
 *
 * Event description: Module: apps. Flow: users click on remove app
 *
 * @param {appsRemoveTpaStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {GUID} appInstanceId - app instance id
 * @property {string} appName - application name
 * @property {string} adiBuilderType - desktop / mobile / MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {string} appId - application id
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<appsRemoveTpaStartParams>} LogObject<appsRemoveTpaStartParams>
 */
export function appsRemoveTpaStart(params) {
    return { evid: 454, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'appsAppPanelOpen' event (evid: 455)
 *
 * Event description: Module: apps. Flow: user opens the app panel
 *
 * @param {appsAppPanelOpenParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - where the panel was opened from (add app dropdown, manage app dropdown)
 * @property {string} adiBuilderType - mobile / desktop
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<appsAppPanelOpenParams>} LogObject<appsAppPanelOpenParams>
 */
export function appsAppPanelOpen(params) {
    return { evid: 455, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'appsAppPanelClose' event (evid: 456)
 *
 * Event description: Module: apps. Flow: when closing app panel
 *
 * @param {appsAppPanelCloseParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - mobile / desktop
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<appsAppPanelCloseParams>} LogObject<appsAppPanelCloseParams>
 */
export function appsAppPanelClose(params) {
    return { evid: 456, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'appsAddTpaStart' event (evid: 457)
 *
 * Event description: Module: apps. Flow: when TPA package installation starts
 *
 * @param {appsAddTpaStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} appName - application name
 * @property {string} adiBuilderType - desktop / mobile / MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - panel / header / biz manager
 * @property {string} viewerName - viewer_name
 * @property {string} appId - application id
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<appsAddTpaStartParams>} LogObject<appsAddTpaStartParams>
 */
export function appsAddTpaStart(params) {
    return { evid: 457, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'appsAddNewTpaPageStart' event (evid: 458)
 *
 * Event description: Module: apps. Flow: when a new TPA page need to be added to the site
 *
 * @param {appsAddNewTpaPageStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} appInstanceId - unique identifier of app (store, bookings, etc.) instance in site
 * @property {string} adiBuilderType - desktop/mobile....
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - apps panel / sections panel / biz manager
 * @property {string} viewerName - viewer_name
 * @property {string} appPageType - some app pages are added automaically by the app: by user / by app
 * @property {string} appId - in case of tpa: app_id/blog
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node
 * @property {GUID} msid - metasite id
 * @return {LogObject<appsAddNewTpaPageStartParams>} LogObject<appsAddNewTpaPageStartParams>
 */
export function appsAddNewTpaPageStart(params) {
    return { evid: 458, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'appsManageDropdownListOpenOnHover' event (evid: 459)
 *
 * Event description: Module: apps. Flow: user hovers (for 2 seconds) on the manage button and the dropdown list is open
 *
 * @param {appsManageDropdownListOpenOnHoverParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} labelName - in case of topbar -  send the button's label (text)
 * @property {string} adiBuilderType - desktop / mobile
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<appsManageDropdownListOpenOnHoverParams>} LogObject<appsManageDropdownListOpenOnHoverParams>
 */
export function appsManageDropdownListOpenOnHover(params) {
    return { evid: 459, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'appsClickOnAscendBusinessTools' event (evid: 461)
 *
 * Event description: Module: apps. Flow: user clicks on managing ascend business tools
 *
 * @param {appsClickOnAscendBusinessToolsParams} params - The parameters for this event
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - med / desktop
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {number} revision - builder version
 * @property {string} origin - topbar - apps / topbar - manage apps
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<appsClickOnAscendBusinessToolsParams>} LogObject<appsClickOnAscendBusinessToolsParams>
 */
export function appsClickOnAscendBusinessTools(params) {
    return { evid: 461, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'dialogsShow' event (evid: 47)
 *
 * Event description: Module: dialogs. Flow: dialog is shown.
 *
 * @param {dialogsShowParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - name of dialog: delete section / move section to page / delete page / signout / create new site /migrate old blog
 * @property {string} structureId - structure id
 * @property {GUID} appInstanceId - app instance id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - where this dialog opened from
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} appId - application id
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<dialogsShowParams>} LogObject<dialogsShowParams>
 */
export function dialogsShow(params) {
    return { evid: 47, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'dialogsApproveChanges' event (evid: 48)
 *
 * Event description: Module: dialogs. Flow: user approves changes in a dialog.
 *
 * @param {dialogsApproveChangesParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - name of dialog: delete section / move section to page / delete page / signout / create new site
 * @property {string} structureId - structure id
 * @property {GUID} appInstanceId - app instance id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - where this dialog was opened from
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} targetName - in case of mover section to page, what's the target page: pageId / new page
 * @property {string} viewerName - viewer_name
 * @property {GUID} appId - app id
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<dialogsApproveChangesParams>} LogObject<dialogsApproveChangesParams>
 */
export function dialogsApproveChanges(params) {
    return { evid: 48, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'dialogsCancel' event (evid: 49)
 *
 * Event description: Module: dialogs. Flow: user clicks on close dialog (cancel or x).
 *
 * @param {dialogsCancelParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - name of dialog: delete section / move section to page / delete page / signout / create new site/ migrate old blog
 * @property {string} structureId - structure id
 * @property {GUID} appInstanceId - app instance id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - where this dialog was opened from
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} appId - app id
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<dialogsCancelParams>} LogObject<dialogsCancelParams>
 */
export function dialogsCancel(params) {
    return { evid: 49, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderSiteConverterSectionPresetPickerFailed' event (evid: 5)
 *
 * Event description: Module: builder. Flow: calculation of presets fit for section has completed unsuccessfully
 *
 * @param {builderSiteConverterSectionPresetPickerFailedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} presetsCalculated - number of presets calculated for this section
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from calculation start
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {number} index - section index in original json
 * @return {LogObject<builderSiteConverterSectionPresetPickerFailedParams>} LogObject<builderSiteConverterSectionPresetPickerFailedParams>
 */
export function builderSiteConverterSectionPresetPickerFailed(params) {
    return { evid: 5, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourStart' event (evid: 50)
 *
 * Event description: Module: tour. Flow: tour starts .
 *
 * @param {tourStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} assistType - tours / smart-assist
 * @property {string} name - name of tour: welcome / preview / design-entire website / design-fonts&colors / edit section / add section / add page
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourStartParams>} LogObject<tourStartParams>
 */
export function tourStart(params) {
    return { evid: 50, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'loadingFirstTimeCreateStoryGetMetadataCompleted' event (evid: 500)
 *
 * Event description: Module: builder. Flow: TPA height changed (automatically by document services)
 *
 * @param {loadingFirstTimeCreateStoryGetMetadataCompletedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile / headless / MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - **deprecated**
 * @property {number} loadTime - **deprecated**
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {number} numOfTpas - number of TPAS that changed their height
 * @property {GUID} msid - metasite id
 * @return {LogObject<loadingFirstTimeCreateStoryGetMetadataCompletedParams>} LogObject<loadingFirstTimeCreateStoryGetMetadataCompletedParams>
 */
export function loadingFirstTimeCreateStoryGetMetadataCompleted(params) {
    return { evid: 500, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourPhaseDuringTour' event (evid: 51)
 *
 * Event description: Module: tour. Flow: a phase during the tour happened.
 *
 * @param {tourPhaseDuringTourParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} assistType - tours / smart assist
 * @property {string} name - name of tour: welcome / preview / design-entire website / design-fonts&colors / edit section / add section / add page
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design, only for the style dialog phase
 * @property {string} industryId - industry id
 * @property {number} totalPhases - number of total phases in tour
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} phaseName - name of phase
 * @property {number} phaseNumber - number of phase in tour
 * @return {LogObject<tourPhaseDuringTourParams>} LogObject<tourPhaseDuringTourParams>
 */
export function tourPhaseDuringTour(params) {
    return { evid: 51, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourComplete' event (evid: 52)
 *
 * Event description: Module: tour. Flow: tour complete dialog is shown.
 *
 * @param {tourCompleteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} assistType - tours / smart assist
 * @property {string} name - name of tour: welcome / preview / design-entire website / design-fonts&colors / edit section / add section / add page
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourCompleteParams>} LogObject<tourCompleteParams>
 */
export function tourComplete(params) {
    return { evid: 52, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourWhatSNextShowMenu' event (evid: 53)
 *
 * Event description: Module: tour. Flow: Assistant menu is shown
 *
 * @param {tourWhatSNextShowMenuParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - tab opened: tours/smart assist
 * @property {string} structureId - structure id
 * @property {string} state - for smart assist: array of item types, 0 if empty. for tours: num of tours that are done
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - go to quick actions button, click tab from smart assist, assistant main button...
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourWhatSNextShowMenuParams>} LogObject<tourWhatSNextShowMenuParams>
 */
export function tourWhatSNextShowMenu(params) {
    return { evid: 53, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourSkipTour' event (evid: 54)
 *
 * Event description: Module: tour. Flow: user skips a tour.
 *
 * @param {tourSkipTourParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - name of tour
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourSkipTourParams>} LogObject<tourSkipTourParams>
 */
export function tourSkipTour(params) {
    return { evid: 54, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourCloseAssistant' event (evid: 55)
 *
 * Event description: Module: tour. Flow: user closes the assistant.
 *
 * @param {tourCloseAssistantParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - smart-assist,tours
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourCloseAssistantParams>} LogObject<tourCloseAssistantParams>
 */
export function tourCloseAssistant(params) {
    return { evid: 55, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignColorPanelOpened' event (evid: 550)
 *
 * Event description: Module: sectionDesign. Flow: user opens section color panel
 *
 * @param {sectionDesignColorPanelOpenedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - where the panel was opened (background-panel, customize-design)
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignColorPanelOpenedParams>} LogObject<sectionDesignColorPanelOpenedParams>
 */
export function sectionDesignColorPanelOpened(params) {
    return { evid: 550, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeColorClick' event (evid: 551)
 *
 * Event description: Module: sectionDesign. Flow: user changes section color
 *
 * @param {sectionDesignChangeColorClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeColorClickParams>} LogObject<sectionDesignChangeColorClickParams>
 */
export function sectionDesignChangeColorClick(params) {
    return { evid: 551, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeColorSuccess' event (evid: 552)
 *
 * Event description: Module: sectionDesign. Flow: user changes section color - success
 *
 * @param {sectionDesignChangeColorSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms to change color
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeColorSuccessParams>} LogObject<sectionDesignChangeColorSuccessParams>
 */
export function sectionDesignChangeColorSuccess(params) {
    return { evid: 552, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignFontPanelOpened' event (evid: 553)
 *
 * Event description: Module: sectionDesign. Flow: user opens section font panel
 *
 * @param {sectionDesignFontPanelOpenedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignFontPanelOpenedParams>} LogObject<sectionDesignFontPanelOpenedParams>
 */
export function sectionDesignFontPanelOpened(params) {
    return { evid: 553, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeFontSizeClick' event (evid: 554)
 *
 * Event description: Module: sectionDesign. Flow: user changes section font size
 *
 * @param {sectionDesignChangeFontSizeClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} setId - unique identifier of the font set
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - OSI / panel
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} fontSize - font size
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeFontSizeClickParams>} LogObject<sectionDesignChangeFontSizeClickParams>
 */
export function sectionDesignChangeFontSizeClick(params) {
    return { evid: 554, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeFontSizeSuccess' event (evid: 555)
 *
 * Event description: Module: sectionDesign. Flow: user changes section font size - success
 *
 * @param {sectionDesignChangeFontSizeSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} setId - unique identifier of the font set
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} fontSize - font size
 * @property {number} loadTime - time in ms to change size
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeFontSizeSuccessParams>} LogObject<sectionDesignChangeFontSizeSuccessParams>
 */
export function sectionDesignChangeFontSizeSuccess(params) {
    return { evid: 555, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignAnimationPanelOpened' event (evid: 556)
 *
 * Event description: Module: sectionDesign. Flow: user opens section animation panel
 *
 * @param {sectionDesignAnimationPanelOpenedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} animationType - e.g: slideshow
 * @return {LogObject<sectionDesignAnimationPanelOpenedParams>} LogObject<sectionDesignAnimationPanelOpenedParams>
 */
export function sectionDesignAnimationPanelOpened(params) {
    return { evid: 556, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeElementAnimationClick' event (evid: 557)
 *
 * Event description: Module: sectionDesign. Flow: user changes section element animation
 *
 * @param {sectionDesignChangeElementAnimationClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - name of animation
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} animationType - e.g.: slideshow
 * @return {LogObject<sectionDesignChangeElementAnimationClickParams>} LogObject<sectionDesignChangeElementAnimationClickParams>
 */
export function sectionDesignChangeElementAnimationClick(params) {
    return { evid: 557, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeElementAnimationSuccess' event (evid: 558)
 *
 * Event description: Module: sectionDesign. Flow: user changes section element animation - success
 *
 * @param {sectionDesignChangeElementAnimationSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - name of animation
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms for changing animation
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} animationType - e.g.: slideshow
 * @return {LogObject<sectionDesignChangeElementAnimationSuccessParams>} LogObject<sectionDesignChangeElementAnimationSuccessParams>
 */
export function sectionDesignChangeElementAnimationSuccess(params) {
    return { evid: 558, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeScrollAnimationClick' event (evid: 559)
 *
 * Event description: Module: sectionDesign. Flow: user changes section scroll animation
 *
 * @param {sectionDesignChangeScrollAnimationClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - name of animation
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} alias - label (name the user sees) of animation: none, parallax, reveal ...
 * @property {number} revision - builder version
 * @property {string} origin - OSI / right click
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeScrollAnimationClickParams>} LogObject<sectionDesignChangeScrollAnimationClickParams>
 */
export function sectionDesignChangeScrollAnimationClick(params) {
    return { evid: 559, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourQuitTour' event (evid: 56)
 *
 * Event description: Module: tour. Flow: user quits a running tour
 *
 * @param {tourQuitTourParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} assistType - tours / smart assist
 * @property {string} name - name of tour: welcome / preview / design-entire website / design-fonts&colors / edit section / add section / add page
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourQuitTourParams>} LogObject<tourQuitTourParams>
 */
export function tourQuitTour(params) {
    return { evid: 56, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeScrollAnimationSuccess' event (evid: 560)
 *
 * Event description: Module: sectionDesign. Flow: user changes section scroll animation - success
 *
 * @param {sectionDesignChangeScrollAnimationSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - name of animation
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {string} alias - label (name the user sees) of animation: none, parallax, reveal ...
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time in ms for changing animation
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeScrollAnimationSuccessParams>} LogObject<sectionDesignChangeScrollAnimationSuccessParams>
 */
export function sectionDesignChangeScrollAnimationSuccess(params) {
    return { evid: 560, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignBackgroundPanelOpened' event (evid: 561)
 *
 * Event description: Module: sectionDesign. Flow: user opens section background panel
 *
 * @param {sectionDesignBackgroundPanelOpenedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - regular/header/list background
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} origin - how panel was opened {section panel, content panel}
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignBackgroundPanelOpenedParams>} LogObject<sectionDesignBackgroundPanelOpenedParams>
 */
export function sectionDesignBackgroundPanelOpened(params) {
    return { evid: 561, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeBackgroundClick' event (evid: 562)
 *
 * Event description: Module: sectionDesign. Flow: user changes section background
 *
 * @param {sectionDesignChangeBackgroundClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - type of background: image,gallery,color...
 * @property {string} name - regular/header/list background
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} position - position of selected background in background type
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeBackgroundClickParams>} LogObject<sectionDesignChangeBackgroundClickParams>
 */
export function sectionDesignChangeBackgroundClick(params) {
    return { evid: 562, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeBackgroundSuccess' event (evid: 563)
 *
 * Event description: Module: sectionDesign. Flow: user changes section background - success
 *
 * @param {sectionDesignChangeBackgroundSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - type of background: image,gallery,color...
 * @property {string} name - regular/header/list background
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} position - position of selected background in background type
 * @property {number} loadTime - time to change background
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeBackgroundSuccessParams>} LogObject<sectionDesignChangeBackgroundSuccessParams>
 */
export function sectionDesignChangeBackgroundSuccess(params) {
    return { evid: 563, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignShowHideListHeaderBackgroundClick' event (evid: 564)
 *
 * Event description: Module: sectionDesign. Flow: user toggles list section header background
 *
 * @param {sectionDesignShowHideListHeaderBackgroundClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} action - hide/show
 * @return {LogObject<sectionDesignShowHideListHeaderBackgroundClickParams>} LogObject<sectionDesignShowHideListHeaderBackgroundClickParams>
 */
export function sectionDesignShowHideListHeaderBackgroundClick(params) {
    return { evid: 564, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignShowHideListHeaderBackgroundSuccess' event (evid: 565)
 *
 * Event description: Module: sectionDesign. Flow: user toggles list section header background - success
 *
 * @param {sectionDesignShowHideListHeaderBackgroundSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time to change background
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} action - hide/show
 * @return {LogObject<sectionDesignShowHideListHeaderBackgroundSuccessParams>} LogObject<sectionDesignShowHideListHeaderBackgroundSuccessParams>
 */
export function sectionDesignShowHideListHeaderBackgroundSuccess(params) {
    return { evid: 565, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignUploadBackgroundClick' event (evid: 566)
 *
 * Event description: Module: sectionDesign. Flow: user clicks on upload section background
 *
 * @param {sectionDesignUploadBackgroundClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} mediaType - type of background: image,gallery,video...
 * @property {string} name - regular/header/list background
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignUploadBackgroundClickParams>} LogObject<sectionDesignUploadBackgroundClickParams>
 */
export function sectionDesignUploadBackgroundClick(params) {
    return { evid: 566, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignOpenBackgroundImageSettings' event (evid: 567)
 *
 * Event description: Module: sectionDesign. Flow: user clicks on background image settings
 *
 * @param {sectionDesignOpenBackgroundImageSettingsParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - regular/header/list background
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignOpenBackgroundImageSettingsParams>} LogObject<sectionDesignOpenBackgroundImageSettingsParams>
 */
export function sectionDesignOpenBackgroundImageSettings(params) {
    return { evid: 567, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeBackgroundImagePositionClick' event (evid: 568)
 *
 * Event description: Module: sectionDesign. Flow: user changes image position in background image settings
 *
 * @param {sectionDesignChangeBackgroundImagePositionClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - regular/header/list background
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} value - selected position (top/center/bottom_right...)
 * @return {LogObject<sectionDesignChangeBackgroundImagePositionClickParams>} LogObject<sectionDesignChangeBackgroundImagePositionClickParams>
 */
export function sectionDesignChangeBackgroundImagePositionClick(params) {
    return { evid: 568, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeBackgroundImagePositionSuccess' event (evid: 569)
 *
 * Event description: Module: sectionDesign. Flow: user changes image position in background image settings - success
 *
 * @param {sectionDesignChangeBackgroundImagePositionSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - regular/header/list background
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time until success
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} value - selected position (top/center/bottom_right...)
 * @return {LogObject<sectionDesignChangeBackgroundImagePositionSuccessParams>} LogObject<sectionDesignChangeBackgroundImagePositionSuccessParams>
 */
export function sectionDesignChangeBackgroundImagePositionSuccess(params) {
    return { evid: 569, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourFirstStepSelectionForMeOrShowMe' event (evid: 57)
 *
 * Event description: Module: tour. Flow: user starts a tour and chooses between 'show me how' and 'do it for me'.
 *
 * @param {tourFirstStepSelectionForMeOrShowMeParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - name of tour: welcome / preview / design-entire website / design-fonts&colors / edit section / add section / add page
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} button - button clicked: show me how / do it for me
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourFirstStepSelectionForMeOrShowMeParams>} LogObject<tourFirstStepSelectionForMeOrShowMeParams>
 */
export function tourFirstStepSelectionForMeOrShowMe(params) {
    return { evid: 57, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeTextAlignmentClick' event (evid: 570)
 *
 * Event description: Module: sectionDesign. Flow: user changes section text alignment
 *
 * @param {sectionDesignChangeTextAlignmentClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} setId - unique identifier of the font set
 * @property {string} adiBuilderType - mobile or desktop editor
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} textAlign - text alignment (left,right.center)
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} origin - OSI / panel
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeTextAlignmentClickParams>} LogObject<sectionDesignChangeTextAlignmentClickParams>
 */
export function sectionDesignChangeTextAlignmentClick(params) {
    return { evid: 570, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeTextAlignmentSuccess' event (evid: 571)
 *
 * Event description: Module: sectionDesign. Flow: user changes section text alignment - success
 *
 * @param {sectionDesignChangeTextAlignmentSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} setId - unique identifier of the font set
 * @property {string} adiBuilderType - mobile or desktop editor
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} textAlign - text alignment (left,right.center)
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time to complete action
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeTextAlignmentSuccessParams>} LogObject<sectionDesignChangeTextAlignmentSuccessParams>
 */
export function sectionDesignChangeTextAlignmentSuccess(params) {
    return { evid: 571, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeHeaderTransparencyStart' event (evid: 572)
 *
 * Event description: Module: sectionDesign. Flow: homepage header transparency changed (by user or system initiated) - start
 *
 * @param {sectionDesignChangeHeaderTransparencyStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - mobile or desktop editor
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} changeBy - user/system
 * @property {string} pageId - page id
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} value - value set: solid/transparent/freeze
 * @return {LogObject<sectionDesignChangeHeaderTransparencyStartParams>} LogObject<sectionDesignChangeHeaderTransparencyStartParams>
 */
export function sectionDesignChangeHeaderTransparencyStart(params) {
    return { evid: 572, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeHeaderTransparencySuccess' event (evid: 573)
 *
 * Event description: Module: sectionDesign. Flow: homepage header transparency changed (by user or system initiated) - success
 *
 * @param {sectionDesignChangeHeaderTransparencySuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - mobile or desktop editor
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} changeBy - user/system
 * @property {string} pageId - page id
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time for change suucces
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @property {string} value - value set: solid/transparent/freeze
 * @return {LogObject<sectionDesignChangeHeaderTransparencySuccessParams>} LogObject<sectionDesignChangeHeaderTransparencySuccessParams>
 */
export function sectionDesignChangeHeaderTransparencySuccess(params) {
    return { evid: 573, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeTextPositionClick' event (evid: 574)
 *
 * Event description: Module: sectionDesign. Flow: user changes section text position
 *
 * @param {sectionDesignChangeTextPositionClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} textPosition - text position: top_left,bottom_right,mid_right...
 * @property {string} setId - unique identifier of the font set
 * @property {string} adiBuilderType - mobile or desktop editor
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeTextPositionClickParams>} LogObject<sectionDesignChangeTextPositionClickParams>
 */
export function sectionDesignChangeTextPositionClick(params) {
    return { evid: 574, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeTextPositionSuccess' event (evid: 575)
 *
 * Event description: Module: sectionDesign. Flow: user changes section text position
 *
 * @param {sectionDesignChangeTextPositionSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} textPosition - text position: top_left,bottom_right,mid_right...
 * @property {string} setId - unique identifier of the font set
 * @property {string} adiBuilderType - mobile or desktop editor
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeTextPositionSuccessParams>} LogObject<sectionDesignChangeTextPositionSuccessParams>
 */
export function sectionDesignChangeTextPositionSuccess(params) {
    return { evid: 575, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignSectionHeightButtonsAreShown' event (evid: 576)
 *
 * Event description: Module: sectionDesign. Flow: user clicks on section height on section customize design dropdown list
 *
 * @param {sectionDesignSectionHeightButtonsAreShownParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignSectionHeightButtonsAreShownParams>} LogObject<sectionDesignSectionHeightButtonsAreShownParams>
 */
export function sectionDesignSectionHeightButtonsAreShown(params) {
    return { evid: 576, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignSectionHeightButtonsDisappeared' event (evid: 577)
 *
 * Event description: Module: sectionDesign. Flow: section height buttons disappears (when user clicks on X)
 *
 * @param {sectionDesignSectionHeightButtonsDisappearedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignSectionHeightButtonsDisappearedParams>} LogObject<sectionDesignSectionHeightButtonsDisappearedParams>
 */
export function sectionDesignSectionHeightButtonsDisappeared(params) {
    return { evid: 577, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignSectionHeightChangeClick' event (evid: 578)
 *
 * Event description: Module: sectionDesign. Flow: user changes section height using section height buttons
 *
 * @param {sectionDesignSectionHeightChangeClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {number} sectionHeight - height of the section after action
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} changeHeightAction - longer / shorter
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignSectionHeightChangeClickParams>} LogObject<sectionDesignSectionHeightChangeClickParams>
 */
export function sectionDesignSectionHeightChangeClick(params) {
    return { evid: 578, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignSectionHeightChangeSuccess' event (evid: 579)
 *
 * Event description: Module: sectionDesign. Flow: section height changed
 *
 * @param {sectionDesignSectionHeightChangeSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {number} sectionHeight - height of the section after action
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} changeHeightAction - longer / shorter
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignSectionHeightChangeSuccessParams>} LogObject<sectionDesignSectionHeightChangeSuccessParams>
 */
export function sectionDesignSectionHeightChangeSuccess(params) {
    return { evid: 579, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourLastStepSelectionKeepOrTryAnother' event (evid: 58)
 *
 * Event description: Module: tour. Flow: user finishes a tour in "do it for me" mode.
 *
 * @param {tourLastStepSelectionKeepOrTryAnotherParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - name of tour: welcome / preview / design-entire website / design-fonts&colors / edit section / add section / add page
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} button - button clicked: show me how / do it for me
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourLastStepSelectionKeepOrTryAnotherParams>} LogObject<tourLastStepSelectionKeepOrTryAnotherParams>
 */
export function tourLastStepSelectionKeepOrTryAnother(params) {
    return { evid: 58, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeSlideshowDurationStart' event (evid: 580)
 *
 * Event description: Module: sectionDesign. Flow: user changes the slide show duration
 *
 * @param {sectionDesignChangeSlideshowDurationStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - slidshow duration. e.g 3sec
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile/ MED....
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeSlideshowDurationStartParams>} LogObject<sectionDesignChangeSlideshowDurationStartParams>
 */
export function sectionDesignChangeSlideshowDurationStart(params) {
    return { evid: 580, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignChangeSlideshowDurationSuccess' event (evid: 581)
 *
 * Event description: Module: sectionDesign. Flow: user changes the slide show duration
 *
 * @param {sectionDesignChangeSlideshowDurationSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - slidshow duration. e.g 3sec
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile/ MED....
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from start
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignChangeSlideshowDurationSuccessParams>} LogObject<sectionDesignChangeSlideshowDurationSuccessParams>
 */
export function sectionDesignChangeSlideshowDurationSuccess(params) {
    return { evid: 581, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignFocalPointPanelOpen' event (evid: 582)
 *
 * Event description: Module: sectionDesign. Flow: user clicks on "position" in media settings panel and opens the Image Position panel
 *
 * @param {sectionDesignFocalPointPanelOpenParams} params - The parameters for this event
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - mobile or desktop editor
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignFocalPointPanelOpenParams>} LogObject<sectionDesignFocalPointPanelOpenParams>
 */
export function sectionDesignFocalPointPanelOpen(params) {
    return { evid: 582, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'sectionDesignFocalPointPanelClick' event (evid: 583)
 *
 * Event description: Module: sectionDesign. Flow: user clicks on a button in focal point panel
 *
 * @param {sectionDesignFocalPointPanelClickParams} params - The parameters for this event
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - mobile or desktop editor
 * @property {string} designId - id of the design
 * @property {string} industryId - industry id
 * @property {boolean} changed - true / false
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {string} navButton - done / cancel
 * @property {GUID} msid - metasite id
 * @return {LogObject<sectionDesignFocalPointPanelClickParams>} LogObject<sectionDesignFocalPointPanelClickParams>
 */
export function sectionDesignFocalPointPanelClick(params) {
    return { evid: 583, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourTimeout' event (evid: 59)
 *
 * Event description: Module: tour. Flow: event sent when session timed out for the user during a tour.
 *
 * @param {tourTimeoutParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {number} lastPhase - last phase of the welcome tour after which timeout occured
 * @property {string} name - name of tour: welcome / preview / design-entire website / design-fonts&colors / edit section / add section / add page
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {number} secondsSinceLastPhase - how many seconds passed since last phase of the tour
 * @property {string} button - button clicked: show me how / do it for me
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourTimeoutParams>} LogObject<tourTimeoutParams>
 */
export function tourTimeout(params) {
    return { evid: 59, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introSiteConverterSiteJsonParsingStart' event (evid: 6)
 *
 * Event description: Module: intro. Flow: JSON is returned from WixWiz and parsing process starts
 *
 * @param {introSiteConverterSiteJsonParsingStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<introSiteConverterSiteJsonParsingStartParams>} LogObject<introSiteConverterSiteJsonParsingStartParams>
 */
export function introSiteConverterSiteJsonParsingStart(params) {
    return { evid: 6, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'notificationsNotificationsBadgeShown' event (evid: 60)
 *
 * Event description: Module: notifications. Flow: notifications badge shown on notification center.
 *
 * @param {notificationsNotificationsBadgeShownParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - deprecated
 * @property {string} structureId - structure id
 * @property {string} state - array of notification types
 * @property {string} adiBuilderType
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<notificationsNotificationsBadgeShownParams>} LogObject<notificationsNotificationsBadgeShownParams>
 */
export function notificationsNotificationsBadgeShown(params) {
    return { evid: 60, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introReCaptchaImNotARobotResult' event (evid: 600)
 *
 * Event description: Module: intro. Flow: user clicked "I'm not a robot" on reCaptcha intro page
 *
 * @param {introReCaptchaImNotARobotResultParams} params - The parameters for this event
 * @property {string} adiBuilderType - adi_builder_type
 * @property {string} result - success, expired, error
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<introReCaptchaImNotARobotResultParams>} LogObject<introReCaptchaImNotARobotResultParams>
 */
export function introReCaptchaImNotARobotResult(params) {
    return { evid: 600, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introReCaptchaPageNavigationClick' event (evid: 601)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button on the reCaptcha page (anon flow)
 *
 * @param {introReCaptchaPageNavigationClickParams} params - The parameters for this event
 * @property {string} adiBuilderType - adi_builder_type
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @return {LogObject<introReCaptchaPageNavigationClickParams>} LogObject<introReCaptchaPageNavigationClickParams>
 */
export function introReCaptchaPageNavigationClick(params) {
    return { evid: 601, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'caasDefaultContentFallback' event (evid: 602)
 *
 * Event description: Module: caas. Flow: when content manager fails to load user selected category / default category
 *
 * @param {caasDefaultContentFallbackParams} params - The parameters for this event
 * @property {string} structureId - structure ID
 * @property {string} adiBuilderType - -
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} language - content language
 * @property {string} viewerName - viewer_name
 * @return {LogObject<caasDefaultContentFallbackParams>} LogObject<caasDefaultContentFallbackParams>
 */
export function caasDefaultContentFallback(params) {
    return { evid: 602, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'upgradeBannerIsDisplayed' event (evid: 61)
 *
 * Event description: Module: dialogs. Flow: A banner is displayed in the builder
 *
 * @param {upgradeBannerIsDisplayedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - name of banner e.g upgrade-default, upgrade-sale
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - adi_builder_type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} editMode - automatic / show me how / manual
 * @property {GUID} msid - metasite id
 * @return {LogObject<upgradeBannerIsDisplayedParams>} LogObject<upgradeBannerIsDisplayedParams>
 */
export function upgradeBannerIsDisplayed(params) {
    return { evid: 61, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourAssistantVideoModalLoaded' event (evid: 63)
 *
 * Event description: Module: tour. Flow: user opened the intro video
 *
 * @param {tourAssistantVideoModalLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop, mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} origin - assistant
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourAssistantVideoModalLoadedParams>} LogObject<tourAssistantVideoModalLoadedParams>
 */
export function tourAssistantVideoModalLoaded(params) {
    return { evid: 63, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourAssistantVideoModalClose' event (evid: 64)
 *
 * Event description: Module: tour. Flow: user closes the assistant video modal
 *
 * @param {tourAssistantVideoModalCloseParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop, mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourAssistantVideoModalCloseParams>} LogObject<tourAssistantVideoModalCloseParams>
 */
export function tourAssistantVideoModalClose(params) {
    return { evid: 64, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourSmartAssistCardMute' event (evid: 65)
 *
 * Event description: Module: tour. Flow: user mutes a smart assist card
 *
 * @param {tourSmartAssistCardMuteParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - name of card
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - mobile / desktop
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourSmartAssistCardMuteParams>} LogObject<tourSmartAssistCardMuteParams>
 */
export function tourSmartAssistCardMute(params) {
    return { evid: 65, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourWelcomeTourKitSelection' event (evid: 66)
 *
 * Event description: Module: tour. Flow: when user choose a design kit in welcome tour
 *
 * @param {tourWelcomeTourKitSelectionParams} params - The parameters for this event
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - -
 * @property {GUID} uuid - user id
 * @property {string} designId - id of the design, only for the style dialog phase
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} position - kit's position
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourWelcomeTourKitSelectionParams>} LogObject<tourWelcomeTourKitSelectionParams>
 */
export function tourWelcomeTourKitSelection(params) {
    return { evid: 66, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderStickySocialBarAddRemoveClick' event (evid: 67)
 *
 * Event description: Module: Builder. Flow: user add/remove Sticky social bar
 *
 * @param {builderStickySocialBarAddRemoveClickParams} params - The parameters for this event
 * @property {string} adiBuilderType - MED / desktop
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - -
 * @property {string} origin - panel / intro / osi
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} action - add / remove
 * @return {LogObject<builderStickySocialBarAddRemoveClickParams>} LogObject<builderStickySocialBarAddRemoveClickParams>
 */
export function builderStickySocialBarAddRemoveClick(params) {
    return { evid: 67, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderStickySocialBarAddRemoveSuccess' event (evid: 68)
 *
 * Event description: Module: Builder. Flow: user succeeded  to add/remove the Sticky social bar
 *
 * @param {builderStickySocialBarAddRemoveSuccessParams} params - The parameters for this event
 * @property {string} adiBuilderType - MED / desktop
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - -
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} action - add / remove
 * @return {LogObject<builderStickySocialBarAddRemoveSuccessParams>} LogObject<builderStickySocialBarAddRemoveSuccessParams>
 */
export function builderStickySocialBarAddRemoveSuccess(params) {
    return { evid: 68, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introSiteConverterSiteJsonParsingSuccess' event (evid: 7)
 *
 * Event description: Module: intro. Flow: parsing process of JSON (imported from WixWiz) complete successfully
 *
 * @param {introSiteConverterSiteJsonParsingSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {number} sectionsLeft - number of sections left after parsing
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} imagesOrig - number of images in the original json
 * @property {number} sectionsOrig - number of sections in the original json
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from process start
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {number} imagesLeft - number of images left after parsing
 * @return {LogObject<introSiteConverterSiteJsonParsingSuccessParams>} LogObject<introSiteConverterSiteJsonParsingSuccessParams>
 */
export function introSiteConverterSiteJsonParsingSuccess(params) {
    return { evid: 7, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderCsArticleSearchApiRequest' event (evid: 70)
 *
 * Event description: Module: builder. Flow: CS article search API call is made
 *
 * @param {builderCsArticleSearchApiRequestParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} text - searched text
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderCsArticleSearchApiRequestParams>} LogObject<builderCsArticleSearchApiRequestParams>
 */
export function builderCsArticleSearchApiRequest(params) {
    return { evid: 70, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderCsArticleSearchApiResponse' event (evid: 71)
 *
 * Event description: Module: builder. Flow: CS article search API returned an answer
 *
 * @param {builderCsArticleSearchApiResponseParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} articles - array of returned articles
 * @property {string} industryId - industry id
 * @property {string} text - searched text
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} tours - array of returned tours
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderCsArticleSearchApiResponseParams>} LogObject<builderCsArticleSearchApiResponseParams>
 */
export function builderCsArticleSearchApiResponse(params) {
    return { evid: 71, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderClickOnAssistantItem' event (evid: 72)
 *
 * Event description: Module: builder . Flow: user clicks on an item in the assia
 *
 * @param {builderClickOnAssistantItemParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - article id/tour name
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} text - searched text if exists
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} origin - tours list/ main panel
 * @property {string} viewerName - viewer_name
 * @property {string} type - article/tour
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderClickOnAssistantItemParams>} LogObject<builderClickOnAssistantItemParams>
 */
export function builderClickOnAssistantItem(params) {
    return { evid: 72, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderClickOnShowMoreLessToursAfterSearching' event (evid: 73)
 *
 * Event description: Module: builder . Flow: user clicks on show more/less tours after searching and getting tours as a result.
 * expands/collapses tours list if returned more than 4
 *
 * @param {builderClickOnShowMoreLessToursAfterSearchingParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} text - searched text if exists
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - show more/show less
 * @return {LogObject<builderClickOnShowMoreLessToursAfterSearchingParams>} LogObject<builderClickOnShowMoreLessToursAfterSearchingParams>
 */
export function builderClickOnShowMoreLessToursAfterSearching(params) {
    return { evid: 73, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourWelcomeTourAddPagesPageView' event (evid: 74)
 *
 * Event description: Module: tour. Flow: when user choose a design kit in welcome tour
 *
 * @param {tourWelcomeTourAddPagesPageViewParams} params - The parameters for this event
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - dekstop / MED / headless
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<tourWelcomeTourAddPagesPageViewParams>} LogObject<tourWelcomeTourAddPagesPageViewParams>
 */
export function tourWelcomeTourAddPagesPageView(params) {
    return { evid: 74, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourWelcomeTourAddPagesInteractions' event (evid: 75)
 *
 * Event description: Module: tour. Flow: when user choose a design kit in welcome tour
 *
 * @param {tourWelcomeTourAddPagesInteractionsParams} params - The parameters for this event
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - dekstop / MED / headless
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {string} viewerName - viewer_name
 * @property {string} pageName - about / contact / blog ...
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - add / remove
 * @return {LogObject<tourWelcomeTourAddPagesInteractionsParams>} LogObject<tourWelcomeTourAddPagesInteractionsParams>
 */
export function tourWelcomeTourAddPagesInteractions(params) {
    return { evid: 75, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'tourWelcomeTourAddPagesNavigation' event (evid: 76)
 *
 * Event description: Module: tour. Flow: when user choose a design kit in welcome tour
 *
 * @param {tourWelcomeTourAddPagesNavigationParams} params - The parameters for this event
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - dekstop / MED / headless
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - -
 * @property {string} selection - pages and their final state separated by comma: contact=true,about=false etc.
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - skip / next
 * @return {LogObject<tourWelcomeTourAddPagesNavigationParams>} LogObject<tourWelcomeTourAddPagesNavigationParams>
 */
export function tourWelcomeTourAddPagesNavigation(params) {
    return { evid: 76, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderPublishTry' event (evid: 78)
 *
 * Event description: Module: builder. Flow: publish started
 *
 * @param {builderPublishTryParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {boolean} isFirst - is first publish
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderPublishTryParams>} LogObject<builderPublishTryParams>
 */
export function builderPublishTry(params) {
    return { evid: 78, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderAddPagesScreenStartsLoading' event (evid: 79)
 *
 * Event description: Module: tour. Flow: add pages screen is starts loading in welcome tour
 *
 * @param {builderAddPagesScreenStartsLoadingParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - user device screen resolution
 * @property {number} pageCount - count of pages suggested
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderAddPagesScreenStartsLoadingParams>} LogObject<builderAddPagesScreenStartsLoadingParams>
 */
export function builderAddPagesScreenStartsLoading(params) {
    return { evid: 79, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introSiteConverterSiteJsonParsingFailed' event (evid: 8)
 *
 * Event description: Module: intro. Flow: parsing process of JSON (imported from WixWiz) has failed
 *
 * @param {introSiteConverterSiteJsonParsingFailedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {number} sectionsLeft - number of sections left after parsing
 * @property {string} adiBuilderType - desktop / mobile
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} imagesOrig - number of images in the original json
 * @property {number} sectionsOrig - number of sections in the original json
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from process start
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {number} imagesLeft - number of images left after parsing
 * @return {LogObject<introSiteConverterSiteJsonParsingFailedParams>} LogObject<introSiteConverterSiteJsonParsingFailedParams>
 */
export function introSiteConverterSiteJsonParsingFailed(params) {
    return { evid: 8, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderAddPagesScreenLoadingSuccess' event (evid: 80)
 *
 * Event description: Module: tour. Flow: add pages screen is successfully loaded in welcome tour
 *
 * @param {builderAddPagesScreenLoadingSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - desktop/MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - user device screen resolution
 * @property {number} pageCount - count of pages suggested
 * @property {string} viewerName - viewer_name
 * @property {number} intervalLoadTime - time (in ms) elapsed since loading started
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<builderAddPagesScreenLoadingSuccessParams>} LogObject<builderAddPagesScreenLoadingSuccessParams>
 */
export function builderAddPagesScreenLoadingSuccess(params) {
    return { evid: 80, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'newContentIsShownAdiToEditorTest' event (evid: 81)
 *
 * Event description: The new content for ADI to Editor test is shown.
 *
 * @param {newContentIsShownAdiToEditorTestParams} params - The parameters for this event
 * @property {string} adiBuilderType - adi_builder_type
 * @property {string} selectedStructureId - structure id
 * @property {string} screenResolution - screen_resolution
 * @property {string} viewerName - viewer_name
 * @property {string} selectedIndustryId - industry id
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<newContentIsShownAdiToEditorTestParams>} LogObject<newContentIsShownAdiToEditorTestParams>
 */
export function newContentIsShownAdiToEditorTest(params) {
    return { evid: 81, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medHeadlessWrapperRequestTry' event (evid: 810)
 *
 * Event description: Module: MED. Flow: Headless Wrapper request (Warmup, Update, Build, Publish) sent
 *
 * @param {medHeadlessWrapperRequestTryParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - warmup/update/build/publish
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - headless
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder revision
 * @property {string} viewerName - viewer_name
 * @property {number} updateRevision - update's revision (relevant for name=update only)
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} action - update's actions (relevant for name=update only)
 * @return {LogObject<medHeadlessWrapperRequestTryParams>} LogObject<medHeadlessWrapperRequestTryParams>
 */
export function medHeadlessWrapperRequestTry(params) {
    return { evid: 810, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medHeadlessWrapperRequestSuccess' event (evid: 811)
 *
 * Event description: Module: MED. Flow: Headless Wrapper request (Warmup, Update, Build, Publish) returns successfully
 *
 * @param {medHeadlessWrapperRequestSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - warmup/update/build/publish
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - headless
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed (in ms) from trial
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<medHeadlessWrapperRequestSuccessParams>} LogObject<medHeadlessWrapperRequestSuccessParams>
 */
export function medHeadlessWrapperRequestSuccess(params) {
    return { evid: 811, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medHeadlessWrapperRequestFailed' event (evid: 812)
 *
 * Event description: Module: MED. Flow: Headless Wrapper request (Warmup, Update, Build, Publish) failed
 *
 * @param {medHeadlessWrapperRequestFailedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} name - warmup/update/build/publish
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - headless
 * @property {string} industryId - industry id
 * @property {string} reason - reason for failure
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed (in ms) from trial
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<medHeadlessWrapperRequestFailedParams>} LogObject<medHeadlessWrapperRequestFailedParams>
 */
export function medHeadlessWrapperRequestFailed(params) {
    return { evid: 812, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderPanelLoaded' event (evid: 820)
 *
 * Event description: Module: MED. user opens a panel
 *
 * @param {medBuilderPanelLoadedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - pages / add page / design / more / add section
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - how the panel was opened - main menu, other panel, button
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editing session
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderPanelLoadedParams>} LogObject<medBuilderPanelLoadedParams>
 */
export function medBuilderPanelLoaded(params) {
    return { evid: 820, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderPanelCancel' event (evid: 821)
 *
 * Event description: Module: MED. user clicks close
 *
 * @param {medBuilderPanelCancelParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} name - pages / add page / design / more / add section
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {string} pageId - number of page
 * @property {number} revision - builder version
 * @property {string} origin - where the panel was loaded from
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editing session
 * @property {string} nodeId - unique number per node, in case this is a list - this is the sectionNodeId
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderPanelCancelParams>} LogObject<medBuilderPanelCancelParams>
 */
export function medBuilderPanelCancel(params) {
    return { evid: 821, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderAddPagesStart' event (evid: 822)
 *
 * Event description: Module: MED. Flow: user clicks on done on add pages panel
 *
 * @param {medBuilderAddPagesStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} pageNames - page chosen (comma separated)
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} numOfPages - number of pages
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderAddPagesStartParams>} LogObject<medBuilderAddPagesStartParams>
 */
export function medBuilderAddPagesStart(params) {
    return { evid: 822, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderAddPagesSuccess' event (evid: 823)
 *
 * Event description: Module: MED. Flow: all pages were successfully added
 *
 * @param {medBuilderAddPagesSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} pageNames - page chosen (comma separated)
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from Add Pages Start
 * @property {number} numOfPages - number of pages
 * @property {GUID} esi - editor session id, for every site editing session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderAddPagesSuccessParams>} LogObject<medBuilderAddPagesSuccessParams>
 */
export function medBuilderAddPagesSuccess(params) {
    return { evid: 823, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderAddSectionsStart' event (evid: 824)
 *
 * Event description: Module: MED. Flow: user clicks done on add sections panel
 *
 * @param {medBuilderAddSectionsStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {number} numOfSections - number of pages
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderAddSectionsStartParams>} LogObject<medBuilderAddSectionsStartParams>
 */
export function medBuilderAddSectionsStart(params) {
    return { evid: 824, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderAddSectionsSuccess' event (evid: 825)
 *
 * Event description: Module: MED. Flow: sections were added to the site
 *
 * @param {medBuilderAddSectionsSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {number} numOfSections - number of pages
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from Add start
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderAddSectionsSuccessParams>} LogObject<medBuilderAddSectionsSuccessParams>
 */
export function medBuilderAddSectionsSuccess(params) {
    return { evid: 825, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderUpdateSectionStart' event (evid: 826)
 *
 * Event description: Module: MED. Flow: when user make several changes in the section panel and then clicks done
 *
 * @param {medBuilderUpdateSectionStartParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} selectedLayout - layout id
 * @property {string} type - single / list/ item
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderUpdateSectionStartParams>} LogObject<medBuilderUpdateSectionStartParams>
 */
export function medBuilderUpdateSectionStart(params) {
    return { evid: 826, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderUpdateSectionSuccess' event (evid: 827)
 *
 * Event description: Module: MED. Flow: When the user's changes (made in the section panel) are successfully done.
 *
 * @param {medBuilderUpdateSectionSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} selectedLayout - layout id
 * @property {number} loadTime - time elapsed from start
 * @property {string} type - single / list/ item
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderUpdateSectionSuccessParams>} LogObject<medBuilderUpdateSectionSuccessParams>
 */
export function medBuilderUpdateSectionSuccess(params) {
    return { evid: 827, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderMediaSettingsDropdownOpen' event (evid: 828)
 *
 * Event description: Module: MED. Flow: when user clicks on "settings" to replace media
 *
 * @param {medBuilderMediaSettingsDropdownOpenParams} params - The parameters for this event
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderMediaSettingsDropdownOpenParams>} LogObject<medBuilderMediaSettingsDropdownOpenParams>
 */
export function medBuilderMediaSettingsDropdownOpen(params) {
    return { evid: 828, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderDownloadOneAppClick' event (evid: 830)
 *
 * Event description: Module: MED. Flow: user clicks on Get/Open Wix App
 *
 * @param {medBuilderDownloadOneAppClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} source - more/manage-app
 * @property {string} appName - app name if source is manage app
 * @property {string} adiBuilderType - MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - builder version
 * @property {string} viewerName - viewer_name
 * @property {GUID} appId - app id if source is manage app
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} isWixAppInstalled - true/false
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderDownloadOneAppClickParams>} LogObject<medBuilderDownloadOneAppClickParams>
 */
export function medBuilderDownloadOneAppClick(params) {
    return { evid: 830, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderRichTextUiClick' event (evid: 831)
 *
 * Event description: Module: MED. Flow: when user makes any rich text interaction. this interaction doesn't assure that the changes will be eventually made (the user has to click 'done' at the end to save changes)
 *
 * @param {medBuilderRichTextUiClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} sectionType - name of the section, about/general description/welcome, can repeat in site several times
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - builder version
 * @property {string} sectionAlias - name of the section in the context of the structure/industry
 * @property {string} viewerName - viewer_name
 * @property {string} selectedLayout - layout id
 * @property {string} fieldType - title / description...
 * @property {string} type - single / list/ item
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} action - Rich Text action (bold, italic, ...)
 * @return {LogObject<medBuilderRichTextUiClickParams>} LogObject<medBuilderRichTextUiClickParams>
 */
export function medBuilderRichTextUiClick(params) {
    return { evid: 831, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderWarmupRequestTry' event (evid: 832)
 *
 * Event description: Module: MED. Flow: Builder sends a warmup request
 *
 * @param {medBuilderWarmupRequestTryParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - saved revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<medBuilderWarmupRequestTryParams>} LogObject<medBuilderWarmupRequestTryParams>
 */
export function medBuilderWarmupRequestTry(params) {
    return { evid: 832, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderWarmupRequestFailed' event (evid: 833)
 *
 * Event description: Module: MED. Flow: Builder sends a watmup request failed
 *
 * @param {medBuilderWarmupRequestFailedParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {string} industryId - industry id
 * @property {string} reason - reason for failure
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - revision
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from start
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<medBuilderWarmupRequestFailedParams>} LogObject<medBuilderWarmupRequestFailedParams>
 */
export function medBuilderWarmupRequestFailed(params) {
    return { evid: 833, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderWarmupRequestSuccess' event (evid: 834)
 *
 * Event description: Module: MED. Flow: Builder sends a watmup request success
 *
 * @param {medBuilderWarmupRequestSuccessParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - saved revision
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - time elapsed from start
 * @property {string} type - warmup type
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<medBuilderWarmupRequestSuccessParams>} LogObject<medBuilderWarmupRequestSuccessParams>
 */
export function medBuilderWarmupRequestSuccess(params) {
    return { evid: 834, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderStartsLoading' event (evid: 835)
 *
 * Event description: Module: MED. Flow: builder application starts loading
 *
 * @param {medBuilderStartsLoadingParams} params - The parameters for this event
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {string} type - new / existing
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderStartsLoadingParams>} LogObject<medBuilderStartsLoadingParams>
 */
export function medBuilderStartsLoading(params) {
    return { evid: 835, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderBuilderReady' event (evid: 836)
 *
 * Event description: Module: MED. Flow: builder is open and ready to work on
 *
 * @param {medBuilderBuilderReadyParams} params - The parameters for this event
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen_resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {number} loadTime - loading time diff from start loading
 * @property {string} type - new / existing
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {number} fullLoadTime - Full loading time diff from start loading for MED
 * @property {GUID} msid - metasite id
 * @return {LogObject<medBuilderBuilderReadyParams>} LogObject<medBuilderBuilderReadyParams>
 */
export function medBuilderBuilderReady(params) {
    return { evid: 836, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medHireAProfessionalClick' event (evid: 840)
 *
 * Event description: click on Hire a Professional from "more" in MED
 *
 * @param {medHireAProfessionalClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} source - "more"
 * @property {string} adiBuilderType - M
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screenResolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @return {LogObject<medHireAProfessionalClickParams>} LogObject<medHireAProfessionalClickParams>
 */
export function medHireAProfessionalClick(params) {
    return { evid: 840, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderRightClick' event (evid: 89)
 *
 * Event description: Module: builder. Flow: user clicks on mouse right click somewhere in the builder
 *
 * @param {builderRightClickParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} classes - the class of what was clicked
 * @property {string} adiBuilderType - adi builder type
 * @property {GUID} uuid - user id
 * @property {string} container - the general area of the click (header,dialog etc.)
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - -
 * @property {string} viewerName - viewer_name
 * @property {string} class - DEPRECATED
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {GUID} msid - metasite id
 * @property {string} dataHook - name of the area clicked (as used by automation)
 * @property {string} component - currently deprecated
 * @property {string} app_state - the builder state (pages panel opened, add section panel opened etc.)
 * @return {LogObject<builderRightClickParams>} LogObject<builderRightClickParams>
 */
export function builderRightClick(params) {
    return { evid: 89, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medIntroGetStartedClick' event (evid: 890)
 *
 * Event description: Module: MED. Flow: user clicks on get started (1st page)
 *
 * @param {medIntroGetStartedClickParams} params - The parameters for this event
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<medIntroGetStartedClickParams>} LogObject<medIntroGetStartedClickParams>
 */
export function medIntroGetStartedClick(params) {
    return { evid: 890, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medIntroPickStyleNavigationClick' event (evid: 891)
 *
 * Event description: Module: MED. Flow: user picks one of the styles which sends them to the next page
 *
 * @param {medIntroPickStyleNavigationClickParams} params - The parameters for this event
 * @property {string} style - theme chosen
 * @property {string} adiBuilderType - MED
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen resolution
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<medIntroPickStyleNavigationClickParams>} LogObject<medIntroPickStyleNavigationClickParams>
 */
export function medIntroPickStyleNavigationClick(params) {
    return { evid: 891, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'medBuilderUpdateRequestSent' event (evid: 892)
 *
 * Event description: Module: MED. Flow: Builder sends an update request
 *
 * @param {medBuilderUpdateRequestSentParams} params - The parameters for this event
 * @property {string} instanceId - instance id
 * @property {string} structureId - structure id
 * @property {string} adiBuilderType - MED
 * @property {string} industryId - industry id
 * @property {string} screenResolution - screen resolution
 * @property {number} revision - saved revision
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} action - action that is updated (change kit, edit section etc.)
 * @return {LogObject<medBuilderUpdateRequestSentParams>} LogObject<medBuilderUpdateRequestSentParams>
 */
export function medBuilderUpdateRequestSent(params) {
    return { evid: 892, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderIntroQuestionsDialogPageView' event (evid: 90)
 *
 * Event description: Module: intro. Flow: questions from intro viewed as dialog in builder
 *
 * @param {builderIntroQuestionsDialogPageViewParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen resolution
 * @property {string} origin - create from template/ builder
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} view - name of page: business type / business name
 * @return {LogObject<builderIntroQuestionsDialogPageViewParams>} LogObject<builderIntroQuestionsDialogPageViewParams>
 */
export function builderIntroQuestionsDialogPageView(params) {
    return { evid: 90, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderIntroQuestionsDialogClick' event (evid: 91)
 *
 * Event description: Module: intro. Flow: users clicks next/dismiss in the questions from intro viewed as dialog in builder
 *
 * @param {builderIntroQuestionsDialogClickParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen resolution
 * @property {string} origin - create from template/ builder
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} value - category/name submitted
 * @property {string} action - next/dismiss
 * @property {string} view - name of page: business type / business name
 * @return {LogObject<builderIntroQuestionsDialogClickParams>} LogObject<builderIntroQuestionsDialogClickParams>
 */
export function builderIntroQuestionsDialogClick(params) {
    return { evid: 91, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'builderIntroQuestionsDialogBusinessTypeSelected' event (evid: 92)
 *
 * Event description: Module: intro. Flow: users selects a business type in the questions from intro viewed as dialog in builder
 *
 * @param {builderIntroQuestionsDialogBusinessTypeSelectedParams} params - The parameters for this event
 * @property {string} name - selected  business type
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - ADI builder type
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - screen resolution
 * @property {string} origin - create from template/ builder
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} value - category/name submitted
 * @return {LogObject<builderIntroQuestionsDialogBusinessTypeSelectedParams>} LogObject<builderIntroQuestionsDialogBusinessTypeSelectedParams>
 */
export function builderIntroQuestionsDialogBusinessTypeSelected(params) {
    return { evid: 92, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introLogoPaletteNavigationClick' event (evid: 95)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button in the Logo palette page of welcomeTour.
 *
 * @param {introLogoPaletteNavigationClickParams} params - The parameters for this event
 * @property {string} colorationLevel - {plain, tinted, colorful, vibrant, none}
 * @property {string} adiBuilderType - desktop / mobile / MED
 * @property {number} num_of_colors - number of colors in the palette
 * @property {GUID} uuid - user id
 * @property {string} selectedStructureId - structure id
 * @property {string} screenResolution - screen resolution of user's device
 * @property {string} viewerName - viewer_name
 * @property {string} selectedIndustryId - industry id
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back / escape
 * @return {LogObject<introLogoPaletteNavigationClickParams>} LogObject<introLogoPaletteNavigationClickParams>
 */
export function introLogoPaletteNavigationClick(params) {
    return { evid: 95, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introMoreServicesDropdownOpened' event (evid: 96)
 *
 * Event description: Module: intro. Flow: user opens the more features part in the services selection page in the intro
 *
 * @param {introMoreServicesDropdownOpenedParams} params - The parameters for this event
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} adiBuilderType - -
 * @property {GUID} uuid - user id
 * @property {string} screenResolution - -
 * @property {string} recommendedApps - recommended apps that are already presented
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @return {LogObject<introMoreServicesDropdownOpenedParams>} LogObject<introMoreServicesDropdownOpenedParams>
 */
export function introMoreServicesDropdownOpened(params) {
    return { evid: 96, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introImageImportConfirmLegalPageNavigationClick' event (evid: 97)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button on the image import confirmation page (legal page)
 *
 * @param {introImageImportConfirmLegalPageNavigationClickParams} params - The parameters for this event
 * @property {string} structureId - structure
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} numOfSuggested - facebook=10,mysite=12,google=15….
 * @property {string} adiBuilderType - desktop / MED...
 * @property {string} numOfChosen - facebook=3,mysite=5,google=0….
 * @property {string} industryId - industry
 * @property {string} screenResolution - -
 * @property {string} chosenOption - facebook, google, mysite,...
 * @property {string} viewerName - viewer_name
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - next,back,review images
 * @return {LogObject<introImageImportConfirmLegalPageNavigationClickParams>} LogObject<introImageImportConfirmLegalPageNavigationClickParams>
 */
export function introImageImportConfirmLegalPageNavigationClick(params) {
    return { evid: 97, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introSelectImagesLegalPageNavigationClick' event (evid: 98)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button on the select images legal page
 *
 * @param {introSelectImagesLegalPageNavigationClickParams} params - The parameters for this event
 * @property {string} structureId - structure
 * @property {string} mainCategory - the category from the 'vertical selector' category that the user chose
 * @property {string} numOfSuggested - facebook=10,mysite=12,google=15….
 * @property {string} adiBuilderType - desktop / MED...
 * @property {string} numOfChosen - facebook=3,mysite=5,google=0….
 * @property {string} industryId - industry
 * @property {string} screenResolution - -
 * @property {string} viewerName - viewer_name
 * @property {string} nextView - name of next page: business type / services selection / business name / location / search confirm / search results / edit info card / builder / pro editor
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - next / skip / back / review images
 * @return {LogObject<introSelectImagesLegalPageNavigationClickParams>} LogObject<introSelectImagesLegalPageNavigationClickParams>
 */
export function introSelectImagesLegalPageNavigationClick(params) {
    return { evid: 98, src: 66, endpoint: 'onboarding', params: params };
}
/**
 * A function to create a log object for the 'introYoutubeChannelPageNavigationClick' event (evid: 99)
 *
 * Event description: Module: intro. Flow: user clicks on a navigation button on the unified import screen/ youtube channel intro page
 *
 * @param {introYoutubeChannelPageNavigationClickParams} params - The parameters for this event
 * @property {boolean} youTube - true / false
 * @property {boolean} website - true / false
 * @property {string} adiBuilderType - desktop / MED
 * @property {string} screenResolution - screen resolution
 * @property {boolean} facebook - true / false
 * @property {string} reviewImages - true / false
 * @property {string} viewerName - viewer_name
 * @property {boolean} googlePlaces - true / false
 * @property {string} nextView - ** Deprecated **
 * @property {GUID} esi - editor session id, for every site editting session
 * @property {string} navButton - name of navigation button: next / skip / back
 * @return {LogObject<introYoutubeChannelPageNavigationClickParams>} LogObject<introYoutubeChannelPageNavigationClickParams>
 */
export function introYoutubeChannelPageNavigationClick(params) {
    return { evid: 99, src: 66, endpoint: 'onboarding', params: params };
}
//# sourceMappingURL=index.js.map